import React, { useState, useMemo, useEffect, useRef } from 'react';
import { format } from 'date-fns';
import useCountUp from '../hooks/useCountUp';
import { contactAge } from '../utils/dateUtils';
import { fr } from 'date-fns/locale';
import { useNavigate, useLocation } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import genieImg from '../assets/genie-appee.png';
import mascotChatImg from '../assets/mascot-chat.png';
import { api } from '../services/api';
import ConfirmModal from '../components/UI/ConfirmModal';
import LandingBuilder from '../components/LandingBuilder/LandingBuilder';
import Breadcrumb from '../components/Breadcrumb';
import GenieTip from '../components/GenieTip';
import SectionHint from '../components/SectionHint';
import './Strategy.css';


/* ── StratStat card ── */
function StratStat({ icon, label, value, suffix = '', color, delay = 0 }) {
  const count = useCountUp(value, 900);
  return (
    <div className="strat-stat-card" style={{ animationDelay: `${delay}ms` }}>
      <div className="strat-stat-icon" style={{ background: color }}>{icon}</div>
      <div className="strat-stat-body">
        <div className="strat-stat-value">{count.toLocaleString()}{suffix}</div>
        <div className="strat-stat-label">{label}</div>
      </div>
    </div>
  );
}

// ─── Channel styles ───────────────────────────────────────────────────────────
const CH = {
  email:   { icon: '📧', label: 'Email',     color: '#2563EB', bg: '#EFF6FF' },
  sms:     { icon: '📱', label: 'SMS',       color: '#059669', bg: '#ECFDF5' },
  print:   { icon: '🖨️', label: 'Print',     color: '#D97706', bg: '#FFFBEB' },
  social:  { icon: '📲', label: 'Réseaux',   color: '#7C3AED', bg: '#F5F3FF' },
  event:   { icon: '🎉', label: 'Événement', color: '#DB2777', bg: '#FDF2F8' },
  landing: { icon: '🌐', label: 'Landing',   color: '#0891B2', bg: '#ECFEFF' },
  phone:   { icon: '📞', label: 'Téléphone', color: '#374151', bg: '#F9FAFB' },
  instore: { icon: '🏪', label: 'Magasin',   color: '#065F46', bg: '#ECFDF5' },
};

// ─── Helper: unique ordered channels from phases ──────────────────────────────
const getChannels = (phases = []) => {
  const seen = new Set();
  phases.forEach(ph => (ph.actions || []).forEach(a => { if (a.ch) seen.add(a.ch); }));
  return [...seen];
};

// ─── Inline channel chips (reused in card, list, configurator) ────────────────
function ChannelChips({ phases, max = 5 }) {
  const chs   = getChannels(phases);
  const shown = chs.slice(0, max);
  const extra = chs.length - shown.length;
  return (
    <div className="strat-channel-chips">
      {shown.map(ch => (
        <span key={ch} className="sch-chip" style={{ background: CH[ch]?.bg, color: CH[ch]?.color }}>
          {CH[ch]?.icon} {CH[ch]?.label}
        </span>
      ))}
      {extra > 0 && <span className="sch-more">+{extra}</span>}
    </div>
  );
}

// ─── Conseils par canal (piliers communication) ───────────────────────────────
const ADVICE = {
  email: {
    piliers: ['Relation client', 'ROI élevé', 'Personnalisation'],
    timing: '2–3 envois/mois · Meilleur créneau : mar–jeu 9h–11h',
    tips: [
      'Mets le prénom dans l\'objet ET dans le corps — taux d\'ouverture +30%',
      'Maximum 150 mots par email — va à l\'essentiel',
      'Un seul bouton CTA par email, pas plusieurs',
      'Objet sous forme de question ou avec un chiffre → x2 d\'ouvertures',
      'Test A/B sur l\'objet : envoie 2 variantes à 10% de la base, garde le gagnant',
    ],
    kpi: 'Taux d\'ouverture cible : 35–55% · Taux de clic : 5–15%',
  },
  sms: {
    piliers: ['Urgence', 'Proximité', 'Taux d\'ouverture 98%'],
    timing: '1–2 SMS/mois max · Créneaux : 10h–12h ou 17h–19h en semaine',
    tips: [
      'Maximum 160 caractères — les SMS longs sont tronqués sur certains appareils',
      'Commence par un emoji ou le prénom pour attirer l\'œil',
      'Lien court tracké (bit.ly ou équivalent) pour mesurer les clics',
      'Jamais de SMS le week-end soir ou tôt le matin',
      'Toujours mentionner comment se désinscrire (STOP)',
    ],
    kpi: 'Taux de réponse cible : 20–30% · Lecture en < 3 minutes',
  },
  print: {
    piliers: ['Premium', 'Mémorabilité', 'Différenciation'],
    timing: '1–4 envois/an · Événements clés ou anniversaire relation client',
    tips: [
      'Papier de qualité = perception premium — investis dans la matière',
      'QR code pour tracker les retours et mesurer le ROI print',
      'Format A5 ou carte — plus facile à conserver qu\'un A4',
      'Personnalise le nom imprimé si possible — 79% de mémorisation vs 44% digital',
      'Call-to-action clair et daté pour créer l\'urgence',
    ],
    kpi: 'Mémorisation courrier : 79% · Conservation moyenne : 17 jours',
  },
  social: {
    piliers: ['Visibilité', 'Engagement', 'Notoriété'],
    timing: '3–5 posts/semaine · Instagram : 18h–20h · LinkedIn : 8h–10h',
    tips: [
      'Les 2 premières lignes sont cruciales — accroche forte OBLIGATOIRE',
      'Instagram : Reels > Carrousels > Posts fixes (portée algorithmique)',
      'Réponds à TOUS les commentaires dans les 2h — l\'algorithme t\'en récompense',
      'Piliers à alterner : éducatif / coulisses / offre / preuve sociale / tendance',
      'Stories quotidiennes maintiennent la visibilité même sans post',
    ],
    kpi: 'Engagement rate cible : 3–6% · Portée organique : 10–20% des abonnés',
  },
  event: {
    piliers: ['Relation', 'Mémorabilité', 'Exclusivité'],
    timing: '2–4 événements/an · Prévois 3–4 semaines de préparation',
    tips: [
      'Maximum 20–30 personnes pour maintenir l\'exclusivité et la qualité',
      'Follow-up email dans les 24h post-événement — indispensable',
      'Prends des photos/vidéos pour alimenter les réseaux sociaux',
      'Crée UN moment "mémorable" : cadeau surprise, expérience unique',
      'Fais signer une liste de présence pour mettre à jour la base contacts',
    ],
    kpi: 'NPS post-événement cible : 8+/10 · Taux de conversion post-event : 30%+',
  },
  landing: {
    piliers: ['Conversion', 'Capture leads', 'Mesurabilité'],
    timing: 'Spécifique à chaque campagne · Test A/B obligatoire',
    tips: [
      '1 objectif = 1 landing page — jamais de navigation ou de distraction',
      'Formulaire minimaliste : prénom + email suffisent pour démarrer',
      'Titre + bénéfice visible SANS scroll (above the fold)',
      'Preuves sociales : avis, chiffres, logos clients — x2 de conversion',
      'Bouton CTA en couleur vive contrastée, texte actif ("Obtenir mon guide")',
    ],
    kpi: 'Taux de conversion landing cible : 20–40% · Coût par lead objectif',
  },
  phone: {
    piliers: ['Humain', 'Relation longue durée', 'Confiance'],
    timing: '5–10 appels ciblés / semaine · Créneau : 10h–12h ou 14h–16h',
    tips: [
      'Prépare 3 questions ouvertes avant chaque appel — pas de script rigide',
      'Commence par remercier la relation avant de demander quoi que ce soit',
      'Note les infos importantes dans la fiche contact PENDANT l\'appel',
      'Durée idéale : 5–10 minutes — concentrée et efficace',
      'Rappelle systématiquement si tu tombes sur messagerie',
    ],
    kpi: 'Taux de joignabilité : 40–60% · Satisfaction post-appel cible : 8+/10',
  },
  instore: {
    piliers: ['Expérience', 'Point de vente', 'Ambiance'],
    timing: 'Renouvelle l\'affichage toutes les 4–8 semaines',
    tips: [
      'Hauteur des yeux = hauteur de mémorisation max (130–160cm du sol)',
      'Format A3 minimum pour une visibilité à 3 mètres',
      'Message en 7 mots max — le client passe vite',
      'QR code sur chaque ILV pour mesurer les scans',
      'Éclairage : une lumière directe sur la signalétique double l\'attention',
    ],
    kpi: 'Taux de mémorisation ILV : 65% des passages · Dwell time +20%',
  },
};

// ─── Piliers de communication (référentiel marketing) ────────────────────────
const COMM_PILLARS = [
  { icon: '🎓', label: 'Éducation & Valeur',   pct: 30, color: '#2563EB', bg: '#EFF6FF',
    desc: 'Tutoriels, conseils, FAQ, articles utiles — montrer ton expertise sans vendre.' },
  { icon: '🛍️', label: 'Promotion',             pct: 25, color: '#DC2626', bg: '#FEF2F2',
    desc: 'Offres, nouveautés, services, tarifs — mais max 25% du contenu total.' },
  { icon: '🌟', label: 'Inspiration',            pct: 20, color: '#D97706', bg: '#FFFBEB',
    desc: 'Histoires de clients, témoignages, valeurs, coulisses inspirantes.' },
  { icon: '🎭', label: 'Divertissement',         pct: 15, color: '#7C3AED', bg: '#F5F3FF',
    desc: 'Coulisses, humour, moments d\'équipe — montre l\'humain derrière la marque.' },
  { icon: '🤝', label: 'Engagement',             pct: 10, color: '#059669', bg: '#ECFDF5',
    desc: 'Questions, sondages, appels à l\'action, interactions directes.' },
];

function CommunicationPillarsWidget() {
  const [open, setOpen] = React.useState(false);
  return (
    <div className="comm-pillars-widget">
      <button className="cpw-toggle" onClick={() => setOpen(o => !o)}>
        📊 {open ? 'Masquer les piliers de communication ▲' : 'Les 5 piliers de communication — clique pour les voir ▼'}
      </button>
      {open && (
        <div className="cpw-body">
          <div className="cpw-intro">
            Tu dois varier ton contenu pour ne pas lasser ton audience. Voici la règle d'or : 5 piliers avec des proportions précises.
          </div>
          <div className="cpw-bars">
            {COMM_PILLARS.map(p => (
              <div key={p.label} className="cpw-pilier">
                <div className="cpw-pilier-head">
                  <span style={{ fontSize: 16 }}>{p.icon}</span>
                  <div className="cpw-pilier-info">
                    <div className="cpw-pilier-name">{p.label}</div>
                    <div className="cpw-pilier-desc">{p.desc}</div>
                  </div>
                  <div className="cpw-pilier-pct" style={{ color: p.color }}>{p.pct}%</div>
                </div>
                <div className="cpw-bar-track">
                  <div className="cpw-bar-fill" style={{ width: `${p.pct}%`, background: p.color }} />
                </div>
              </div>
            ))}
          </div>
          <div className="cpw-example">
            <strong>💡 Exemple concret :</strong> Sur 10 posts Instagram → 3 tutoriels/conseils, 3 promos/offres, 2 témoignages, 1 coulisse fun, 1 sondage/question.
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Objectifs par stratégie (pour pré-remplir le formulaire campagne) ────────
const STRAT_OBJECTIVES = {
  vip:            'Fidéliser mes clients',
  reactivation:   'Réactiver les clients dormants',
  anniversaire:   'Créer une campagne anniversaire',
  acquisition:    "Augmenter mon chiffre d'affaires",
  local:          'Créer une campagne locale',
  'plan-comm':    'Fidéliser mes clients',
  fidelisation:   'Fidéliser mes clients',
  'event-prive':  'Fidéliser mes clients',
  'event-public': "Augmenter mon chiffre d'affaires",
};

// ─── Company profile helpers (travaillent avec un objet cp passé en paramètre) ─
function getCompanyAge(cp = {}) {
  if (!cp.founded) return null;
  const y = new Date().getFullYear() - new Date(cp.founded).getFullYear();
  return y > 0 ? y : null;
}

function getCompanyAnniversaryAlert(cp = {}) {
  if (!cp.founded) return null;
  const founded = new Date(cp.founded);
  const now = new Date();
  const nextBday = new Date(now.getFullYear(), founded.getMonth(), founded.getDate());
  if (nextBday < now) nextBday.setFullYear(now.getFullYear() + 1);
  const daysUntil = Math.ceil((nextBday - now) / 86400000);
  if (daysUntil > 90) return null;
  const years = nextBday.getFullYear() - founded.getFullYear();
  const isMilestone = years % 5 === 0 || years % 10 === 0;
  return { daysUntil, years, isMilestone, name: cp.name || 'l\'entreprise' };
}

// ─── Smart recommendation engine ─────────────────────────────────────────────
function getSmartReco(contacts, cp = {}) {
  const ann = getCompanyAnniversaryAlert(cp);

  // Priorité 1 : anniversaire entreprise milestone dans les 90 jours
  if (ann && ann.isMilestone) {
    const stratId = ann.years >= 10 ? 'event-public' : 'event-prive';
    return {
      stratId,
      icon: '🎂', urgency: 'high',
      reason: `${ann.name} fête ses ${ann.years} ans dans ${ann.daysUntil} jour${ann.daysUntil > 1 ? 's' : ''} 🎉`,
      action: `Un anniversaire rond de ${ann.years} ans, ça se prépare avec soin — je peux orchestrer tout l'événement`,
    };
  }

  if (!contacts || contacts.length === 0) return null;
  const now = new Date();
  const sixAgo = new Date(now); sixAgo.setMonth(now.getMonth() - 6);
  const curMonth = now.getMonth() + 1;
  const dormant = contacts.filter(c => !c.last_interaction || new Date(c.last_interaction) < sixAgo);
  const vip     = contacts.filter(c => (c.quality_score || 0) > 80);
  const bdays   = contacts.filter(c => c.birth_date && parseInt(c.birth_date.split('-')[1], 10) === curMonth);

  // Anniv entreprise non-milestone mais proche
  if (ann && !ann.isMilestone && ann.daysUntil <= 30) {
    return {
      stratId: 'event-prive', icon: '🥂', urgency: 'medium',
      reason: `${ann.name} fête ses ${ann.years} ans dans ${ann.daysUntil} jours`,
      action: `Une belle occasion de remercier tes clients fidèles avec un événement privé`,
    };
  }

  if (dormant.length >= Math.max(3, Math.floor(contacts.length * 0.15))) {
    return { stratId: 'reactivation', icon: '💤', urgency: 'high',
      reason: `${dormant.length} contacts sans interaction depuis +6 mois`,
      action: `Réactiver ces dormants pourrait générer du CA dès cette semaine` };
  }
  if (bdays.length > 0) {
    return { stratId: 'anniversaire', icon: '🎂', urgency: 'high',
      reason: `${bdays.length} client${bdays.length > 1 ? 's ont' : ' a'} son anniversaire ce mois-ci`,
      action: `Un SMS ou email d'anniversaire personnalisé dépasse 80% d'ouverture` };
  }
  if (vip.length >= 3) {
    return { stratId: 'vip', icon: '⭐', urgency: 'medium',
      reason: `${vip.length} clients à fort potentiel non encore traités en VIP`,
      action: `Créer un Club VIP fidélise durablement tes meilleurs clients` };
  }
  // Fallback contextualisé selon le secteur
  const sector = cp.sector ? ` dans le secteur ${cp.sector}` : '';
  return { stratId: 'plan-comm', icon: '📋', urgency: 'medium',
    reason: `Ta base de ${contacts.length} contacts est prête pour un plan structuré${sector}`,
    action: `Un plan de communication 6 mois maximise ton ROI de façon mesurable` };
}

function SmartReco({ contacts, onLaunch, onDismiss }) {
  const { companyProfile } = useApp();
  const [dismissed, setDismissed] = React.useState(false);
  const reco  = React.useMemo(() => getSmartReco(contacts, companyProfile), [contacts, companyProfile]);
  const strat = React.useMemo(() => reco ? STRATS.find(s => s.id === reco.stratId) : null, [reco]);
  if (dismissed || !reco || !strat) return null;
  const dismiss = () => { setDismissed(true); if (onDismiss) onDismiss(); };
  return (
    <div className="an-conseil-banner strat-animate-in">
      <img src={genieImg} alt="Appee" className="an-conseil-mascot" />
      <div className="an-conseil-body">
        <div className="an-conseil-title">Ce que je ferais à ta place</div>
        <div className="an-conseil-text">
          <strong>{reco.reason}</strong> — {reco.action}.{' '}
          Je te recommande de lancer <strong>{strat.icon} {strat.title}</strong>.
        </div>
      </div>
      <button className="an-conseil-btn" onClick={() => { onLaunch(strat); dismiss(); }}>
        Lancer →
      </button>
      <button className="srb-close" onClick={dismiss}>✕</button>
    </div>
  );
}

// ─── Email draft templates ─────────────────────────────────────────────────────
const EMAIL_DRAFTS = {
  'vip-1': {
    subject: '{{prenom}}, tu fais partie de notre cercle privilégié ✨',
    body: `Bonjour {{prenom}},

Parmi tous nos clients, il en est qui comptent vraiment.
Toi, tu en fais partie.

C'est pourquoi je tenais à t'inviter personnellement dans notre Club Privilège — un espace réservé à nos clients les plus fidèles.

Ce que ça veut dire concrètement :
→ Tu es la première personne informée de nos nouveautés
→ Tu as accès à des offres que je ne propose pas au grand public
→ Tu bénéficies d'une attention personnalisée à chaque visite

Il n'y a rien à faire de ta part — ton accès est activé dès aujourd'hui.

Je voulais juste que tu le saches : ta fidélité compte vraiment.

À très bientôt,
[Ton prénom]
[Nom de l'entreprise]`,
    imageHint: 'Logo ou photo de votre établissement',
  },
  'vip-5': {
    subject: '{{prenom}}, avant-première réservée 🔓',
    body: `Bonjour {{prenom}},

Avant que ce soit annoncé publiquement, je voulais que tu sois la première à le savoir.

[Décris ici ta nouveauté — nouveau produit, nouvelle collection, nouveau service]

En tant que membre de notre Club Privilège, tu y as accès avant tout le monde.

→ [Lien ou instruction pour profiter de l'avant-première]

Cette exclusivité se termine le [date]. Après ça, tout le monde y aura accès.

Merci d'être là depuis le début — c'est pour des clients comme toi que je continue.

À bientôt,
[Ton prénom]`,
    imageHint: 'Photo du nouveau produit ou service',
  },
  'vip-9': {
    subject: 'Ton offre exclusive de [mois] est là, {{prenom}} 🎁',
    body: `Bonjour {{prenom}},

Comme chaque mois, voici ton avantage Club Privilège.

🎁 [Décris l'offre du mois — réduction, cadeau, service offert]

Code à utiliser : VIP-[MOIS]
Valable jusqu'au [date de fin]

Utilise-le en magasin ou en nous mentionnant lors de ta prochaine commande.

Bonne journée {{prenom}},
[Ton prénom]`,
    imageHint: 'Visuel de l\'offre du mois',
  },
  'vip-12': {
    subject: '{{prenom}}, une année ensemble — merci 💛',
    body: `Bonjour {{prenom}},

Cette année qui se termine a été belle — en grande partie grâce à des clients comme toi.

Quelques chiffres qui me tiennent à cœur :
· Tu es avec nous depuis [durée]
· [Nb visites / achats] fois, tu nous as fait confiance
· Tu fais partie de nos clients les plus fidèles

Je voulais juste te dire merci. Pas pour les chiffres — pour la relation.

Parce qu'une boutique sans ses clients, ce n'est qu'un local vide.

Joyeuses fêtes et belle nouvelle année,
[Ton prénom] et toute l'équipe`,
    imageHint: 'Photo de l\'équipe ou de votre établissement',
  },
  'vip-13': {
    subject: '{{prenom}}, tu montes de niveau 🏆',
    body: `Bonjour {{prenom}},

C'est officiel : tu passes au niveau [Gold / Platinum] dans notre Club Privilège.

Ce que ça change pour toi :
→ [Avantage 1 du nouveau niveau]
→ [Avantage 2]
→ [Avantage 3]

Tu as mérité ça — et je voulais que tu le saches.

À bientôt,
[Ton prénom]`,
    imageHint: 'Badge ou visuel du niveau VIP',
  },
  'reac-1': {
    subject: '{{prenom}}, un petit mot de notre part 💙',
    body: `Bonjour {{prenom}},

Ça fait un moment que tu n'es pas passé(e), et je voulais juste prendre de tes nouvelles.

Est-ce qu'il y a quelque chose qu'on aurait pu mieux faire ? Une attente qu'on n'a pas comblée ?

Je pose la question directement parce que ton avis compte vraiment pour nous.

Si tu as envie de nous redonner une chance, on sera vraiment contents de te revoir.

Et si tu préfères qu'on ne t'écrive plus — je comprendrai aussi.

À bientôt, peut-être,
[Ton prénom]`,
    imageHint: 'Photo chaleureuse de votre équipe',
  },
  'reac-3': {
    subject: '{{prenom}}, une offre rien que pour toi — valable 15 jours 🎁',
    body: `Bonjour {{prenom}},

Je ne voulais pas te laisser partir sans te faire une proposition.

Pour ton retour parmi nous, je t'offre :
🎁 [Décris l'offre — réduction, cadeau, service gratuit]

C'est une offre que je ne propose qu'à toi, pas au grand public.
Elle est valable jusqu'au [date] — après ça, je la retire.

→ [Lien ou instruction pour en profiter]

On espère te revoir bientôt {{prenom}},
[Ton prénom]`,
    imageHint: 'Visuel attractif de l\'offre',
  },
  'reac-7': {
    subject: '{{prenom}}, tu veux qu\'on reste en contact ? 🤝',
    body: `Bonjour {{prenom}},

Je t'ai écrit quelques fois ces dernières semaines, et je comprends que tu sois occupé(e).

Avant de cesser de t'envoyer des nouvelles, je voulais juste te demander directement :
tu souhaites rester dans notre liste de contacts ?

→ OUI, garde-moi dans ta liste : [lien]
→ NON, tu peux me retirer : [lien désabonnement]

Pas de rancœur dans un cas comme dans l'autre. Ici, on respecte tes choix.

Merci pour le temps que tu nous as accordé,
[Ton prénom]`,
    imageHint: null,
  },
  'bday-1': {
    subject: 'Joyeux anniversaire {{prenom}} 🎂',
    body: `Bonjour {{prenom}},

Aujourd'hui c'est ton jour — joyeux anniversaire !

On pense à toi et on voulait te faire une petite surprise :

🎁 [Décris le cadeau ou l'offre — réduction, cadeau offert, service gratuit]

Ce cadeau t'attend jusqu'au [date — 7 jours].
Viens nous voir ou utilise le code : BDAY-[MOIS]

Passe une magnifique journée entouré(e) de ceux que tu aimes.

Avec toute notre affection,
[Ton prénom] et l'équipe [Nom de l'entreprise]`,
    imageHint: 'Visuel d\'anniversaire festif aux couleurs de votre marque',
  },
  'bday-4': {
    subject: 'Un anniversaire comme le tien, ça se fête comme il se doit 🥂',
    body: `Bonjour {{prenom}},

[X] ans — c'est une étape qui mérite une attention particulière.

Chez nous, les grandes dates comptent double. C'est pourquoi on a préparé quelque chose de spécial pour toi :

🎁 [Décris le cadeau spécial — offre premium, invitation événement, cadeau personnalisé]

C'est notre façon de te dire que tu comptes pour nous — pas juste comme client(e), mais comme personne.

Tous nos vœux,
[Ton prénom] et toute l'équipe`,
    imageHint: 'Photo festive ou visuel de votre cadeau',
  },
  'bday-8': {
    subject: '{{prenom}}, un cadeau t\'attend si tu partages ta date d\'anniversaire 🎁',
    body: `Bonjour {{prenom}},

On aimerait penser à toi le jour de ton anniversaire — mais pour ça, on a besoin d'une info.

Partage ta date de naissance ici : [lien formulaire]

Et en échange, on t'offre :
🎁 [Incentive — réduction, cadeau offert]

Ça prend 30 secondes et tu reçois ton cadeau dès que ta date approche.

À bientôt,
[Ton prénom]`,
    imageHint: 'Visuel cadeau / surprise',
  },
  'onb-1': {
    subject: 'Bienvenue {{prenom}} — on est vraiment contents de t\'avoir ! 🎉',
    body: `Bonjour {{prenom}},

Bienvenue dans notre famille !

Je voulais te souhaiter personnellement la bienvenue et te dire ce qui t'attend ici :

✓ Des actualités et conseils adaptés à toi
✓ Des offres réservées à nos clients fidèles
✓ Une équipe disponible si tu as la moindre question

Pour bien commencer, voici ce que je te conseille :
→ [Première action recommandée]

Et si jamais tu as une question, tu peux me répondre directement à ce mail. Je lis tout.

À très bientôt,
[Ton prénom]
[Nom de l'entreprise]`,
    imageHint: 'Photo accueillante de votre équipe ou de votre lieu',
  },
  'onb-4': {
    subject: '{{prenom}}, comment se passe ton expérience avec nous ? 🙏',
    body: `Bonjour {{prenom}},

Cela fait quelques jours que tu nous as rejoints, et je voulais prendre de tes nouvelles.

Est-ce que tout se passe bien ? Est-ce qu'il y a quelque chose qui t'a surpris — en bien ou en moins bien ?

Je pose la question directement parce que ton retour est vraiment précieux pour nous. C'est comme ça qu'on s'améliore.

Tu peux me répondre directement à ce mail — je lis tous les retours personnellement.

Merci d'être avec nous,
[Ton prénom]`,
    imageHint: null,
  },
  'onb-7': {
    subject: '{{prenom}}, un avantage rien que pour toi 🎁',
    body: `Bonjour {{prenom}},

Tu nous fais confiance depuis quelques semaines maintenant — et j'ai envie de te remercier.

Voici un avantage réservé aux nouveaux clients qui sont restés avec nous :

🎁 [Décris l'offre — réduction sur le prochain achat, cadeau offert]

Code : MERCI-[MOIS]
Valable jusqu'au [date]

C'est ma façon de te dire que tu n'es pas juste un numéro dans une base de données.

À très bientôt,
[Ton prénom]`,
    imageHint: 'Visuel de votre offre de fidélisation',
  },
  'sais-1': {
    subject: '{{prenom}}, quelque chose de spécial s\'en vient... 🔔',
    body: `Bonjour {{prenom}},

Je ne peux pas encore tout te dire.

Mais dans [X] jours, on lance quelque chose qu'on prépare depuis longtemps.
Quelque chose qu'on n'a jamais fait avant.

Je voulais que tu sois parmi les premiers à le savoir.

→ Garde le [date] dans ton agenda.

On en parle très bientôt,
[Ton prénom]`,
    imageHint: 'Visuel mystérieux aux couleurs de la saison',
  },
  'sais-4': {
    subject: 'C\'est parti, {{prenom}} ! L\'offre est là 🚀',
    body: `Bonjour {{prenom}},

Le moment est arrivé — voici ce qu'on avait préparé pour toi.

🎉 [Nom de l'offre ou de l'événement]
[Décris l'offre en 2-3 lignes]

→ [Lien ou instruction pour en profiter]

Disponible jusqu'au [date de fin].
Après ça, c'est terminé — et on ne fera pas de prolongation.

Profites-en bien {{prenom}},
[Ton prénom]`,
    imageHint: 'Visuel festif aux couleurs de la promotion',
  },
  'sais-8': {
    subject: '{{prenom}}, il reste encore [X] jours ⏰',
    body: `Bonjour {{prenom}},

Je voulais juste te rappeler — notre offre [nom de l'offre] se termine le [date].

Si tu n'as pas encore eu le temps d'en profiter, c'est le moment.

🎁 [Rappel de l'offre en une ligne]
→ [Lien direct]

Après le [date], l'offre disparaît.

À bientôt,
[Ton prénom]`,
    imageHint: 'Visuel avec indication de délai',
  },
  'sais-10': {
    subject: 'Merci {{prenom}} — et bientôt la suite 👀',
    body: `Bonjour {{prenom}},

Notre [offre / événement] est terminé(e) — merci d'en avoir été.

[Bilan rapide — résultat, ambiance, retour positif]

Et pour ceux qui ont manqué ça... la prochaine fois arrive vite.

→ [Teaser de la prochaine campagne ou événement]

À bientôt,
[Ton prénom]`,
    imageHint: 'Photo ou visuel de clôture de la campagne',
  },
  'ep-6': {
    subject: '{{prenom}}, tu es sur notre liste d\'invités 🥂',
    body: `Bonjour {{prenom}},

Il y a des personnes avec qui on aime partager les bons moments.
Toi, tu en fais partie.

C'est pourquoi je t'invite personnellement à notre [type d'événement] :

📅 Le [date]
🕗 À [heure]
📍 Au [lieu]
🎯 [Description en une phrase de l'ambiance ou du programme]

Les places sont limitées à [nombre] personnes.

→ Confirme ta présence ici : [lien RSVP]
→ Ou réponds simplement à ce mail

J'espère vraiment te voir là-bas,
[Ton prénom]`,
    imageHint: 'Photo du lieu ou visuel de l\'ambiance prévue',
  },
  'ep-11': {
    subject: 'Tout ce qu\'il faut savoir pour [nom événement] — {{prenom}} 📋',
    body: `Bonjour {{prenom}},

Plus que quelques jours avant notre [type d'événement] — et j'ai hâte !

Voici tout ce que tu dois savoir :

📅 Date : [date]
🕗 Heure : [heure] (accueil dès [heure - 15 min])
📍 Adresse : [adresse complète]
🚗 Parking : [info parking]
👗 Tenue : [indication si besoin]

Au programme :
→ [Point 1]
→ [Point 2]
→ [Point 3]

Si tu as une question ou une contrainte alimentaire, réponds directement à ce mail.

On t'attend avec impatience,
[Ton prénom]`,
    imageHint: 'Plan d\'accès ou photo du lieu',
  },
  'ep-20': {
    subject: 'Merci {{prenom}} — c\'était une belle soirée 💛',
    body: `Bonjour {{prenom}},

Je voulais te remercier d'être venu(e) hier soir.

Ces moments partagés avec des clients comme toi, c'est ce qui donne du sens à notre travail.

[1-2 phrases sur un moment fort ou l'ambiance de la soirée]

On espère que tu as passé une belle soirée.

À très bientôt,
[Ton prénom]`,
    imageHint: 'Une belle photo de l\'événement (ambiance)',
  },
  'ep-23': {
    subject: 'En souvenir de notre soirée, {{prenom}} 🎁',
    body: `Bonjour {{prenom}},

J'espère que tu es rentré(e) avec de beaux souvenirs de notre [type d'événement].

Pour prolonger ce moment, j'ai quelque chose pour toi :

🎁 [Décris l'offre exclusive post-event]

Code : EVENT-[DATE]
Valable jusqu'au [date — 7 jours après l'événement]

C'est ma façon de te remercier d'avoir été là.

À bientôt {{prenom}},
[Ton prénom]`,
    imageHint: 'Photo de l\'événement ou visuel de l\'offre',
  },
  'pub-9': {
    subject: '{{prenom}}, tu es invité(e) — [nom de l\'événement] 🎪',
    body: `Bonjour {{prenom}},

On organise quelque chose de spécial et je voulais que tu sois parmi les premiers informés.

🎉 [Nom de l'événement]
📅 Le [date] de [heure] à [heure]
📍 À [lieu, adresse]
💰 Entrée : [gratuite / prix]

[2-3 lignes sur ce qui t'y attend — animations, démonstrations, offres]

⚠️ Les places sont limitées — inscris-toi avant le [date limite].

→ Je m'inscris : [lien]

On espère te voir là-bas !
[Ton prénom] et toute l'équipe`,
    imageHint: 'Affiche ou visuel de l\'événement',
  },
  'pub-14': {
    subject: '{{prenom}}, plus que 2 semaines pour [nom événement] 📅',
    body: `Bonjour {{prenom}},

Notre [type d'événement] approche — et on a hâte de te voir !

Si tu n'as pas encore confirmé ta venue, c'est le bon moment.

📅 [date] · [heure] · [lieu]

[Si places limitées :] Il reste [X] places disponibles.

→ Je confirme ma présence : [lien RSVP]

On compte sur toi !
[Ton prénom]`,
    imageHint: 'Visuel de rappel de l\'événement',
  },
  'pub-19': {
    subject: 'On se retrouve dans 2 jours — tout ce qu\'il faut savoir, {{prenom}} 🎉',
    body: `Bonjour {{prenom}},

C'est dans 2 jours — on a hâte de t'accueillir !

Un récap rapide :

📅 [date] de [heure] à [heure]
📍 [Adresse complète]
🚗 Parking : [info]

Ce qui t'attend :
→ [Point 1 du programme]
→ [Point 2 du programme]
→ [Point 3 — offres, animations]

N'hésite pas à partager l'événement autour de toi !

À très bientôt,
[Ton prénom]`,
    imageHint: 'Affiche ou carte de localisation',
  },
  'pub-27': {
    subject: 'Merci d\'avoir été là, {{prenom}} 💙',
    body: `Bonjour {{prenom}},

Merci d'être venu(e) à notre [type d'événement].

C'était une belle journée — grâce à vous.

[1-2 phrases sur un moment fort ou sur l'ambiance]

On a pris quelques photos — si tu veux les retrouver : [lien album]

À très bientôt,
[Ton prénom] et toute l'équipe`,
    imageHint: 'Belle photo de l\'événement',
  },
  'pub-28': {
    subject: 'Un petit cadeau en souvenir de notre rencontre, {{prenom}} 🎁',
    body: `Bonjour {{prenom}},

J'espère que tu es reparti(e) avec de bons souvenirs de notre [type d'événement].

Pour te remercier d'être venu(e), voici une offre exclusive :

🎁 [Décris l'offre — réduction, produit offert]

Valable jusqu'au [date — 7 jours après l'événement]
→ [Lien ou instruction pour en profiter]

À bientôt {{prenom}},
[Ton prénom]`,
    imageHint: 'Photo de l\'événement ou visuel de l\'offre',
  },
  'pc-7': {
    subject: 'Les nouvelles du mois chez [Entreprise] — {{prenom}} 📰',
    body: `Bonjour {{prenom}},

Voici les actualités du mois — j'espère que ça t'intéressera.

📌 Actualité n°1
[Titre + 2-3 phrases]

📌 Actualité n°2
[Titre + 2-3 phrases]

💡 Le conseil du mois
[Un conseil pratique lié à ton activité]

🎁 Offre du moment
[Si applicable — offre ou événement à venir]

Merci de nous lire. Si tu as une idée pour notre prochain numéro, réponds directement.

À le mois prochain,
[Ton prénom]`,
    imageHint: 'Photo représentant votre activité ou actualité principale',
  },
  'pc-13': {
    subject: '{{prenom}}, une offre qu\'on a préparée rien que pour toi 🌸',
    body: `Bonjour {{prenom}},

[Saison/occasion] approche — et on a préparé quelque chose de spécial.

🎉 [Nom de l'offre]
[Description en 2-3 lignes]

→ [Lien ou instruction]
Valable jusqu'au [date]

Profites-en {{prenom}} — c'est une offre qu'on ne renouvelle pas souvent.

À bientôt,
[Ton prénom]`,
    imageHint: 'Visuel saisonnier aux couleurs de votre promo',
  },
  'pc-16': {
    subject: '{{prenom}}, voici le bilan de nos 6 mois ensemble 📊',
    body: `Bonjour {{prenom}},

Cela fait 6 mois qu'on avance ensemble — et je voulais partager quelques chiffres.

📊 En 6 mois :
· [X] emails envoyés, [X]% ouverts
· [X] événements organisés
· [X] nouveaux clients rejoints

Ce qu'on prépare pour la suite :
→ [Projet ou nouveauté 1]
→ [Projet ou nouveauté 2]

Merci d'être là. Vraiment.

[Ton prénom]`,
    imageHint: 'Graphique ou visuel bilan',
  },
  'conc-3': {
    subject: '{{prenom}}, on organise un concours — et tu peux gagner 🎰',
    body: `Bonjour {{prenom}},

On lance un concours et je voulais que tu sois parmi les premiers participants.

🎁 À gagner : [Décris le lot — valeur, description]

Comment participer ?
→ [Instructions simples en 2-3 étapes]
→ [Lien d'inscription]

Le gagnant sera tiré au sort le [date].
C'est gratuit, ça prend 2 minutes.

Bonne chance {{prenom}} !
[Ton prénom]`,
    imageHint: 'Photo du lot à gagner',
  },
  'conc-8': {
    subject: '{{prenom}}, tu n\'as pas gagné... mais voici quelque chose pour toi 🎁',
    body: `Bonjour {{prenom}},

Le tirage au sort est terminé — et malheureusement, tu n'étais pas le gagnant cette fois.

Mais je ne voulais pas que tu repartes les mains vides.

Pour te remercier d'avoir participé :

🎁 [Offre consolation — réduction, produit offert]

Code : MERCI-CONCOURS
Valable jusqu'au [date]

À très bientôt {{prenom}},
[Ton prénom]`,
    imageHint: 'Visuel sympathique avec votre offre consolation',
  },
  'enr-1': {
    subject: '{{prenom}}, 2 minutes pour mettre à jour ton profil ? 🎁',
    body: `Bonjour {{prenom}},

En ajoutant ta date de naissance dans ton profil, tu pourras recevoir :
→ Une surprise le jour de ton anniversaire
→ Des offres encore plus personnalisées

Ça prend 2 minutes : [lien vers formulaire]

Et pour te remercier :
🎁 [Incentive — réduction, cadeau]

Merci {{prenom}},
[Ton prénom]`,
    imageHint: 'Visuel cadeau / surprise',
  },
  'enr-7': {
    subject: '{{prenom}}, tu veux qu\'on reste en contact ?',
    body: `Bonjour {{prenom}},

Je t'ai envoyé quelques messages ces derniers temps, et je pense que tu n'as pas eu le temps de les lire.

Avant de fermer ton dossier, une dernière question : tu souhaites rester dans notre liste ?

→ Oui, continue à m'envoyer des nouvelles : [lien]
→ Non merci, je me désinscris : [lien désinscription]

Dans les deux cas, je respecte ton choix.

Bonne journée {{prenom}},
[Ton prénom]`,
    imageHint: null,
  },
};

function getEmailDraft(action, strat, cp = {}) {
  const nomEntreprise = cp.name || '[Nom de l\'entreprise]';
  const secteur       = cp.sector || '';
  const age           = getCompanyAge(cp);

  // Remplace les placeholders génériques dans le draft par les vraies infos
  const hydrate = (draft) => {
    const hydrated = { ...draft };
    if (cp.name) {
      hydrated.subject = (hydrated.subject || '').replace(/\[Nom de l'entreprise\]/g, cp.name).replace(/\[Entreprise\]/g, cp.name);
      hydrated.body    = (hydrated.body    || '').replace(/\[Nom de l'entreprise\]/g, cp.name).replace(/\[Entreprise\]/g, cp.name).replace(/chez nous/g, `chez ${cp.name}`);
    }
    return hydrated;
  };

  if (EMAIL_DRAFTS[action.id]) return hydrate({ ...EMAIL_DRAFTS[action.id] });

  const t = (action.title || '').toLowerCase();
  const nomStrat = strat?.title || 'notre démarche';
  const sig = `[Ton prénom]\n${nomEntreprise}`;

  if (t.includes('bienvenue') || t.includes('onboarding')) return hydrate({ subject: `Bienvenue {{prenom}} — on est contents de t'avoir chez ${nomEntreprise} !`, body: `Bonjour {{prenom}},\n\nBienvenue chez ${nomEntreprise} — on est vraiment contents de t'accueillir.\n\n[Complète avec le contexte spécifique]\n\nÀ bientôt,\n${sig}`, imageHint: 'Photo d\'accueil' });
  if (t.includes('anniver')) return hydrate({ subject: `Joyeux anniversaire {{prenom}} 🎂`, body: `Bonjour {{prenom}},\n\nJoyeux anniversaire ! Toute l'équipe ${nomEntreprise.replace('[Nom de l\'entreprise]', '')} pense à toi aujourd'hui.\n\n🎁 [Ton cadeau ou ton offre anniversaire]\n\nPasse une belle journée,\n${sig}`, imageHint: 'Visuel festif anniversaire' });
  if (t.includes('merci') || t.includes('remerci')) return hydrate({ subject: `Merci {{prenom}} 💛`, body: `Bonjour {{prenom}},\n\nJe voulais juste te dire merci au nom de toute l'équipe ${nomEntreprise.replace('[Nom de l\'entreprise]', '')}.\n\n[Précise le contexte : fidélité, venue, confiance]\n\nÀ très bientôt,\n${sig}`, imageHint: null });
  if (t.includes('invitation') || t.includes('invit')) return hydrate({ subject: `{{prenom}}, tu es invité(e) 📅`, body: `Bonjour {{prenom}},\n\nJ'ai le plaisir de t'inviter à :\n\n[Décris l'événement]\n📅 Le [date] à [heure]\n📍 [Lieu]\n\n→ Confirme ta présence : [lien]\n\nOn espère te voir là-bas,\n${sig}`, imageHint: 'Visuel de l\'événement' });
  if (t.includes('rapport') || t.includes('bilan')) return hydrate({ subject: `${nomStrat} — bilan de la période 📊`, body: `Bonjour {{prenom}},\n\nVoici le bilan de la période écoulée.\n\n[Résume les résultats clés]\n\nProchaines étapes :\n[Liste des actions à venir]\n\nÀ très bientôt,\n${sig}`, imageHint: null });
  if (t.includes('relance') || t.includes('dernière chance') || t.includes('expire')) return hydrate({ subject: `{{prenom}}, c'est ta dernière chance ⏰`, body: `Bonjour {{prenom}},\n\n[L'offre / l'événement] se termine dans [X] jours.\n\n🎁 [Rappel de l'offre]\n→ [Lien direct]\n\nAprès le [date], c'est terminé.\n\n${sig}`, imageHint: 'Visuel avec indication de délai' });
  if (t.includes('offre') || t.includes('promo') || t.includes('exclusiv')) return hydrate({ subject: `{{prenom}}, une offre préparée pour toi 🎁`, body: `Bonjour {{prenom}},\n\nJe t'ai préparé quelque chose de spécial chez ${nomEntreprise} :\n\n🎁 [Décris l'offre]\nValable jusqu'au [date]\n\n→ [Lien ou instruction]\n\nÀ bientôt,\n${sig}`, imageHint: 'Visuel de l\'offre' });
  if (t.includes('newsletter') || t.includes('actualité') || t.includes('nouvelles')) return hydrate({ subject: `Les nouvelles du mois chez ${nomEntreprise} — {{prenom}} 📰`, body: `Bonjour {{prenom}},\n\nVoici les actualités du mois chez ${nomEntreprise}.\n\n📌 [Actualité 1]\n[Description]\n\n📌 [Actualité 2]\n[Description]\n\n💡 [Conseil pratique${secteur ? ` lié à ${secteur}` : ''}]\n\nÀ le mois prochain,\n${sig}`, imageHint: 'Photo représentant votre actualité principale' });
  return hydrate({
    subject: `${action.title} — {{prenom}}`,
    body: `Bonjour {{prenom}},\n\n${action.desc || ''}\n\n[Complète ce message avec tes informations spécifiques]\n\nÀ bientôt,\n${sig}`,
    imageHint: 'Ajoute une image en rapport avec ce message',
  });
}

// ─── Catégories ───────────────────────────────────────────────────────────────
const CATEGORIES = [
  { id: 'all',             icon: '🎯', label: 'Toutes' },
  { id: 'accompagnement',  icon: '🏢', label: 'Accompagnement 360°' },
  { id: 'fidelisation',    icon: '💛', label: 'Fidélisation' },
  { id: 'acquisition',     icon: '🚀', label: 'Acquisition' },
  { id: 'lancement',       icon: '🎯', label: 'Lancement' },
  { id: 'communication',   icon: '📢', label: 'Communication' },
  { id: 'evenement',       icon: '🎉', label: 'Événement' },
  { id: 'relationnel',     icon: '🤝', label: 'Relationnel' },
  { id: 'data',            icon: '📊', label: 'Data & CRM' },
];

// ─── Strategy definitions ─────────────────────────────────────────────────────
const STRATS = [
  {
    id: 'vip', category: 'fidelisation',
    icon: '🏆', color: '#FFC400', bg: '#FFFBEB',
    title: 'Programme Fidélisation VIP',
    tagline: 'Transforme tes meilleurs clients en ambassadeurs',
    adviceIntro: "J'ai identifié tes meilleurs clients. Ce programme va les fidéliser à vie et multiplier leur panier moyen. Je m'occupe de chaque email et SMS — toi tu les accueilles.",
    duration: '12 semaines', roi: '+CHF 200/client',
    contacts_fn: c => c.filter(x => x.category === 'VIP' || (x.engagement_score || 0) > 75).length,
    kpis: ['Rétention VIP cible 85%+', 'Panier moyen VIP', 'NPS recommandation', 'Taux ouverture emails VIP'],
    phases: [
      { id: 'p1', name: 'Identification & Création du Club', week: 'Sem. 1–2', actions: [
        { id: 'vip-1', ch: 'email',   title: "Email d'invitation VIP",   desc: "Invitation exclusive personnalisée 'Tu fais partie de notre cercle privilégié' — prénom + mémoire relationnelle.",          appee: true },
        { id: 'vip-2', ch: 'sms',     title: 'SMS de confirmation',       desc: "Je confirme leur statut VIP par SMS avec un code club unique le lendemain.",                                              appee: true },
        { id: 'vip-3', ch: 'print',   title: 'Carte membre physique',     desc: "Prépare et envoie la carte de membre VIP imprimée + livret de bienvenue pour les 50 ans et plus.",                      appee: false },
        { id: 'vip-4', ch: 'instore', title: 'Signalétique Club VIP',    desc: "Installe l'affichage 'Club Privilège — accès réservé' en point de vente pour créer de l'exclusivité visible.",          appee: false, optional: true },
      ]},
      { id: 'p2', name: 'Lancement & Activation', week: 'Sem. 3–5', actions: [
        { id: 'vip-5', ch: 'email',   title: "Email 'En avant-première'", desc: "Accès prioritaire aux nouveautés avant tout le monde — crée le sentiment d'appartenance exclusif.",                     appee: true },
        { id: 'vip-6', ch: 'landing', title: "Espace membre VIP",         desc: "Landing page dédiée avec avantages, code personnel et offres exclusives — bookmark à envoyer aux VIP.",                 appee: false, optional: true },
        { id: 'vip-7', ch: 'social',  title: "Post 'Merci à nos VIP'",   desc: "Publication Instagram/Facebook valorisant vos meilleurs clients — anonymisé et avec leur accord.",                       appee: false },
        { id: 'vip-8', ch: 'event',   title: 'Soirée VIP exclusive',      desc: "Événement privé 10–20 clients : atelier, dégustation ou soirée. Le moment le plus mémorable de l'année.",             appee: false, optional: true },
      ]},
      { id: 'p3', name: 'Animation Continue', week: 'Sem. 6–10', actions: [
        { id: 'vip-9',  ch: 'email', title: 'Email offre exclusive mensuelle', desc: "Code 'VIP-[MOIS]' valable en magasin — envoi automatique chaque mois sans effort de ta part.",                    appee: true },
        { id: 'vip-10', ch: 'sms',   title: 'SMS anniversaire VIP',           desc: "SMS personnalisé avec offre cadeau le jour J — moment humain unique qui ne s'oublie pas.",                         appee: true },
        { id: 'vip-11', ch: 'phone', title: 'Appel de suivi personnalisé',     desc: "5–10 appels à tes VIP clés pour recueillir leurs avis et renforcer le lien.",                                     appee: false, optional: true },
      ]},
      { id: 'p4', name: 'Bilan & Renouvellement', week: 'Sem. 11–12', actions: [
        { id: 'vip-12', ch: 'email', title: "Email bilan 'Ton année avec nous'", desc: "Stats personnalisées : nb visites, ancienneté, achats — très impactant, fort taux d'ouverture.",                appee: true },
        { id: 'vip-13', ch: 'email', title: 'Email montée en niveau Gold/Platinum', desc: "Mise à jour des niveaux VIP avec avantages supplémentaires pour les plus actifs.",                            appee: true },
        { id: 'vip-14', ch: 'print', title: 'Courrier récapitulatif annuel',    desc: "Lettre physique avec résumé + cadeau surprise — fidélisation à 12 mois garantie.",                              appee: false, optional: true },
      ]},
    ],
  },
  {
    id: 'reactivation', category: 'fidelisation',
    icon: '💤', color: '#5A35FF', bg: '#EDE9FF',
    title: 'Réactivation Dormants',
    tagline: 'Ramène tes clients perdus avec une approche humaine',
    adviceIntro: "J'ai repéré tes clients qui n'ont plus donné signe de vie. Avec la bonne approche, je vais en récupérer 20–25% — c'est du chiffre d'affaires immédiat sans prospecter.",
    duration: '6 semaines', roi: '20–25% récupérés',
    contacts_fn: c => { const t = new Date(); t.setFullYear(t.getFullYear() - 1); return c.filter(x => !x.last_interaction || new Date(x.last_interaction) < t).length; },
    kpis: ['Taux réactivation cible 20%', 'CA récupéré', 'Désabonnements évités', 'Panier 1er rachat'],
    phases: [
      { id: 'p1', name: 'Première Approche', week: 'Sem. 1', actions: [
        { id: 'reac-1', ch: 'email', title: "Email 'On pense à toi'",  desc: "Message chaleureux avec question ouverte — surtout pas promotionnel. On veut juste reprendre contact.",   appee: true },
        { id: 'reac-2', ch: 'sms',   title: 'SMS relance douce',        desc: "SMS court 3 jours après si l'email n'est pas ouvert — ton humain, pas commercial.",                      appee: true },
      ]},
      { id: 'p2', name: 'Offre de Retour', week: 'Sem. 2–3', actions: [
        { id: 'reac-3', ch: 'email',   title: 'Email offre exclusive retour',  desc: "Réduction ou avantage unique 'Uniquement pour toi — valable 15 jours'. Urgence et exclusivité.",   appee: true },
        { id: 'reac-4', ch: 'landing', title: 'Landing page de retour',         desc: "Page dédiée avec formulaire de préférences + offre de bienvenue — mesure les conversions.",        appee: false, optional: true },
        { id: 'reac-5', ch: 'sms',     title: "SMS 'Dernière chance'",          desc: "SMS 48h avant expiration de l'offre — crée l'urgence finale pour les indécis.",                   appee: true },
        { id: 'reac-6', ch: 'print',   title: 'Courrier physique surprise',     desc: "Lettre manuscrite numérique pour tes dormants premium — différenciation totale.",                appee: false, optional: true },
      ]},
      { id: 'p3', name: 'Suivi & Nettoyage Base', week: 'Sem. 4–6', actions: [
        { id: 'reac-7', ch: 'email', title: "Email 'Souhaites-tu rester ?'", desc: "Dernière tentative humaine avant suppression propre — montre que tu respectes leur choix.",         appee: true },
        { id: 'reac-8', ch: 'phone', title: 'Appels dormants premium',        desc: "10–15 appels aux ex-clients à fort potentiel — conversation directe pour comprendre pourquoi.",    appee: false, optional: true },
      ]},
    ],
  },
  {
    id: 'birthday', category: 'relationnel',
    icon: '🎂', color: '#FF4FA3', bg: '#FDF2F8',
    title: 'Anniversaires & Moments Forts',
    tagline: "Je surveille chaque date — tu n'oublieras plus jamais",
    adviceIntro: "Les messages d'anniversaire ont un taux d'ouverture de 70%+ — c'est le canal le plus rentable que je connaisse. Je m'en occupe automatiquement, sans effort de ta part.",
    duration: 'Continu', roi: '+45% taux ouverture',
    contacts_fn: c => c.filter(x => x.birth_date).length,
    kpis: ['Contacts avec date naissance', 'Taux ouverture cible 70%+', 'Feedbacks positifs reçus', 'Réachat post-anniversaire'],
    phases: [
      { id: 'p1', name: 'Configuration Automatique', week: 'Sem. 1', actions: [
        { id: 'bday-1', ch: 'email', title: 'Email anniversaire automatique', desc: "Message personnalisé envoyé à 7h le jour J — je le gère entièrement, tu n'as rien à faire.",          appee: true },
        { id: 'bday-2', ch: 'sms',   title: 'SMS anniversaire backup',         desc: "SMS si pas d'email ou non ouvert après 2h — taux de lecture 98% en moins de 3 minutes.",            appee: true },
        { id: 'bday-3', ch: 'print', title: 'Carte anniversaire physique',     desc: "Carte postale pour les 50 ans et plus ou clients VIP — moment unique mémorable.",                   appee: false, optional: true },
      ]},
      { id: 'p2', name: 'Âges Ronds & VIP', week: 'Continu', actions: [
        { id: 'bday-4', ch: 'email', title: 'Email âge rond spécial 30/40/50', desc: "Message encore plus personnalisé pour les anniversaires marquants — panier moyen +50% ces jours-là.", appee: true },
        { id: 'bday-5', ch: 'event', title: 'Cadeau ou invitation âge rond',   desc: "Offre spéciale ou invitation événement pour les grandes dates — fidélisation à vie.",               appee: false, optional: true },
        { id: 'bday-6', ch: 'social', title: "Story 'Happy Birthday' client",  desc: "Avec accord du client — story Instagram personnalisée qui leur fait vraiment plaisir.",             appee: false, optional: true },
      ]},
      { id: 'p3', name: 'Optimisation Continue', week: 'Mensuel', actions: [
        { id: 'bday-7', ch: 'email', title: 'Rapport mensuel anniversaires',       desc: "Récap des anniversaires du mois, taux d'ouverture et feedbacks — pour mesurer l'impact.",      appee: true },
        { id: 'bday-8', ch: 'email', title: 'Campagne collecte dates manquantes', desc: "Email demandant la date de naissance avec un cadeau à la clé — enrichit ta base automatiquement.", appee: true },
      ]},
    ],
  },
  {
    id: 'onboarding', category: 'acquisition',
    icon: '🆕', color: '#10B981', bg: '#ECFDF5',
    title: 'Onboarding Nouveaux Clients',
    tagline: 'La première impression crée la fidélité pour 10 ans',
    adviceIntro: "Les 30 premiers jours sont décisifs. Je m'occupe d'accueillir tes nouveaux clients de façon à les garder 10× plus longtemps — et à déclencher le 2e achat rapidement.",
    duration: '8 semaines', roi: 'Fidélisation long terme',
    contacts_fn: c => { const t = new Date(); t.setMonth(t.getMonth() - 1); return c.filter(x => x.created_at && new Date(x.created_at) > t).length || Math.max(1, Math.round(c.length * 0.05)); },
    kpis: ['Taux activation J+7', 'Taux 2e achat J+30', 'NPS à J+60', 'Taux désabonnement J+30'],
    phases: [
      { id: 'p1', name: 'Bienvenue Immédiate', week: 'J+1', actions: [
        { id: 'onb-1', ch: 'email', title: 'Email de bienvenue personnalisé', desc: "Message chaleureux avec prénom, accès à l'espace client et les prochaines étapes claires.",          appee: true },
        { id: 'onb-2', ch: 'sms',   title: 'SMS de bienvenue',                desc: "SMS court le lendemain : 'Bienvenue dans notre famille 🎉' — humain et direct.",                    appee: true },
        { id: 'onb-3', ch: 'print', title: 'Kit de bienvenue physique',       desc: "Brochure produits + carte personnalisée dans un coffret soigné — première impression premium.",     appee: false, optional: true },
      ]},
      { id: 'p2', name: 'Activation J+7', week: 'Sem. 1–2', actions: [
        { id: 'onb-4', ch: 'email', title: "Email 'Comment se passe ton expérience ?'", desc: "Question ouverte sur la satisfaction — montre que tu t'intéresses vraiment à eux.",        appee: true },
        { id: 'onb-5', ch: 'email', title: "Email 'Pour bien démarrer'",                desc: "Tips, conseils pratiques, FAQ — apporte de la valeur avant de vendre quoi que ce soit.",  appee: true },
        { id: 'onb-6', ch: 'landing', title: 'Page ressources nouveaux clients',         desc: "Espace dédié : tutoriels, FAQ, contacts utiles — diminue les demandes support.",          appee: false, optional: true },
      ]},
      { id: 'p3', name: 'Fidélisation J+30', week: 'Sem. 3–8', actions: [
        { id: 'onb-7', ch: 'email', title: "Email offre 2e achat exclusif", desc: "'Merci pour ta confiance — voici un avantage réservé aux nouveaux clients' — déclenche l'habitude.", appee: true },
        { id: 'onb-8', ch: 'sms',   title: 'SMS invitation parrainage',     desc: "SMS avec code parrainage personnel — ton meilleur client devient ton meilleur commercial.",        appee: true },
        { id: 'onb-9', ch: 'phone', title: 'Appel de satisfaction J+30',    desc: "Appel bref pour valider la satisfaction et proposer de l'aide — taux NPS excellent après.",        appee: false, optional: true },
      ]},
    ],
  },
  {
    id: 'saisonnier', category: 'communication',
    icon: '🌸', color: '#F59E0B', bg: '#FFFBEB',
    title: 'Campagnes Saisonnières & Promos',
    tagline: 'Synchronise ton marketing avec le calendrier de tes clients',
    adviceIntro: "Je connais ton calendrier commercial par cœur. Chaque saison est une opportunité — voici comment l'exploiter pour maximiser ton CA sans effort.",
    duration: '4–6 sem. par saison', roi: '+35% CA sur période',
    contacts_fn: c => c.length,
    kpis: ['Taux ouverture cible 45%', 'Taux de conversion', 'CA généré sur période', 'Nouveaux contacts acquis'],
    phases: [
      { id: 'p1', name: 'Préparation J-14', week: 'J-14', actions: [
        { id: 'sais-1', ch: 'email',   title: "Email teaser 'Quelque chose arrive...'", desc: "Email mystère 2 semaines avant pour créer de l'anticipation — ne révèle rien encore.",   appee: true },
        { id: 'sais-2', ch: 'landing', title: 'Landing page événement saisonnier',       desc: "Page dédiée avec compte à rebours, offre et formulaire d'inscription anticipée.",       appee: false },
        { id: 'sais-3', ch: 'social',  title: 'Campagne teaser réseaux sociaux',          desc: "Posts teaser sur Instagram/Facebook + Stories avec hashtag dédié — 3-4 posts min.",    appee: false },
      ]},
      { id: 'p2', name: 'Lancement Jour J', week: 'Jour J', actions: [
        { id: 'sais-4', ch: 'email',   title: 'Email de lancement personnalisé', desc: "Message principal avec offre exclusive — prénom dans l'objet, urgence dans le corps.",        appee: true },
        { id: 'sais-5', ch: 'sms',     title: "SMS flash du jour J",              desc: "'Aujourd'hui seulement — offre réservée [prénom]' — taux de lecture instantané.",             appee: true },
        { id: 'sais-6', ch: 'print',   title: 'Flyer et affichage en magasin',   desc: "Affichage qui renforce l'ambiance saisonnière en point de vente — cohérence 360°.",          appee: false, optional: true },
        { id: 'sais-7', ch: 'event',   title: 'Animation ou événement',           desc: "Workshop, démo, dégustation, journée portes ouvertes — crée le buzz local.",                  appee: false, optional: true },
      ]},
      { id: 'p3', name: 'Relance & Clôture', week: 'J+5 à J+10', actions: [
        { id: 'sais-8',  ch: 'email', title: "Email relance 'Dernière chance'", desc: "Rappel aux non-convertis avec urgence forte et compte à rebours — taux de conversion x3.",     appee: true },
        { id: 'sais-9',  ch: 'sms',   title: "SMS 'Expire dans 24h'",           desc: "SMS ultra-court pour les indécis — heure d'expiration + lien direct.",                         appee: true },
        { id: 'sais-10', ch: 'email', title: 'Email bilan + teaser suite',       desc: "Merci + teaser de la prochaine campagne pour maintenir l'engagement au-delà de la promo.",   appee: true },
      ]},
    ],
  },
  {
    id: 'digital360', category: 'communication',
    icon: '📱', color: '#0891B2', bg: '#ECFEFF',
    title: 'Plan Communication Digital',
    tagline: 'Stratégie complète réseaux sociaux, e-mail & contenus',
    adviceIntro: "Je construis pour toi un plan éditorial complet. Chaque semaine, tu sais exactement quoi publier, sur quel canal et à quelle heure — plus d'improvisation.",
    duration: '6 mois', roi: 'ROI +180%',
    contacts_fn: c => c.length,
    kpis: ['Followers gagnés', 'Engagement rate cible 4%+', 'Leads générés', 'CA attribuable digital'],
    phases: [
      { id: 'p1', name: 'Audit & Stratégie', week: 'Sem. 1–2', actions: [
        { id: 'dig-1', ch: 'email',   title: 'Newsletter mensuelle régulière',  desc: "Newsletter avec actualités, conseils et offres — pilier indéboulonnable de la relation client.", appee: true },
        { id: 'dig-2', ch: 'social',  title: 'Calendrier éditorial 3 posts/sem', desc: "Planifie 3 posts/semaine en alternant les piliers : éducatif / coulisses / offre / témoignage. Outil recommandé : Later, Buffer ou un tableau Notion.",            appee: false },
        { id: 'dig-3', ch: 'landing', title: 'Page de capture leads',            desc: "Landing page pour collecter emails avec lead magnet : guide, promo, diagnostic gratuit.",       appee: false },
      ]},
      { id: 'p2', name: 'Contenus Réguliers', week: 'Mensuel', actions: [
        { id: 'dig-4', ch: 'social', title: 'Vidéo Reels / Stories',         desc: "1 Reel par semaine : coulisses, témoignage client, astuce produit — portée algorithmique x5.",     appee: false },
        { id: 'dig-5', ch: 'social', title: 'Posts LinkedIn (si B2B)',        desc: "2 posts par semaine : expertise, cas client, veille — positionnement d'autorité.",                appee: false, optional: true },
        { id: 'dig-6', ch: 'email',  title: 'Email segmenté mensuel ciblé',  desc: "Email ciblé par segment VIP/dormants/nouveaux — pertinence maximale = ouvertures maximales.",     appee: true },
      ]},
      { id: 'p3', name: 'Acquisition & Conversion', week: 'Trimestriel', actions: [
        { id: 'dig-7', ch: 'landing', title: 'Campagne génération leads',       desc: "Pub Facebook/Instagram avec landing page dédiée + pixel de conversion installé.",                appee: false },
        { id: 'dig-8', ch: 'email',   title: 'Séquence emails nurturing',       desc: "5 emails automatiques pour convertir les leads froids en clients — Appee s'en occupe.",         appee: true },
        { id: 'dig-9', ch: 'phone',   title: 'Qualification leads chauds',      desc: "Appels des leads les plus engagés pour finaliser la conversion — 60–70% de succès.",            appee: false, optional: true },
      ]},
    ],
  },
  {
    id: 'concours', category: 'acquisition',
    icon: '🎰', color: '#EF4444', bg: '#FEF2F2',
    title: 'Lancer un Concours',
    tagline: 'Booste ta visibilité et double ta base en 4 semaines',
    adviceIntro: "Un concours bien orchestré peut doubler ta base e-mail en 4 semaines. Je prépare tout — du teaser à la relance — pour maximiser les participations et les conversions.",
    duration: '4 semaines', roi: '+30% nouveaux contacts',
    contacts_fn: c => c.length,
    kpis: ['Participants inscrits', 'Partages organiques', 'Nouveaux emails collectés', 'Conversion post-concours'],
    phases: [
      { id: 'p1', name: 'Création & Teaser', week: 'Sem. 1', actions: [
        { id: 'conc-1', ch: 'landing', title: "Page d'inscription concours",  desc: "Landing page avec formulaire, règlement clair et visuel du lot attractif — design soigné.",       appee: false },
        { id: 'conc-2', ch: 'social',  title: 'Annonce réseaux sociaux',       desc: "'Gagne [lot] — participe maintenant !' — Post + Story + partage Reel pour diffusion max.",       appee: false },
        { id: 'conc-3', ch: 'email',   title: "Email annonce à la base",        desc: "Email à tous tes contacts existants pour booster les premières participations rapidement.",       appee: true },
      ]},
      { id: 'p2', name: 'Animation', week: 'Sem. 2–3', actions: [
        { id: 'conc-4', ch: 'sms',    title: 'SMS de rappel mi-concours',          desc: "SMS aux contacts non inscrits + rappel aux inscrits de partager — multiplication x3.",       appee: true },
        { id: 'conc-5', ch: 'social', title: 'Posts de relance et stories quoti.', desc: "Compteur de participants, témoignages, rappel du lot — maintient l'élan chaque jour.",        appee: false },
        { id: 'conc-6', ch: 'email',  title: "Email 'Encore quelques jours !'",   desc: "Relance des non-inscrits à J+10 — urgence douce, rappel du lot attractif.",                  appee: true },
      ]},
      { id: 'p3', name: 'Clôture & Conversion', week: 'Sem. 4', actions: [
        { id: 'conc-7', ch: 'social', title: 'Annonce du gagnant en live',          desc: "Post vidéo ou story de tirage au sort — transparence totale = confiance et engagement.",     appee: false },
        { id: 'conc-8', ch: 'email',  title: "Email consolation participants",       desc: "Offre exclusive pour tous les non-gagnants — convertit 15–25% des participants.",           appee: true },
        { id: 'conc-9', ch: 'email',  title: "Email bienvenue nouveaux inscrits",    desc: "Séquence onboarding pour les nouveaux contacts collectés — convertis-les en clients.",      appee: true },
      ]},
    ],
  },
  {
    id: 'enrichissement', category: 'data',
    icon: '📊', color: '#7C3AED', bg: '#F5F3FF',
    title: 'Enrichissement CRM & Data',
    tagline: 'Ta base est ton actif le plus précieux — enrichis-la',
    adviceIntro: "J'ai analysé ta base et trouvé des lacunes. Les contacts sans email ou sans date de naissance, c'est du chiffre d'affaires qui dort. Je vais t'aider à les compléter.",
    duration: '4 semaines', roi: '+16 pts qualité CRM',
    contacts_fn: c => c.filter(x => !x.email || !x.birth_date).length,
    kpis: ['Score qualité CRM +16pts', 'Emails manquants récupérés', 'Dates naissance ajoutées', 'Taux complétion profil'],
    phases: [
      { id: 'p1', name: 'Audit & Quick Wins', week: 'Sem. 1', actions: [
        { id: 'enr-1', ch: 'email',   title: "Email 'Mets à jour tes infos'", desc: "Email aux contacts incomplets : date de naissance + préférences avec avantage fidélité à la clé.", appee: true },
        { id: 'enr-2', ch: 'sms',     title: 'SMS collecte anniversaire',      desc: "SMS court avec lien formulaire : 'Dis-nous ton anniversaire et reçois un cadeau !' — simple.", appee: true },
        { id: 'enr-3', ch: 'instore', title: 'QR code / tablette en magasin', desc: "Formulaire digital sur tablette ou QR code pour collecter les infos manquantes en point de vente.", appee: false },
      ]},
      { id: 'p2', name: 'Campagne Collecte Active', week: 'Sem. 2–3', actions: [
        { id: 'enr-4', ch: 'landing', title: 'Page formulaire mise à jour',        desc: "Landing page dédiée avec champs profil + avantage fidélité à la clé — taux remplissage 40%.",  appee: false },
        { id: 'enr-5', ch: 'social',  title: "Post 'Mets à jour et gagne'",        desc: "Post Instagram/Facebook lié à la mise à jour du profil — gamification de la collecte data.",  appee: false, optional: true },
        { id: 'enr-6', ch: 'email',   title: 'Email relance non-répondants',        desc: "Rappel doux 10 jours après pour les contacts qui n'ont pas encore rempli leur profil.",       appee: true },
      ]},
      { id: 'p3', name: 'Nettoyage & Score', week: 'Sem. 4', actions: [
        { id: 'enr-7', ch: 'email', title: "Email 'Souhaites-tu rester en contact ?'", desc: "Dernière tentative pour les invalides avant suppression propre — RGPD compliant.",          appee: true },
        { id: 'enr-8', ch: 'email', title: 'Rapport qualité CRM',                      desc: "Résumé des améliorations : score avant/après, contacts complétés, projection prochains mois.", appee: true },
      ]},
    ],
  },

  // ─── Accompagnement 360° ──────────────────────────────────────────────────
  {
    id: 'plan-comm', category: 'accompagnement',
    icon: '📋', color: '#6366F1', bg: '#EEF2FF',
    title: 'Plan de Communication',
    tagline: 'Plan éditorial complet multicanal — 6 ou 12 mois',
    adviceIntro: "Je construis ton plan de communication de A à Z. Chaque mois, tu sais exactement quoi faire, sur quel canal et pour quel objectif. Choisis ta durée en haut — je m'adapte.",
    duration: '6 ou 12 mois', roi: 'ROI +180%',
    duration_options: ['6 mois', '12 mois'],
    contacts_fn: c => c.length,
    kpis: ['Audience gagnée', 'Engagement rate cible 4%+', 'Leads générés', 'CA attribuable'],
    phases: [
      { id: 'pc-m1', name: 'Mois 1 — Audit & Positionnement', week: 'M1', months: [6, 12], actions: [
        { id: 'pc-1',  ch: 'email',   title: 'Audit de la base contacts existante',    desc: "J'analyse ta base : qualité, segments, opportunités — le diagnostic de départ.",              appee: true },
        { id: 'pc-2',  ch: 'social',  title: 'Audit de tes réseaux sociaux actuels',   desc: "Analyse de ta présence actuelle : posts, engagement, concurrents. Je propose le positionnement.", appee: false },
        { id: 'pc-3',  ch: 'landing', title: 'Charte éditoriale & ton de voix',         desc: "Définis les piliers de contenu, le ton et la fréquence — socle de toute ta communication.",  appee: false },
      ]},
      { id: 'pc-m2', name: 'Mois 2 — Lancement Réseaux Sociaux', week: 'M2', months: [6, 12], actions: [
        { id: 'pc-4',  ch: 'social', title: 'Calendrier éditorial Instagram/Facebook',  desc: "Planifie 3 posts/semaine avec piliers alternés : éducatif / coulisses / offre / preuve sociale. Utilise Later, Buffer ou Meta Business Suite pour programmer.",        appee: false },
        { id: 'pc-5',  ch: 'social', title: 'Création des visuels et templates',        desc: "Crée les templates Canva réutilisables — cohérence visuelle sur tous les posts.",            appee: false },
        { id: 'pc-6',  ch: 'social', title: 'Stories quotidiennes',                     desc: "5-7 Stories/semaine : sondages, coulisses, avant/après — maintient l'algorithme actif.",      appee: false, optional: true },
      ]},
      { id: 'pc-m3', name: 'Mois 3 — Email Marketing & Newsletter', week: 'M3', months: [6, 12], actions: [
        { id: 'pc-7',  ch: 'email', title: 'Newsletter mensuelle pilier',               desc: "Newsletter régulière avec actualités, conseils et offres — pilier de la relation client.",    appee: true },
        { id: 'pc-8',  ch: 'email', title: 'Séquence bienvenue automatique',            desc: "5 emails automatiques pour les nouveaux abonnés — convertit leads en clients.",               appee: true },
        { id: 'pc-9',  ch: 'email', title: 'Segmentation et emails ciblés',            desc: "Emails séparés par segment : VIP / actifs / dormants — pertinence maximale.",                appee: true },
      ]},
      { id: 'pc-m4', name: 'Mois 4 — Contenu & Acquisition', week: 'M4', months: [6, 12], actions: [
        { id: 'pc-10', ch: 'landing', title: 'Page de capture leads + lead magnet',      desc: "Landing page avec guide gratuit ou offre découverte — commence à alimenter la base.",        appee: false },
        { id: 'pc-11', ch: 'social',  title: 'Premier Reel / vidéo courte',              desc: "1 Reel par semaine : coulisses, démonstration, témoignage — portée algorithmique x5.",       appee: false },
        { id: 'pc-12', ch: 'sms',     title: 'Mise en place SMS marketing',              desc: "Premier envoi SMS à la base existante — présentation du programme fidélité.",               appee: true, optional: true },
      ]},
      { id: 'pc-m5', name: 'Mois 5 — Activation & Promotion', week: 'M5', months: [6, 12], actions: [
        { id: 'pc-13', ch: 'email',  title: 'Campagne promo ou offre saisonnière',       desc: "Campagne email complète : teaser + lancement + relance — séquence testée et optimisée.",     appee: true },
        { id: 'pc-14', ch: 'social', title: 'Campagne de visibilité payante',            desc: "Publicité Facebook/Instagram ciblée sur ta zone géographique + intérêts clients.",          appee: false, optional: true },
        { id: 'pc-15', ch: 'event',  title: 'Événement client ou animation',             desc: "Soirée, workshop ou journée portes ouvertes — crée le buzz local et la fidélité.",          appee: false, optional: true },
      ]},
      { id: 'pc-m6', name: 'Mois 6 — Bilan & Optimisation', week: 'M6', months: [6, 12], actions: [
        { id: 'pc-16', ch: 'email', title: 'Rapport de performance 6 mois',              desc: "J'analyse les résultats : audience, engagement, CA — avec recommandations d'optimisation.",  appee: true },
        { id: 'pc-17', ch: 'email', title: 'Sondage satisfaction clients',               desc: "Email de satisfaction à toute la base — améliore l'offre et récolte des témoignages.",      appee: true },
        { id: 'pc-18', ch: 'social', title: 'Post bilan + teaser saison 2',             desc: "'6 mois avec vous — merci !' — engagement + annonce de la suite.",                           appee: false },
      ]},
      { id: 'pc-m7', name: 'Mois 7 — Ambassadeurs & Témoignages', week: 'M7', months: [12], actions: [
        { id: 'pc-19', ch: 'email',  title: 'Programme ambassadeurs clients',           desc: "Sélectionne 5–10 clients fidèles, propose-leur un partenariat : témoignage contre avantage.", appee: true },
        { id: 'pc-20', ch: 'social', title: 'Série de posts témoignages',               desc: "Posts clients satisfaits 1 fois/semaine — preuve sociale = meilleure publicité.",           appee: false },
        { id: 'pc-21', ch: 'landing', title: 'Page témoignages dédiée',                 desc: "Landing page 'Ils nous font confiance' avec avis, chiffres et photos clients.",              appee: false, optional: true },
      ]},
      { id: 'pc-m8', name: 'Mois 8 — Vidéo & Contenus Longs', week: 'M8', months: [12], actions: [
        { id: 'pc-22', ch: 'social', title: 'Série Reels thématique',                   desc: "Mini-série de 4 Reels sur un thème fort : ton expertise, ta différence, tes valeurs.",      appee: false },
        { id: 'pc-23', ch: 'email',  title: 'Newsletter enrichie avec contenu vidéo',   desc: "Newsletter avec vidéo embarquée ou aperçu — taux de clic email +65%.",                      appee: true, optional: true },
        { id: 'pc-24', ch: 'landing', title: 'Page ressources / blog',                  desc: "1 article de fond par mois : SEO + expertise + partage sur réseaux.",                        appee: false, optional: true },
      ]},
      { id: 'pc-m9', name: 'Mois 9 — Partenariats & Co-marketing', week: 'M9', months: [12], actions: [
        { id: 'pc-25', ch: 'email',  title: 'Email co-marketing partenaire local',      desc: "Collaboration avec un business complémentaire : échange de visibilité sur les bases.",      appee: true },
        { id: 'pc-26', ch: 'social', title: 'Post croisé avec un partenaire',           desc: "Publication commune Instagram/Facebook — touche une nouvelle audience sans budget pub.",     appee: false },
        { id: 'pc-27', ch: 'event',  title: 'Événement co-organisé',                   desc: "Atelier, conférence ou pop-up en partenariat — mutualise les coûts et double l'audience.",  appee: false, optional: true },
      ]},
      { id: 'pc-m10', name: 'Mois 10 — Événements & Animations', week: 'M10', months: [12], actions: [
        { id: 'pc-28', ch: 'event',  title: 'Événement annuel clients',                 desc: "Grand rendez-vous annuel : soirée, fête, remise de prix — fidélise et crée des ambassadeurs.", appee: false },
        { id: 'pc-29', ch: 'email',  title: 'Campagne invitation événement',            desc: "Séquence email d'invitation : teaser + invitation + rappel + recap post-événement.",        appee: true },
        { id: 'pc-30', ch: 'social', title: 'Couverture live de l\'événement',          desc: "Stories, Reels en direct — 3× plus d'engagement que les posts classiques.",                appee: false },
      ]},
      { id: 'pc-m11', name: 'Mois 11 — Campagnes Saisonnières', week: 'M11', months: [12], actions: [
        { id: 'pc-31', ch: 'email',  title: 'Campagne Black Friday / Noël',             desc: "Campagne complète avec teaser, compte à rebours et relances — pic de chiffre d'affaires.",  appee: true },
        { id: 'pc-32', ch: 'sms',    title: 'SMS d\'urgence offre limitée',             desc: "SMS le jour J de l'offre : urgence + personnalisation — taux d'achat immédiat x3.",         appee: true },
        { id: 'pc-33', ch: 'print',  title: 'Flyer saisonnier en magasin',             desc: "Affichage et flyer pour renforcer l'ambiance festive en point de vente.",                   appee: false, optional: true },
      ]},
      { id: 'pc-m12', name: 'Mois 12 — Bilan Annuel & Stratégie Suivante', week: 'M12', months: [12], actions: [
        { id: 'pc-34', ch: 'email',  title: "Email bilan 'Une année ensemble'",         desc: "Message récapitulatif de l'année avec stats : posts, emails, événements, CA — très impactant.", appee: true },
        { id: 'pc-35', ch: 'email',  title: 'Rapport annuel complet',                   desc: "Je génère le rapport de performance annuel + recommandations stratégiques pour l'an prochain.", appee: true },
        { id: 'pc-36', ch: 'social', title: 'Post de fin d\'année + vœux',              desc: "Publication sincère et humaine — engagement très fort en décembre.",                         appee: false },
      ]},
    ],
  },

  // ─── Événement Privé ─────────────────────────────────────────────────────
  {
    id: 'event-prive', category: 'evenement',
    icon: '🥂', color: '#DB2777', bg: '#FDF2F8',
    title: 'Organiser un Événement Privé',
    tagline: 'Soirée exclusive, atelier VIP ou dîner clients — mémorable et rentable',
    adviceIntro: "Un événement privé bien orchestré fidélise tes meilleurs clients pour des années. Je t'accompagne de la réservation du lieu jusqu'au suivi post-event — chaque étape est planifiée.",
    duration: '6 semaines', roi: 'Fidélisation long terme',
    contacts_fn: c => c.filter(x => x.category === 'VIP' || (x.quality_score || 0) > 70).length,
    kpis: ['Taux de présence cible 80%+', 'NPS post-événement 8+/10', 'Taux de conversion post-event 30%', 'Nouveaux contacts collectés'],
    phases: [
      { id: 'ep1', name: 'Conception & Logistique', week: 'J-42 à J-28', actions: [
        { id: 'ep-1',  ch: 'phone',   title: 'Définir le thème, le format et les objectifs',            desc: "Soirée VIP, atelier, dîner, journée découverte ? Fixe le thème, le nombre de places (10–30 recommandé) et l'objectif principal — fidéliser, vendre, remercier.",                        appee: false },
        { id: 'ep-2',  ch: 'phone',   title: 'Réserver le lieu / la salle',                             desc: "Restaurant privatisé, salle de réception, atelier artisan, domaine ? Réserve l'endroit idéal. Prévois un acompte et vérifie accessibilité parking. Budget recommandé : CHF 25–60/pers.",    appee: false },
        { id: 'ep-3',  ch: 'phone',   title: 'Commander le traiteur ou la restauration',                desc: "Cocktail dinatoire, buffet, repas assis ou brunch ? Contacte le traiteur pour le menu, les régimes spéciaux et la décoration de table. Confirme la commande 2 semaines avant le J.",        appee: false },
        { id: 'ep-4',  ch: 'instore', title: 'Choisir et commander les cadeaux clients',                desc: "Un cadeau soigné amplifie le souvenir de la soirée. Idées : produit de ta gamme, coffret artisanal local, objet gravé au prénom, bon expérience. Budget CHF 20–50/pers.",                  appee: false, optional: true },
        { id: 'ep-5',  ch: 'email',   title: 'Créer la liste des invités et bloquer les places',        desc: "Sélectionne les clients à inviter (VIP, top 20%, ancienneté). Définis le nombre de places maximum et crée une liste de réserve. Appee t'aide à segmenter depuis ta base.",                  appee: true },
      ]},
      { id: 'ep2', name: 'Invitations & RSVP', week: 'J-28 à J-14', actions: [
        { id: 'ep-6',  ch: 'email',   title: "Email d'invitation exclusif personnalisé",                desc: "'Tu fais partie des personnes que nous avons choisi d'inviter.' — prénom, date, lieu, programme. Ton + chaleureux et exclusif. Lien RSVP clair. Appee rédige et envoie.",                   appee: true },
        { id: 'ep-7',  ch: 'sms',     title: 'SMS de confirmation invitation',                          desc: "SMS le lendemain de l'email pour les non-ouverts : 'Tu as reçu notre invitation par email — on espère te voir !' — court, chaleureux, lien RSVP.",                                        appee: true },
        { id: 'ep-8',  ch: 'print',   title: 'Carton d\'invitation papier premium (optionnel)',          desc: "Pour les clients VIP ou les occasions importantes : carte imprimée de qualité, enveloppe à la main, cachet de cire. Expédition 3 semaines avant. Différenciation totale.",                 appee: false, optional: true },
        { id: 'ep-9',  ch: 'landing', title: 'Page RSVP avec compteur de places',                       desc: "Page de réponse en ligne avec formulaire (présence + régimes alimentaires + +1 éventuel). Affiche le nombre de places restantes pour créer l'urgence — 'Plus que 5 places'.",              appee: false, optional: true },
        { id: 'ep-10', ch: 'sms',     title: 'SMS relance non-répondants J-10',                         desc: "SMS doux aux invités qui n'ont pas encore répondu. 'On a besoin de ton retour pour finaliser les préparatifs — 3 clics et c'est fait.' Lien RSVP direct.",                                appee: true },
      ]},
      { id: 'ep3', name: 'Préparation Finale', week: 'J-7 à J-1', actions: [
        { id: 'ep-11', ch: 'email',   title: 'Email programme détaillé & infos pratiques',              desc: "Envoi à tous les confirmés : programme de la soirée, adresse précise, parking, tenue conseillée, contact le jour J. Crée l'anticipation et évite les questions.",                           appee: true },
        { id: 'ep-12', ch: 'sms',     title: 'SMS de rappel J-2 avec localisation',                     desc: "'On se retrouve dans 2 jours — voici l'adresse exacte [lien Google Maps] et le programme. On a hâte de te voir !' — simple, rassurant.",                                                  appee: true },
        { id: 'ep-13', ch: 'phone',   title: 'Confirmer les prestataires (traiteur, salle, sono…)',     desc: "Appels de confirmation à tous les prestataires : traiteur (nombre de couverts final), salle (horaires d'accès), technicien son/lumière si besoin. Zéro surprise le Jour J.",               appee: false },
        { id: 'ep-14', ch: 'instore', title: 'Préparer la liste de présence et les badges',             desc: "Liste d'émargement avec prénom + nom. Badges nominatifs imprimés. Prévois un plan de table si repas assis. Disposition des cadeaux sur les chaises ou au départ selon le concept.",         appee: false },
        { id: 'ep-15', ch: 'social',  title: 'Story teaser "Quelque chose se prépare…"',               desc: "Story Instagram mystère sans dévoiler les invités : décoration, ambiance, détail du lieu. Crée l'intrigue sans révéler la nature privée de l'événement.",                                  appee: false, optional: true },
      ]},
      { id: 'ep4', name: 'Jour J', week: 'Jour de l\'événement', actions: [
        { id: 'ep-16', ch: 'instore', title: 'Accueil, émargement et remise des cadeaux',               desc: "Accueille chaque invité par son prénom. Fais signer la liste de présence (utile pour mettre à jour la base CRM). Remet le cadeau surprise à l'arrivée ou au départ selon le concept.",    appee: false },
        { id: 'ep-17', ch: 'social',  title: 'Photos et vidéos de l\'ambiance',                         desc: "Prends 20+ photos qualité et quelques vidéos courtes. Cadre sur l'ambiance, les sourires, les moments forts — PAS de photos sans accord des invités.",                                    appee: false },
        { id: 'ep-18', ch: 'social',  title: 'Story live discrète en temps réel',                       desc: "1–2 Stories pendant l'événement pour ceux qui n'ont pas pu venir — crée l'envie pour la prochaine fois. Reste dans le ton exclusif.",                                                     appee: false, optional: true },
        { id: 'ep-19', ch: 'instore', title: 'Collecter les contacts des +1 ou nouveaux arrivants',     desc: "Profite des présentations pour enrichir ta base CRM : prénom, email ou téléphone des personnes que tu rencontres. C'est le moment idéal.",                                                 appee: false, optional: true },
      ]},
      { id: 'ep5', name: 'Suivi Post-Événement', week: 'J+1 à J+7', actions: [
        { id: 'ep-20', ch: 'email',   title: 'Email remerciement personnalisé dans les 24h',            desc: "'Hier soir était vraiment spécial — merci d'y avoir été.' Email chaleureux avec quelques photos de l'événement. Mention de la prochaine occasion. Appee rédige et envoie.",               appee: true },
        { id: 'ep-21', ch: 'sms',     title: 'SMS de suivi "Merci d\'être venu"',                       desc: "SMS court et humain dans les 48h : 'C'était super de passer ce moment avec toi — à très vite !' — simple mais fort.",                                                                    appee: true },
        { id: 'ep-22', ch: 'social',  title: 'Post album photos de l\'événement',                       desc: "Post Instagram/Facebook avec les meilleures photos (avec accords). Caption émotionnelle et remerciements sincères. Tague les invités si accord — engagement maximum.",                     appee: false },
        { id: 'ep-23', ch: 'email',   title: 'Email offre exclusive "Souvenir de la soirée"',           desc: "Dans les 7 jours : offre réservée aux participants. 'En souvenir de cette belle soirée, voici quelque chose que je t'ai réservé.' — taux de conversion post-event 25–35%.",              appee: true },
        { id: 'ep-24', ch: 'email',   title: 'Mise à jour CRM & bilan',                                 desc: "Appee met à jour les fiches contacts : présence confirmée, nouveaux contacts ajoutés, notes relationnelles. Bilan chiffré : présents / absents / conversions post-event.",               appee: true },
      ]},
    ],
  },

  // ─── Événement Public ────────────────────────────────────────────────────
  {
    id: 'event-public', category: 'evenement',
    icon: '🎪', color: '#7C3AED', bg: '#F5F3FF',
    title: 'Organiser un Événement Public',
    tagline: 'Portes ouvertes, salon, lancement produit — visibilité maximale',
    adviceIntro: "Un événement public bien communiqué génère de la notoriété, des leads qualifiés et du chiffre d'affaires immédiat. Je construis toute la stratégie de communication pour toi — de l'annonce jusqu'au bilan.",
    duration: '8 semaines', roi: '+30% nouveaux contacts',
    contacts_fn: c => c.length,
    kpis: ['Nombre de participants', 'Leads collectés sur place', 'CA généré le jour J', 'Portée totale communication'],
    phases: [
      { id: 'pub1', name: 'Conception & Logistique', week: 'J-56 à J-35', actions: [
        { id: 'pub-1',  ch: 'phone',   title: 'Définir le concept, le thème et le programme',           desc: "Portes ouvertes, lancement produit, journée découverte, salon, conférence, atelier public ? Fixe la cible (tout public, professionnels, familles), la durée, les animations prévues.",       appee: false },
        { id: 'pub-2',  ch: 'phone',   title: 'Réserver le lieu et la capacité maximale',               desc: "Local, salle polyvalente, espace extérieur, lieu partenaire ? Vérifie la capacité, l'accessibilité PMR, le parking, les exigences légales (déclaration mairie si >300 pers.).",             appee: false },
        { id: 'pub-3',  ch: 'phone',   title: 'Organiser le traiteur / restauration',                   desc: "Buffet, stands food, cocktail ou simple bar à eau ? Adapte selon la durée et le budget. Pensez allergènes et offre végétarienne. Demande plusieurs devis comparatifs.",                     appee: false },
        { id: 'pub-4',  ch: 'instore', title: 'Choisir les cadeaux, goodies ou lots à offrir',          desc: "Sacs de goodies, produits de démonstration, lots concours sur place ? Commande avec 3 semaines d'avance. Floque avec ton logo si possible — outil de communication en soi.",               appee: false, optional: true },
        { id: 'pub-5',  ch: 'landing', title: 'Décider si les places sont limitées ou libres',          desc: "Places limitées = urgence + qualification (formulaire inscription). Entrée libre = volume maximal. Crée la landing page avec ou sans jauge de places — puis lance une campagne email/SMS depuis Appee.",                appee: false },
        { id: 'pub-6',  ch: 'phone',   title: 'Identifier et contacter des partenaires co-organisateurs', desc: "Partenaire local, mairie, chambre de commerce, association professionnelle ? Un co-organisateur double la visibilité et partage les coûts. Fixe les rôles clairement.",                 appee: false, optional: true },
      ]},
      { id: 'pub2', name: 'Communication & Annonces', week: 'J-35 à J-14', actions: [
        { id: 'pub-7',  ch: 'social',  title: 'Créer l\'événement Facebook et l\'activer',              desc: "Crée l'événement Facebook avec visuels soignés, description complète, date, lieu et lien d'inscription. Invite tous tes abonnés. Active la billetterie gratuite si places limitées. Partage en story.", appee: false },
        { id: 'pub-8',  ch: 'landing', title: 'Landing page d\'inscription avec compteur de places',    desc: "Page dédiée avec visuels, programme, plan d'accès et formulaire d'inscription. Si places limitées : jauge visible 'XX places restantes'. Intègre pixel Facebook pour le retargeting.",       appee: false },
        { id: 'pub-9',  ch: 'email',   title: 'Email d\'annonce à toute la base existante',             desc: "Premier email à tous tes contacts : date, lieu, programme, lien d'inscription. Objet percutant : '[Prénom], tu es invité(e)…' — Appee rédige et envoie avec personnalisation.",              appee: true },
        { id: 'pub-10', ch: 'sms',     title: 'SMS d\'annonce à la base',                               desc: "SMS court 3 jours après l'email : 'Événement [nom] le [date] à [lieu] — inscription gratuite : [lien]. On t'attend !' — texte court, lien tracké.",                                        appee: true },
        { id: 'pub-11', ch: 'print',   title: 'Flyers, affiches et kakémono à distribuer',             desc: "Format A5 flyer (distribution) + A3 affiche (vitrine, partenaires) + kakémono entrée le Jour J. Design cohérent avec les visuels digitaux — imprimerie locale ou en ligne.",                appee: false },
        { id: 'pub-12', ch: 'social',  title: 'Campagne de posts et stories pendant 3 semaines',       desc: "Planning : 3 posts/semaine + 1 story/jour. Alternes : programme / témoignage / countdown / coulisses préparation. Hashtag dédié à l'événement.",                                            appee: false },
        { id: 'pub-13', ch: 'print',   title: 'Communiqué de presse et agenda local',                  desc: "Rédige un communiqué pour la presse locale, les agendas culturels et les groupes Facebook locaux. Délai de parution : J-21 minimum. Soumets à : mairie, OT, journaux gratuits.",            appee: false, optional: true },
      ]},
      { id: 'pub3', name: 'Relances & Inscriptions', week: 'J-14 à J-3', actions: [
        { id: 'pub-14', ch: 'email',   title: 'Email relance "Plus que 2 semaines"',                   desc: "Rappel aux non-inscrits avec urgence : nombre de places restantes si limité, programme mis à jour, nouveau visuel. Appee envoie uniquement aux non-confirmés.",                              appee: true },
        { id: 'pub-15', ch: 'sms',     title: 'SMS relance J-10 non-inscrits',                         desc: "'Encore [X] places disponibles pour notre événement — inscris-toi avant qu'il soit complet !' Fonctionne même pour événements en entrée libre — crée l'urgence psychologique.",             appee: true },
        { id: 'pub-16', ch: 'social',  title: 'Stories compte à rebours quotidien J-14 à J-1',        desc: "Story avec sticker compte à rebours Instagram — chaque jour crée de l'anticipation. Varie les contenus : annonce intervenant, démo produit, teaser ambiance, interview organisateur.",       appee: false },
        { id: 'pub-17', ch: 'sms',     title: 'SMS alerte "Places presque complètes" si applicable',  desc: "SMS automatique quand 80% des places sont remplies : urgence maximale — taux de conversion +40%. Appee gère le déclenchement si landing page avec jauge est en place.",                    appee: true, optional: true },
        { id: 'pub-18', ch: 'print',   title: 'Distribution flyers en zone de chalandise',             desc: "Distribution ciblée : commerces proches, marchés, salles de sport, pharmacies, offices de tourisme. Toujours pointer sur un QR code tracké.",                                              appee: false, optional: true },
      ]},
      { id: 'pub4', name: 'Préparation Finale', week: 'J-3 à J-1', actions: [
        { id: 'pub-19', ch: 'email',   title: 'Email "J-2 — Tout ce qu\'il faut savoir"',              desc: "Email aux inscrits : programme horaire, adresse GPS, parking, accessibilité, ce qui les attend. Ton enthousiaste et pratique. Réduit les no-shows de 30%.",                                 appee: true },
        { id: 'pub-20', ch: 'sms',     title: 'SMS rappel J-1 avec lien localisation',                 desc: "'On se retrouve demain ! [nom event] — [heure] à [lieu] — lien GPS [lien]. Hâte de t'y voir !' — 3 lignes max, lien direct.",                                                             appee: true },
        { id: 'pub-21', ch: 'instore', title: 'Préparer la signalétique et le matériel sur place',    desc: "Kakémono, direction fléchage, roll-up, tablette d'inscription nouveaux contacts, badges équipe, liste présence, décoration. Visite technique du lieu J-1 obligatoire.",                     appee: false },
        { id: 'pub-22', ch: 'social',  title: 'Story "Demain c\'est le grand jour !"',                desc: "Story Instagram + Facebook avec compteur final et appel à l'action. Si billets : lien de dernière minute. Crée l'excitation pour l'audience qui hésite encore.",                            appee: false, optional: true },
      ]},
      { id: 'pub5', name: 'Jour J', week: 'Jour de l\'événement', actions: [
        { id: 'pub-23', ch: 'instore', title: 'Accueil, badges et collecte de contacts',               desc: "Table d'accueil avec liste présence + tablette ou QR code pour les entrées libres (prénom + email). Offrir un badge nominatif crée une ambiance professionnelle.",                           appee: false },
        { id: 'pub-24', ch: 'social',  title: 'Couverture live : stories et posts en temps réel',     desc: "1 story toutes les 30–45 minutes avec moments forts, ambiance, sourires. 1 post pendant l'événement. Utilise le hashtag dédié et encourage les participants à l'utiliser.",                 appee: false },
        { id: 'pub-25', ch: 'instore', title: 'Animation, démos et remise des goodies',               desc: "Assure le déroulé du programme. Remise des goodies ou cadeaux en fin d'événement — mémorise la positive last impression. Prends des photos de qualité.",                                  appee: false },
        { id: 'pub-26', ch: 'social',  title: 'Post Instagram / Facebook en fin d\'événement',        desc: "'C'est une magnifique journée qui se termine — merci à tous !' Post avec photo forte + invitation à commenter leur moment préféré. Génère de l'engagement organique post-event.",            appee: false },
      ]},
      { id: 'pub6', name: 'Suivi & Bilan', week: 'J+1 à J+10', actions: [
        { id: 'pub-27', ch: 'email',   title: 'Email remerciement à tous les participants',            desc: "'Merci d'avoir été là — c'était une journée formidable.' Email dans les 24h avec photos, résumé et lien vers l'album. Appee envoie à tous les inscrits et présents.",                       appee: true },
        { id: 'pub-28', ch: 'email',   title: 'Email offre exclusive "Merci d\'être venu"',            desc: "Offre limitée dans les 7 jours post-event pour convertir les participants en clients ou déclencher le 2e achat. 'En souvenir de cette belle journée…' — taux de conversion 20–30%.",        appee: true },
        { id: 'pub-29', ch: 'social',  title: 'Post album photos + remerciements publics',            desc: "Post Facebook/Instagram avec galerie photos (accord participants). Caption sincère avec remerciements. Invite les absents à la prochaine édition.",                                          appee: false },
        { id: 'pub-30', ch: 'email',   title: 'Séquence onboarding pour les nouveaux contacts',       desc: "Appee crée une séquence de 3 emails pour convertir les nouveaux contacts collectés sur place : bienvenue + présentation + première offre. Automatique, sans effort de ta part.",             appee: true },
        { id: 'pub-31', ch: 'email',   title: 'Bilan complet : participants, leads, CA, portée',      desc: "Rapport Appee : nombre de présents, contacts collectés, CA généré, portée totale communication, engagement social. Base pour planifier la prochaine édition.",                              appee: true },
      ]},
    ],
  },

  // ─── LANCEMENT PRODUIT / SERVICE ─────────────────────────────────────────────
  {
    id: 'lancement-produit',
    category: 'lancement',
    icon: '🚀',
    color: '#8B5CF6',
    bg: '#F5F3FF',
    title: 'Lancer mon Activité, un Produit ou un Service',
    tagline: 'Nouvelle boîte, nouveau produit ou nouveau service : crée l\'attente, réussis le jour J, fidélise tes premiers clients',
    adviceIntro: "Un lancement réussi ne s'improvise pas. J'ai structuré un plan complet en 5 phases sur 6 semaines : teaser, buzz, jour J, activation et consolidation. Chaque étape est pensée pour maximiser la visibilité, convertir tes contacts existants en premiers clients et transformer ces premiers clients en ambassadeurs.",
    duration: '6 semaines',
    roi: '+35% CA mois J',
    contacts_fn: c => c.length,
    kpis: [
      'Taux de conversion liste d\'attente → achat',
      'CA généré J+7 / J+30',
      'Taux d\'ouverture emails lancement',
      'Nombre de partages & parrainages',
      'Satisfaction premiers clients (NPS)',
    ],
    phases: [
      {
        id: 'lp1',
        name: 'Préparation & Liste d\'attente',
        week: 'J-21 à J-8',
        actions: [
          { id: 'lp-1',  ch: 'email',   title: 'Email "Quelque chose arrive…" à toute ta base',              desc: "Premier teaser mystère envoyé à tous tes contacts. Pas de détails, juste une promesse : 'Je travaille sur quelque chose qui va changer la donne pour toi. Je t\'en dis plus très bientôt.' Objectif : créer de la curiosité et mesurer l'engagement initial.", appee: true },
          { id: 'lp-2',  ch: 'landing', title: 'Page liste d\'attente (early access)',                        desc: "Crée une page simple : titre percutant, 3 bénéfices clés du produit/service, formulaire de capture email. Promesse : les inscrits seront les premiers prévenus et auront un avantage exclusif (prix early bird, bonus, accès prioritaire).", appee: false },
          { id: 'lp-3',  ch: 'social',  title: 'Posts teaser sur les réseaux',                                desc: "Série de 3 posts sur Facebook/Instagram (J-21, J-14, J-7) avec compte à rebours visuel. Ton mystérieux, visuel intrigant. Invite tes abonnés à s'inscrire sur la liste d'attente pour être les premiers informés.", appee: false },
          { id: 'lp-4',  ch: 'email',   title: 'Email confirmation inscription liste d\'attente',             desc: "Email automatique envoyé à chaque personne qui s'inscrit sur la liste d'attente. Confirme leur place, rappelle l'avantage exclusif réservé et crée de l'impatience. Appee gère l'envoi automatique à chaque nouvelle inscription.", appee: true },
          { id: 'lp-5',  ch: 'phone',   title: 'Appels VIP : annonce en avant-première à tes meilleurs clients', desc: "Appelle personnellement tes 10–20 clients VIP pour leur annoncer le lancement en avant-première. Demande leur feedback, propose-leur un avantage exclusif. Ces appels créent des ambassadeurs avant même le lancement.", appee: false },
        ],
      },
      {
        id: 'lp2',
        name: 'Teaser & Révélation progressive',
        week: 'J-7 à J-2',
        actions: [
          { id: 'lp-6',  ch: 'email',   title: 'Email "La révélation : voici ce que j\'ai préparé pour toi"', desc: "Email à toute la liste d'attente + base contacts. Révèle officiellement le produit/service : nom, bénéfice principal, photos/visuels. Annonce la date de lancement et l'offre early bird. Crée l'urgence sans vendre encore.", appee: true },
          { id: 'lp-7',  ch: 'email',   title: 'Email "Les coulisses" — comment c\'est né',                   desc: "Histoire personnelle du produit/service : pourquoi tu l'as créé, le problème qu'il résout, ton parcours. Les emails storytelling ont un taux d'ouverture 40% supérieur. Humanise le lancement et renforce la confiance.", appee: true },
          { id: 'lp-8',  ch: 'social',  title: 'Unboxing / démonstration en stories',                         desc: "Stories ou Reels montrant le produit en action, les coulisses de préparation, les premiers retours de testeurs. Minimum 5 stories sur les 5 jours avant le lancement. Authentique, sans surproduction.", appee: false },
          { id: 'lp-9',  ch: 'email',   title: 'Email "J-2 : ta place early bird expire bientôt"',            desc: "Rappel d'urgence à tous les inscrits liste d'attente : leur accès prioritaire / prix réduit expire à minuit J+2 après le lancement. Inclure un résumé des bénéfices et un bouton d'achat pré-activé.", appee: true },
          { id: 'lp-10', ch: 'sms',     title: 'SMS teaser J-1 aux contacts VIP',                             desc: "Message court et percutant aux clients VIP : 'Demain, c'est le grand jour ! Ton accès prioritaire t'attend 🚀 [prénom]'. Maximum 160 caractères, personnalisé. Taux d'ouverture SMS : 95%.", appee: true, optional: true },
        ],
      },
      {
        id: 'lp3',
        name: 'Jour J — Le Lancement Officiel',
        week: 'Jour J',
        actions: [
          { id: 'lp-11', ch: 'email',   title: 'Email lancement 9h00 — "C\'est live !"',                      desc: "L'email le plus important. Objet : '[Prénom], c\'est maintenant ! [Nom produit] est disponible.' Corps : bénéfice principal, offre early bird avec timer 48h, témoignages si disponibles, CTA unique et gros. Appee l'envoie exactement à 9h00 à toute ta base.", appee: true },
          { id: 'lp-12', ch: 'email',   title: 'Email rappel 18h00 — "Quelques heures pour le tarif early bird"', desc: "Second email le jour du lancement, envoyé à 18h uniquement aux contacts qui n'ont pas ouvert l'email de 9h. Objet différent, angle différent (bénéfice #2 ou témoignage), même urgence. Augmente les conversions de 20–40% sans doubler les envois à ceux qui ont déjà vu.", appee: true },
          { id: 'lp-13', ch: 'social',  title: 'Post annonce officielle + stories "C\'est live"',              desc: "Post Facebook/Instagram : photo produit soignée, texte court et punch, lien en bio. Stories tout au long de la journée : réactions en temps réel, premières commandes, compte à rebours early bird. Minimum 3 stories dans la journée.", appee: false },
          { id: 'lp-14', ch: 'phone',   title: 'Appels de suivi aux VIP qui n\'ont pas encore acheté',         desc: "En fin de journée, appelle les VIP contactés en J-7 qui n'ont pas encore finalisé leur achat. Approche naturelle : 'Tu as eu le temps de regarder ? Tu as des questions ?' Pas de pression, juste de la disponibilité.", appee: false },
          { id: 'lp-15', ch: 'instore', title: 'Annonce en magasin / en réunion client si pertinent',          desc: "Si tu as un point de vente physique ou des RDV clients réguliers, le jour J est l'occasion de présenter le produit/service en personne. Prépare un pitch de 2 minutes avec les bénéfices clés et une offre exclusive 'vu ici'.", appee: false, optional: true },
        ],
      },
      {
        id: 'lp4',
        name: 'Activation — Transformer les tièdes en acheteurs',
        week: 'J+1 à J+14',
        actions: [
          { id: 'lp-16', ch: 'email',   title: 'Email J+2 — "Dernières heures pour le tarif early bird"',      desc: "Compte à rebours final sur l'offre de lancement. Objet avec le timer : 'Il reste 6h [Prénom] ⏰'. Corps : synthèse des bénéfices + résumé FAQ + CTA urgent. Dernier rappel avant retour au tarif normal.", appee: true },
          { id: 'lp-17', ch: 'email',   title: 'Email J+5 — Premiers témoignages clients',                     desc: "Email social proof : retours des premiers acheteurs (screenshots, citations, résultats). 'Voici ce que [Prénom] a obtenu en 3 jours.' Rassure les hésitants et relance ceux qui n'ont pas encore sauté le pas.", appee: true },
          { id: 'lp-18', ch: 'email',   title: 'Email J+10 — FAQ : vos questions, mes réponses',               desc: "Recense les vraies questions reçues par email/téléphone et réponds publiquement. Format Q&A simple. Objectif : lever les dernières objections. 'Est-ce que ça marche si… ? Combien de temps avant les résultats ?'", appee: true },
          { id: 'lp-19', ch: 'social',  title: 'Posts résultats & témoignages J+3 à J+10',                     desc: "Série de posts présentant les premiers retours clients (avec permission). Chaque témoignage devient un post. Relance l'algorithme et maintient la visibilité du lancement pendant 2 semaines.", appee: false },
          { id: 'lp-20', ch: 'email',   title: 'Séquence onboarding automatique aux nouveaux acheteurs',       desc: "Appee envoie automatiquement 3 emails aux nouveaux clients : J+1 'Bienvenue + comment bien démarrer', J+4 'Conseil pro pour aller plus loin', J+10 'Es-tu satisfait ? Ton avis compte'. Réduit le churn et crée des ambassadeurs.", appee: true },
          { id: 'lp-21', ch: 'email',   title: 'Email parrainage — offre pour chaque client qui recommande',   desc: "'Tu adores [produit] ? Offre à un ami et gagne [récompense].' Email envoyé J+7 aux acheteurs satisfaits. Le bouche-à-oreille digital est le canal d'acquisition le moins cher. Appee intègre un code de parrainage unique par client.", appee: true, optional: true },
        ],
      },
      {
        id: 'lp5',
        name: 'Consolidation & Bilan',
        week: 'J+15 à J+30',
        actions: [
          { id: 'lp-22', ch: 'email',   title: 'Email relance aux contacts non-acheteurs avec angle différent', desc: "Dernier email aux contacts qui n'ont pas acheté. Angle complètement différent des précédents : nouvelle promesse, nouveau bénéfice mis en avant, ou format 'histoire d'un client qui hésitait comme toi'. Pas d'urgence artificielle, juste de la valeur.", appee: true },
          { id: 'lp-23', ch: 'email',   title: 'Email J+30 — Bilan & prochaine étape pour les acheteurs',      desc: "Email aux acheteurs : 'Un mois avec [produit] — où en es-tu ?' Propose une ressource bonus, invite au prochain webinaire, ou présente un produit complémentaire. Objectif : 2e achat ou montée en gamme.", appee: true },
          { id: 'lp-24', ch: 'email',   title: 'Rapport de lancement complet Appee',                           desc: "Rapport automatique Appee : CA total généré, taux de conversion par email, segment le plus réactif, meilleur canal, NPS des premiers clients, recommandations pour le prochain lancement.", appee: true },
          { id: 'lp-25', ch: 'social',  title: 'Post bilan public : merci + chiffres clés',                    desc: "Post de clôture de campagne : remerciements à la communauté, quelques chiffres marquants (X clients en 30 jours, Y retours positifs). Montre la traction et renforce la légitimité du produit pour les futurs prospects.", appee: false },
          { id: 'lp-26', ch: 'email',   title: 'Segmentation post-lancement : acheteurs vs non-acheteurs',     desc: "Appee segmente automatiquement ta base selon le comportement pendant le lancement : acheteurs (séquence fidélisation), intéressés non-acheteurs (nurturing long terme), inactifs (nettoyage ou réactivation). Base propre = prochains lancements plus efficaces.", appee: true },
        ],
      },
    ],
  },
];

// ─── Strategy persistence helpers (localStorage fallback si API indisponible) ──
const LS_KEY = 'appee_strategies_v2';
const lsLoad  = ()  => { try { return JSON.parse(localStorage.getItem(LS_KEY)) || []; } catch { return []; } };
const lsSave  = arr => { try { localStorage.setItem(LS_KEY, JSON.stringify(arr)); } catch {} };

// ─── Word / HTML export ───────────────────────────────────────────────────────
function exportToWord(strat, tasks, notes, progress) {
  const dateStr = format(new Date(), 'd MMMM yyyy', { locale: fr });
  const html = `<!DOCTYPE html>
<html><head><meta charset="UTF-8">
<style>
body{font-family:Arial,sans-serif;font-size:11pt;color:#1F2937;margin:40px 50px;line-height:1.6}
h1{color:#5A35FF;font-size:18pt;border-bottom:3px solid #5A35FF;padding-bottom:8px;margin-bottom:4px}
h2{color:#370c77;font-size:13pt;margin:28px 0 10px;border-left:4px solid #5A35FF;padding-left:10px}
h3{font-size:11pt;color:#374151;margin:16px 0 6px}
.meta{font-size:10pt;color:#6B7280;margin-bottom:20px}
.progress-wrap{background:#E5E7EB;height:10px;border-radius:5px;margin:8px 0 20px}
.progress-fill{background:#5A35FF;height:10px;border-radius:5px;width:${progress}%}
table{width:100%;border-collapse:collapse;margin:12px 0;font-size:10pt}
th{background:#EDE9FF;color:#5A35FF;padding:8px 12px;text-align:left;border:1px solid #DDD6FE}
td{padding:8px 12px;border:1px solid #E5E7EB;vertical-align:top}
tr:nth-child(even) td{background:#FAFAFA}
.done{color:#059669;font-weight:bold}
.pending{color:#9CA3AF}
.note-item{background:#F9FAFB;border-left:3px solid #5A35FF;padding:8px 12px;margin:8px 0;font-size:10pt}
.note-date{font-size:9pt;color:#6B7280;font-weight:bold}
.kpi-item{padding:4px 0;font-size:10pt;color:#374151}
.footer{color:#9CA3AF;font-size:9pt;margin-top:40px;border-top:1px solid #E5E7EB;padding-top:12px;text-align:center}
.chip{display:inline-block;background:#EDE9FF;color:#5A35FF;border-radius:4px;padding:2px 8px;font-size:9pt;margin-right:4px}
</style></head><body>
<h1>${strat.icon} ${strat.title}</h1>
<div class="meta">Rapport généré le ${dateStr} &nbsp;·&nbsp; Durée : ${strat.duration} &nbsp;·&nbsp; ROI estimé : ${strat.roi}</div>
<strong>Avancement global : ${progress}%</strong>
<div class="progress-wrap"><div class="progress-fill"></div></div>

<h2>📊 Progression par phase</h2>
<table>
<tr><th>Phase</th><th>Période</th><th>Avancement</th><th>Actions</th></tr>
${strat.phases.map((ph, i) => {
  const done = ph.actions.filter(a => tasks[a.id]).length;
  const tot = ph.actions.length;
  const pct = tot > 0 ? Math.round(done / tot * 100) : 0;
  const status = pct === 100 ? '<span class="done">✅ Terminée</span>' : i === (strat.currentPhase || 0) ? '<span style="color:#F59E0B">▶ En cours</span>' : '<span class="pending">○ À venir</span>';
  return `<tr><td><strong>${ph.name}</strong></td><td>${ph.week}</td><td>${pct}% (${done}/${tot})</td><td>${status}</td></tr>`;
}).join('')}
</table>

<h2>✅ Détail des actions</h2>
${strat.phases.map(ph => `
<h3>${ph.name} — ${ph.week}</h3>
<table>
<tr><th>Canal</th><th>Action</th><th>Statut</th></tr>
${ph.actions.map(a => {
  const ch = CH[a.ch] || {};
  const done = tasks[a.id];
  return `<tr><td><span class="chip">${ch.icon || ''} ${ch.label || a.ch}</span></td><td>${a.title}<br/><span style="font-size:9pt;color:#6B7280">${a.desc}</span></td><td>${done ? '<span class="done">✅ Fait</span>' : '<span class="pending">○ À faire</span>'}</td></tr>`;
}).join('')}
</table>`).join('')}

<h2>📝 Notes par étape</h2>
${strat.phases.map(ph => {
  const actionNotesInPhase = ph.actions.filter(a => (a.notes || []).length > 0);
  if (actionNotesInPhase.length === 0) return '';
  return `<h3>${ph.name}</h3>` + actionNotesInPhase.map(a =>
    `<div style="margin-bottom:8px"><strong>${a.title}</strong>` +
    (a.notes || []).map(n => `<div class="note-item"><div class="note-date">${format(new Date(n.date), "d MMM yyyy 'à' HH:mm", { locale: fr })}</div>${n.text}</div>`).join('') +
    `</div>`
  ).join('');
}).join('') || '<p style="color:#9CA3AF;font-style:italic">Aucune note par étape.</p>'}

<h2>📅 Timeline</h2>
${(() => {
  const events = [];
  strat.phases.forEach(ph => {
    ph.actions.forEach(a => {
      if (tasks[a.id]) events.push({ type: 'done', label: `✅ ${a.title} (${ph.name})`, date: a.doneAt || strat.launchedAt });
      (a.notes || []).forEach(n => events.push({ type: 'note', label: `📝 [${a.title}] ${n.text}`, date: n.date }));
    });
  });
  notes.forEach(n => events.push({ type: 'note', label: `${n.auto ? '🤖' : '💬'} ${n.text}`, date: n.date }));
  const sorted = events.sort((a, b) => new Date(b.date) - new Date(a.date));
  if (sorted.length === 0) return '<p style="color:#9CA3AF;font-style:italic">Aucun événement.</p>';
  return sorted.map(ev => `<div class="note-item"><div class="note-date">${format(new Date(ev.date), "d MMM yyyy 'à' HH:mm", { locale: fr })}</div>${ev.label}</div>`).join('');
})()}

<h2>📝 Notes de suivi</h2>
${notes.length > 0
  ? notes.map(n => `<div class="note-item"><div class="note-date">${format(new Date(n.date), "d MMM yyyy 'à' HH:mm", { locale: fr })}</div>${n.text}</div>`).join('')
  : '<p style="color:#9CA3AF;font-style:italic">Aucune note ajoutée.</p>'}

<h2>📈 Indicateurs clés à suivre</h2>
${strat.kpis.map(k => `<div class="kpi-item">• ${k}</div>`).join('')}

<div class="footer">Rapport généré automatiquement par Appee · appee.io · ${dateStr}</div>
</body></html>`;

  const blob = new Blob(['﻿', html], { type: 'application/msword' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = `rapport-${strat.id}-${format(new Date(), 'yyyy-MM-dd')}.doc`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ─── CompanyProfileWarning — alerte si profil incomplet ──────────────────────
function CompanyProfileWarning({ cp, onFix }) {
  const missing = [];
  if (!cp?.sector)      missing.push('secteur d\'activité');
  if (!cp?.description) missing.push('description');
  if (!cp?.founded)     missing.push('date de création');
  if (missing.length === 0) return null;
  return (
    <div style={{
      background: '#FFF7ED', border: '1.5px solid #FED7AA',
      borderRadius: 12, padding: '12px 18px', marginBottom: 20,
      display: 'flex', alignItems: 'center', gap: 12,
    }}>
      <span style={{ fontSize: 20, flexShrink: 0 }}>⚠️</span>
      <div style={{ flex: 1, fontSize: 13, color: '#92400E', lineHeight: 1.5 }}>
        <strong>Profil entreprise incomplet</strong> — Plus Appee en sait sur toi, mieux il personnalise ta stratégie.{' '}
        Manquant : <em>{missing.join(', ')}</em>
      </div>
      <button
        onClick={onFix}
        style={{
          background: '#F97316', color: '#fff', border: 'none',
          borderRadius: 8, padding: '7px 14px', cursor: 'pointer',
          fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0,
        }}
        onMouseEnter={e => e.currentTarget.style.background = '#EA6C0A'}
        onMouseLeave={e => e.currentTarget.style.background = '#F97316'}
      >
        Compléter mon profil →
      </button>
    </div>
  );
}

// ─── Matching local : mots-clés par stratégie (zéro token IA) ────────────────
const STRAT_KEYWORDS = {
  'vip':               ['vip', 'fidel', 'fidèl', 'ambassadeur', 'loyal', 'meilleur client', 'premium', 'privilege', 'privilège', 'fidelisation', 'fidélisation'],
  'reac':              ['dormant', 'inactif', 'reactiv', 'réactiv', 'relance', 'disparu', 'perdu', 'reveill', 'réveill', 'retour client'],
  'bday':              ['anniversaire', 'anniv', 'fete', 'fête', 'birthday'],
  'onb':               ['bienvenu', 'onboard', 'nouveau client', 'accueil', 'premier achat', 'integration', 'intégration'],
  'sais':              ['saisonnier', 'noel', 'noël', 'ete', 'été', 'rentree', 'rentrée', 'solde', 'promo', 'saint-valentin', 'halloween', 'paques', 'pâques', 'black friday'],
  'digital360':        ['digital', 'reseau', 'réseau', 'instagram', 'facebook', 'tiktok', 'linkedin', 'seo', 'visibilite', 'visibilité', 'presence en ligne', 'présence en ligne', 'web', 'site internet', 'google'],
  'acquisition':       ['acquisition', 'prospecti', 'attirer', 'lead', 'croissance', 'nouveau client', 'gagner des clients', 'trouver des clients'],
  'enr':               ['enrichiss', 'donnee', 'donnée', 'base', 'completer', 'compléter', 'email manquant', 'data', 'qualite base', 'qualité base'],
  'plan-comm':         ['plan', 'communication', 'comm', 'annuel', 'calendrier editorial', 'calendrier éditorial', 'plan marketing'],
  'evenement-prive':   ['evenement prive', 'événement privé', 'soiree', 'soirée', 'invitation', 'vip event', 'prive', 'privé', 'inauguration'],
  'evenement-public':  ['evenement public', 'événement public', 'salon', 'expo', 'conference', 'conférence', 'foire', 'ouverture', 'portes ouvertes', 'concours', 'jeu concours', 'tirage au sort', 'fete', 'fête'],
  'lancement-produit': ['lancement', 'lancer', 'me lancer', 'nouveau produit', 'nouvelle collection', 'nouveau service', 'sortie', 'mise en vente', 'demarrer', 'démarrer', 'demarrage', 'démarrage', 'ma boite', 'ma boîte', 'mon entreprise', 'nouvelle entreprise', 'nouvelle activite', 'nouvelle activité', 'creation entreprise', 'création', 'creer mon', 'créer mon', 'ouvrir', 'ouverture', 'startup', 'start-up', 'monter ma', 'monter mon'],
};

// Normalise texte (minuscules, sans accents)
function norm(s) {
  return (s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
}

// Retourne {id, score} du meilleur match local, ou null
function localMatch(goal) {
  const g = norm(goal);
  let best = null;
  let bestScore = 0;
  for (const [id, kws] of Object.entries(STRAT_KEYWORDS)) {
    const score = kws.reduce((s, kw) => s + (g.includes(norm(kw)) ? kw.split(' ').length : 0), 0);
    if (score > bestScore) { bestScore = score; best = id; }
  }
  return bestScore > 0 ? { id: best, score: bestScore } : null;
}

// ─── AIStrategyInput — l'utilisateur décrit son objectif, l'IA propose ────────
function AIStrategyInput({ cp, contacts, onWizard, onSelectStrat }) {
  const [goal, setGoal]             = useState('');
  const [loading, setLoading]       = useState(false);
  const [suggestion, setSuggestion] = useState(null);
  const [matchedStrat, setMatchedStrat] = useState(null);
  const [wizardLoading, setWizardLoading] = useState(false);

  const handleSuggest = async () => {
    if (!goal.trim() || loading) return;
    setLoading(true);
    setSuggestion(null);
    setMatchedStrat(null);
    try {
      // ── Étape 1 : matching local (zéro token) ──
      const local = localMatch(goal);
      const localStrat = local ? STRATS.find(s => s.id === local.id) : null;

      if (localStrat && local.score >= 1) {
        // Match trouvé localement — utilise adviceIntro de la stratégie
        const cpName = cp?.name ? ` pour ${cp.name}` : '';
        const reply = `${localStrat.adviceIntro || localStrat.tagline} C'est exactement ce qu'il te faut${cpName} pour cet objectif. Lance la stratégie ci-dessous et je m'occupe de tout structurer pour toi.`;
        setSuggestion(reply);
        setMatchedStrat(localStrat);
        return;
      }

      // ── Étape 2 : matching IA léger (si pas de match local) ──
      // On demande UNIQUEMENT l'ID — pas de longue explication
      const stratIds = STRATS.map(s => `${s.id}: ${s.title}`).join(', ');
      const cpCtx = [
        cp?.sector      && `Secteur : ${cp.sector}`,
        cp?.description && cp.description,
        contacts?.length && `${contacts.length} contacts`,
      ].filter(Boolean).join(', ');

      const message = `[Contexte]\nObjectif client : "${goal}"${cpCtx ? `\nEntreprise : ${cpCtx}` : ''}\n\nMa question : Parmi ces stratégies Appee (${stratIds}), laquelle correspond le mieux à cet objectif ? Réponds avec uniquement l'ID exact (ex: "vip") ou "aucune" si vraiment rien ne correspond.`;

      const res = await api.chatMessage(message, []);
      const rawId = (res?.reply || '').trim().toLowerCase().replace(/[^a-z0-9-]/g, '').split('\n')[0];
      const aiStrat = STRATS.find(s => s.id === rawId);

      if (aiStrat) {
        const cpName = cp?.name ? ` pour ${cp.name}` : '';
        const reply = `${aiStrat.adviceIntro || aiStrat.tagline} Cette stratégie est parfaitement adaptée à ton objectif${cpName}.`;
        setSuggestion(reply);
        setMatchedStrat(aiStrat);
      } else {
        // ── Étape 3 : aucun match → wizard sur mesure ──
        setSuggestion(`Ton objectif est spécifique — aucune stratégie prête ne correspond exactement. Je vais te construire un plan sur mesure étape par étape, en fonction de ton activité et tes clients.`);
        setMatchedStrat(null);
      }
    } catch {
      setSuggestion('Je n\'ai pas pu analyser ton objectif. Réessaie dans un instant !');
    } finally {
      setLoading(false);
    }
  };

  const handleWizard = async () => {
    if (onWizard) { onWizard(goal); return; }
    setWizardLoading(true);
    try {
      await api.generateWizardStrategy(goal);
    } catch {}
    finally { setWizardLoading(false); }
  };

  return (
    <div style={{
      background: 'linear-gradient(135deg, #F8F7FF 0%, #F0EEFF 100%)',
      border: '1.5px solid #C4B5FD',
      borderRadius: 16, padding: 20, marginBottom: 24,
    }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 14 }}>
        <span style={{ fontSize: 24 }}>🎯</span>
        <div>
          <div style={{ fontWeight: 700, fontSize: 15, color: '#1F2937' }}>
            Dis-moi ton objectif — je te propose la stratégie idéale
          </div>
          <div style={{ fontSize: 12, color: '#6B7280', marginTop: 3 }}>
            Ex : "Fidéliser mes VIP", "Réactiver mes clients dormants", "Gagner en visibilité sur les réseaux"
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <input
          value={goal}
          onChange={e => setGoal(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSuggest()}
          placeholder="Quel est ton objectif principal ?"
          style={{
            flex: 1, padding: '10px 14px',
            border: '1.5px solid #C4B5FD', borderRadius: 10,
            fontSize: 14, outline: 'none', fontFamily: 'inherit',
            background: '#fff', color: '#1F2937',
          }}
          onFocus={e => e.target.style.borderColor = '#5A35FF'}
          onBlur={e => e.target.style.borderColor = '#C4B5FD'}
        />
        <button
          onClick={handleSuggest}
          disabled={!goal.trim() || loading}
          style={{
            background: goal.trim() && !loading
              ? 'linear-gradient(135deg, #5A35FF, #7C3AED)'
              : '#E5E7EB',
            color: goal.trim() && !loading ? '#fff' : '#9CA3AF',
            border: 'none', borderRadius: 10,
            padding: '10px 18px',
            cursor: goal.trim() && !loading ? 'pointer' : 'not-allowed',
            fontWeight: 600, fontSize: 14, whiteSpace: 'nowrap',
            transition: 'all 0.15s',
          }}
        >
          {loading ? '⏳ Analyse…' : '✨ Analyser'}
        </button>
      </div>
      {suggestion && (
        <div style={{
          marginTop: 14, background: '#fff',
          border: '1.5px solid #C4B5FD', borderRadius: 12,
          padding: '14px 16px',
        }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <img src={mascotChatImg} alt="Appee" style={{ width: 36, height: 36, flexShrink: 0, objectFit: 'contain' }} />
            <div style={{ flex: 1 }}>
              <p style={{ margin: '0 0 14px 0', fontSize: 13, color: '#1F2937', lineHeight: 1.65, whiteSpace: 'pre-wrap' }}>
                {suggestion}
              </p>
              {matchedStrat ? (
                <button
                  onClick={() => onSelectStrat && onSelectStrat(matchedStrat)}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 8,
                    background: 'linear-gradient(135deg, #5A35FF, #7C3AED)',
                    color: '#fff', border: 'none', borderRadius: 10,
                    padding: '10px 18px', cursor: 'pointer',
                    fontWeight: 600, fontSize: 14, transition: 'all 0.15s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.opacity = '0.9'}
                  onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                >
                  <span style={{ fontSize: 16 }}>{matchedStrat.icon}</span>
                  Lancer « {matchedStrat.title} » →
                </button>
              ) : (
                <button
                  onClick={handleWizard}
                  disabled={wizardLoading}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 8,
                    background: wizardLoading ? '#E5E7EB' : 'linear-gradient(135deg, #EC4899, #8B5CF6)',
                    color: wizardLoading ? '#9CA3AF' : '#fff',
                    border: 'none', borderRadius: 10,
                    padding: '10px 18px',
                    cursor: wizardLoading ? 'not-allowed' : 'pointer',
                    fontWeight: 600, fontSize: 14, transition: 'all 0.15s',
                  }}
                  onMouseEnter={e => { if (!wizardLoading) e.currentTarget.style.opacity = '0.9'; }}
                  onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                >
                  {wizardLoading ? '⏳ Génération…' : '✨ Créer une stratégie sur mesure'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── StrategyList — liste des stratégies actives ──────────────────────────────
function StrategyList({ strategies, onOpen, onNew, onDelete, contacts, onReco, companyProfile, onFixProfile, onWizard, onUpdate }) {
  const [reportStrat, setReportStrat] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [mapStrat, setMapStrat] = useState(null);
  const [stratInsights, setStratInsights]   = useState({});
  const [insightLoading, setInsightLoading] = useState({});
  const stratToDelete = strategies.find(s => s.instanceId === confirmDeleteId);

  // Génère un conseil IA contextuel à chaque ouverture du rapport
  useEffect(() => {
    if (!reportStrat) return;
    const key = reportStrat.instanceId;
    if (stratInsights[key]) return; // déjà chargé

    const total       = reportStrat.phases.reduce((s, p) => s + p.actions.length, 0);
    const done        = reportStrat.phases.reduce((s, p) => s + p.actions.filter(a => a.done).length, 0);
    const pct         = total > 0 ? Math.round(done / total * 100) : 0;
    const launchedDays = Math.floor((Date.now() - new Date(reportStrat.launchedAt)) / 86400000);
    const d = reportStrat.duration || '';
    const n = parseInt(d);
    const totalDays = d.includes('12 mois') ? 365 : d.includes('mois') ? n * 30 : d.includes('semaine') ? n * 7 : 90;
    const expectedPct  = Math.min(100, Math.round(launchedDays / Math.max(totalDays, 1) * 100));

    const currentPhase = reportStrat.phases[reportStrat.currentPhase || 0];
    const pendingActions = currentPhase
      ? currentPhase.actions.filter(a => !a.done).slice(0, 3).map(a => a.title)
      : [];

    const avancementStatus = pct === 0 && expectedPct === 0
      ? 'Stratégie venant d\'être lancée, aucune action encore réalisée.'
      : pct < expectedPct
        ? `En retard de ${expectedPct - pct} points par rapport au planning prévu.`
        : pct > expectedPct
          ? `En avance de ${pct - expectedPct} points sur le planning.`
          : 'Dans les temps, avancement conforme au planning.';

    const prompt = `Stratégie "${reportStrat.title}" lancée il y a ${launchedDays} jour${launchedDays > 1 ? 's' : ''} (durée totale : ${reportStrat.duration}).
Avancement réel : ${pct}% (${done}/${total} actions terminées) — attendu à ce stade : ${expectedPct}%.
Situation : ${avancementStatus}
Phase en cours : ${currentPhase?.name || 'démarrage'}.
Prochaines actions à faire : ${pendingActions.length > 0 ? pendingActions.join(' / ') : 'aucune action en attente'}.

En tant qu'expert marketing, donne UN conseil pratique et actionnable en 2 phrases max. Sois direct, concret, motivant. Utilise le tutoiement. Adapte ton message à la situation réelle (ne dis pas bravo si rien n'a été fait).`;

    setInsightLoading(prev => ({ ...prev, [key]: true }));
    api.chatMessage(prompt, [])
      .then(res => setStratInsights(prev => ({ ...prev, [key]: res?.reply || '' })))
      .catch(() => {})
      .finally(() => setInsightLoading(prev => ({ ...prev, [key]: false })));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportStrat]);
  return (
    <div className="strat-list-page">
      <CompanyProfileWarning cp={companyProfile} onFix={onFixProfile} />
      <SmartReco contacts={contacts} onLaunch={(strat) => onReco(strat)} />
      <div className="strat-list-toolbar">
        <div className="strat-list-title">
          <span className="strat-list-title-dot" />
          Mes stratégies en cours
          {strategies.length > 0 && <span className="strat-list-count">{strategies.length}</span>}
        </div>
        <div className="strat-toolbar-btns">
          <button className="strat-wizard-btn" onClick={onWizard}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
            Me prendre par la main
          </button>
          <button className="strat-new-btn" onClick={onNew}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            Nouvelle stratégie
          </button>
        </div>
      </div>

      {strategies.length === 0 ? (
        <div className="strat-empty-teaser">
          {/* Header */}
          <div className="strat-empty-head">
            <div className="strat-empty-badge">✨ Nouveau</div>
            <h3 className="strat-empty-title">Lance ta première stratégie marketing</h3>
            <p className="strat-empty-sub">
              Appee analyse tes contacts, ton secteur et tes objectifs — puis te crée un plan d'actions semaine par semaine, avec les messages déjà rédigés.
            </p>
          </div>

          {/* Preview de 3 stratégies */}
          <div className="strat-empty-preview">
            {STRATS.slice(0, 3).map(s => (
              <div key={s.id} className="strat-empty-card" style={{ '--sc': s.color, '--sb': s.bg }}>
                <div className="strat-empty-card-icon">{s.icon}</div>
                <div className="strat-empty-card-title">{s.title}</div>
                <div className="strat-empty-card-tagline">{s.tagline}</div>
                <div className="strat-empty-card-meta">
                  <span>⏱ {s.duration}</span>
                  <span style={{ color: '#10B981', fontWeight: 700 }}>📈 {s.roi}</span>
                </div>
                {/* Mini progress bar vide pour montrer l'interface */}
                <div style={{ height: 4, background: 'rgba(0,0,0,.07)', borderRadius: 10, marginTop: 10 }}>
                  <div style={{ width: '0%', height: '100%', background: s.color, borderRadius: 10 }} />
                </div>
              </div>
            ))}
          </div>

          {/* Promesses */}
          <div className="strat-empty-perks">
            {[
              { icon: '🤖', text: 'Appee rédige tous les messages à ta place' },
              { icon: '📅', text: 'Plan d\'actions clair semaine par semaine' },
              { icon: '📈', text: 'Suivi de ta progression en temps réel' },
              { icon: '⚡', text: 'Lancé en moins de 2 minutes' },
            ].map(p => (
              <div key={p.icon} className="strat-empty-perk">
                <span>{p.icon}</span>
                <span>{p.text}</span>
              </div>
            ))}
          </div>

          <button className="strat-launch-btn strat-launch-btn-big" onClick={onNew}>
            🚀 Choisir ma stratégie →
          </button>
        </div>
      ) : (
        <div className="strat-list-grid">
          {/* Alerte doublons */}
          {(() => {
            const idCounts = {};
            strategies.forEach(s => { idCounts[s.id] = (idCounts[s.id] || 0) + 1; });
            const hasDuplicates = Object.values(idCounts).some(c => c > 1);
            return hasDuplicates ? (
              <div className="slc-duplicate-alert">
                ⚠️ Tu as plusieurs fois la même stratégie en cours. Supprime les doublons pour garder un suivi clair.
              </div>
            ) : null;
          })()}
          {strategies.map((s, idx) => {
            const totalT = s.phases.reduce((sum, p) => sum + p.actions.length, 0);
            const doneT  = s.phases.reduce((sum, p) => sum + p.actions.filter(a => a.done).length, 0);
            const pct    = totalT > 0 ? Math.round(doneT / totalT * 100) : 0;
            const def    = STRATS.find(x => x.id === s.id) || {};
            return (
              <div key={s.instanceId} className="strat-list-card strat-card-animate" style={{ '--sc': def.color || '#5A35FF', animationDelay: `${idx * 60}ms` }}>

                {/* ── Header ── */}
                <div className="slc-header">
                  <div className="slc-icon">{s.icon}</div>
                  <div className="slc-info">
                    <div className="slc-name">{s.title}</div>
                    {s.source === 'audit' && (
                      <span className="slc-badge-audit">📊 Stratégie audit</span>
                    )}
                    {s.subtitle && !s.source && (
                      <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>{s.subtitle}</div>
                    )}
                    <div className="slc-meta">
                      Lancée le {format(new Date(s.launchedAt), 'd MMM yyyy', { locale: fr })} · {s.duration}
                    </div>
                  </div>
                  <div className={`slc-pct-badge${pct === 100 ? ' done' : ''}`}>{pct}%</div>
                </div>

                {/* ── Progress bar ── */}
                <div className="slc-progress-bar">
                  <div className="slc-progress-fill" style={{ width: `${pct}%` }} />
                </div>

                {/* ── Brief objectif ── */}
                {s.briefing?.objective ? (
                  <div className="slc-brief-row">
                    <span className="slc-brief-icon">🎯</span>
                    <span className="slc-brief-text">{s.briefing.objective.length > 80 ? s.briefing.objective.slice(0, 80) + '…' : s.briefing.objective}</span>
                  </div>
                ) : (
                  <div className="slc-brief-empty" onClick={() => onOpen(s)} title="Définir le brief de stratégie">
                    <span>📋</span> Brief stratégique à définir — <span className="slc-brief-cta">Compléter →</span>
                  </div>
                )}

                {/* ── Phases + actions détaillées ── */}
                <div className="slc-phases-detail">
                  {s.phases.map((ph, pi) => {
                    const phaseDone  = ph.actions.filter(a => a.done).length;
                    const phaseTotal = ph.actions.length;
                    const isActive   = pi === (s.currentPhase || 0);
                    const isDone     = phaseTotal > 0 && phaseDone === phaseTotal;
                    const isNext     = pi > (s.currentPhase || 0);
                    return (
                      <div key={ph.id} className={`slc-phase-block${isActive ? ' slc-phase-active' : ''}${isDone ? ' slc-phase-done' : ''}${isNext ? ' slc-phase-future' : ''}`}>
                        {/* Phase header */}
                        <div className="slc-phase-hd">
                          <span className="slc-phase-status">
                            {isDone ? '✅' : isActive ? '▶' : '○'}
                          </span>
                          <span className="slc-phase-nm">{ph.name}</span>
                          {ph.week && <span className="slc-phase-wk">{ph.week}</span>}
                          <span className="slc-phase-cnt">{phaseDone}/{phaseTotal}</span>
                        </div>
                        {/* Mini progress bar per phase */}
                        <div className="slc-phase-mini-bar">
                          <div className="slc-phase-mini-fill" style={{ width: `${phaseTotal > 0 ? Math.round(phaseDone/phaseTotal*100) : 0}%` }} />
                        </div>
                        {/* Actions — afficher pour phase active et phases terminées */}
                        {(isActive || isDone) && (() => {
                          const nextAction = isActive ? ph.actions.find(a => !a.done) : null;
                          return (
                            <div className="slc-action-list">
                              {ph.actions.map(action => {
                                const ch = CH[action.ch] || CH.email;
                                const isNext = nextAction && action.id === nextAction.id;
                                return (
                                  <div key={action.id} className={`slc-action-row${action.done ? ' slc-ar-done' : ''}${action.optional ? ' slc-ar-opt' : ''}${isNext ? ' slc-ar-next' : ''}`}>
                                    <span className="slc-ar-check">{action.done ? '✓' : isNext ? '▶' : '·'}</span>
                                    <span className="slc-ar-ch" style={{ background: action.done ? '#D1FAE5' : ch.bg, color: action.done ? '#059669' : ch.color }}>
                                      {ch.icon}
                                    </span>
                                    <span className="slc-ar-title">{action.title}</span>
                                    {action.appee && <span className="slc-ar-appee" title="Appee automatise">🤖</span>}
                                    {isNext && <span className="slc-ar-next-badge">À faire</span>}
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })()}
                        {/* Phases futures : juste un résumé */}
                        {isNext && (
                          <div className="slc-phase-preview">
                            {ph.actions.slice(0, 3).map(a => {
                              const ch = CH[a.ch] || CH.email;
                              return <span key={a.id} className="slc-phase-preview-chip" style={{ background: ch.bg, color: ch.color }}>{ch.icon}</span>;
                            })}
                            {ph.actions.length > 3 && <span className="slc-phase-preview-more">+{ph.actions.length - 3}</span>}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* ── Next-action spotlight ── */}
                {(() => {
                  if (pct === 100) return (
                    <div className="slc-next-done">
                      <span>🏆</span>
                      <span>Stratégie terminée — félicitations !</span>
                    </div>
                  );
                  // Find first undone action
                  let nextAct = null, nextPh = null;
                  for (const ph of s.phases) {
                    const a = ph.actions.find(x => !x.done);
                    if (a) { nextAct = a; nextPh = ph; break; }
                  }
                  if (!nextAct) return null;
                  const ch = CH[nextAct.ch] || CH.email;
                  return (
                    <div className="slc-next-spotlight" style={{ '--sc': def.color || '#5A35FF' }} onClick={() => onOpen(s)}>
                      <div className="slc-ns-left">
                        <div className="slc-ns-badge">Prochaine action</div>
                        <div className="slc-ns-title">{nextAct.title}</div>
                        <div className="slc-ns-meta">
                          <span className="slc-ns-ch" style={{ background: ch.bg, color: ch.color }}>{ch.icon} {ch.label}</span>
                          {nextPh && <span className="slc-ns-phase">{nextPh.name}</span>}
                          {nextAct.appee && <span className="slc-ns-bot">🤖 Appee aide</span>}
                        </div>
                      </div>
                      <div className="slc-ns-arrow">→</div>
                    </div>
                  );
                })()}

                {/* ── Boutons ── */}
                <div className="slc-actions">
                  <button className="slc-open-btn" onClick={() => onOpen(s)}>Continuer →</button>
                  <button className="slc-report-btn" onClick={e => { e.stopPropagation(); setMapStrat(s); }} title="Mind map">🗺</button>
                  <button className="slc-report-btn" onClick={e => { e.stopPropagation(); setReportStrat(s); }} title="Rapport">📊</button>
                  <button className="slc-del-btn" onClick={() => setConfirmDeleteId(s.instanceId)} title="Archiver">🗑</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {reportStrat && (
        <div className="slr-overlay" onClick={e => { if (e.target === e.currentTarget) setReportStrat(null); }}>
          <div className="slr-modal">
            <div className="slr-header">
              <span className="slr-icon">{reportStrat.icon}</span>
              <div>
                <div className="slr-name">{reportStrat.title}</div>
                <div className="slr-meta">Lancée le {format(new Date(reportStrat.launchedAt), 'd MMMM yyyy', { locale: fr })} · {reportStrat.duration}</div>
              </div>
              <button className="slr-close" onClick={() => setReportStrat(null)}>✕</button>
            </div>
            {(() => {
              const total = reportStrat.phases.reduce((s, p) => s + p.actions.length, 0);
              const done  = reportStrat.phases.reduce((s, p) => s + p.actions.filter(a => a.done).length, 0);
              const pct   = total > 0 ? Math.round(done / total * 100) : 0;
              const launchedDays = Math.floor((Date.now() - new Date(reportStrat.launchedAt)) / 86400000);
              const totalDays = (() => { const d = reportStrat.duration || ''; const n = parseInt(d); if (d.includes('12 mois')) return 365; if (d.includes('mois')) return n * 30; if (d.includes('semaine')) return n * 7; return 90; })();
              const expectedPct = Math.min(100, Math.round(launchedDays / Math.max(totalDays, 1) * 100));
              const diff = pct - expectedPct;
              const justLaunched = pct === 0 && expectedPct === 0;
              const status = justLaunched ? 'start' : diff >= 0 ? 'ahead' : diff >= -12 ? 'ok' : 'behind';
              const statusMsg = status === 'start'
                ? { emoji: '🚀', label: 'C\'est parti !', color: '#5A35FF' }
                : status === 'ahead'
                ? { emoji: '⚡', label: `En avance · +${Math.abs(diff)} pts`, color: '#10B981' }
                : status === 'ok'
                ? { emoji: '✅', label: 'Dans les temps', color: '#2563EB' }
                : { emoji: '⚠️', label: `En retard · -${Math.abs(diff)} pts`, color: '#EF4444' };
              const aiText = stratInsights[reportStrat.instanceId];
              const aiLoading = insightLoading[reportStrat.instanceId];

              const allActionNotes = [];
              reportStrat.phases.forEach(ph => ph.actions.forEach(a => {
                (a.notes || []).forEach(n => allActionNotes.push({ action: a.title, phase: ph.name, ...n }));
              }));
              const stratNotes = reportStrat.notes || [];

              return (
                <div className="slr-body">
                  <div className="slr-status" style={{ borderColor: statusMsg.color, background: statusMsg.color + '15' }}>
                    <span className="slr-status-emoji">{statusMsg.emoji}</span>
                    <div style={{ flex: 1 }}>
                      <div className="slr-status-label" style={{ color: statusMsg.color }}>{statusMsg.label}</div>
                      <div className="slr-status-msg">
                        {aiLoading ? (
                          <span style={{ color: '#9CA3AF', fontStyle: 'italic', fontSize: 13 }}>
                            Appee analyse ta stratégie…&nbsp;
                            <span style={{ display: 'inline-flex', gap: 3, verticalAlign: 'middle' }}>
                              {[0,1,2].map(i => (
                                <span key={i} style={{
                                  width: 5, height: 5, borderRadius: '50%', background: '#9CA3AF', display: 'inline-block',
                                  animation: `appeeBounce 1.3s ease-in-out ${i * 0.18}s infinite`,
                                }} />
                              ))}
                            </span>
                          </span>
                        ) : aiText || (
                          status === 'start'
                            ? "Stratégie lancée ! Commence par les premières actions de la phase en cours — chaque étape validée te rapproche de ton objectif."
                            : status === 'ahead'
                              ? "Belle avance sur le planning ! Profites-en pour soigner la qualité des prochaines actions."
                              : status === 'ok'
                                ? "Tout roule, tu es dans les temps. Maintiens ce rythme jusqu'à la prochaine phase."
                                : "Tu as pris un peu de retard — concentre-toi sur les prochaines actions prioritaires cette semaine."
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="slr-progress-block">
                    <div className="slr-prog-row">
                      <span>Avancement réel</span>
                      <strong style={{ color: statusMsg.color }}>{pct}%</strong>
                    </div>
                    <div className="slr-prog-bar-track">
                      <div className="slr-prog-bar-fill" style={{ width: `${pct}%`, background: statusMsg.color }} />
                      <div className="slr-prog-expected" style={{ left: `${expectedPct}%` }} title={`Attendu : ${expectedPct}%`} />
                    </div>
                    <div className="slr-prog-labels">
                      <span>{done}/{total} actions terminées</span>
                      <span>Attendu : {expectedPct}%</span>
                    </div>
                  </div>

                  <div className="slr-phases">
                    {reportStrat.phases.map((ph, i) => {
                      const d = ph.actions.filter(a => a.done).length;
                      const t = ph.actions.length;
                      const p = t > 0 ? Math.round(d / t * 100) : 0;
                      return (
                        <div key={i} className="slr-phase-row">
                          <span className="slr-phase-icon">{p === 100 ? '✅' : i === (reportStrat.currentPhase || 0) ? '▶' : '○'}</span>
                          <span className="slr-phase-name">{ph.name}</span>
                          <div className="slr-phase-bar"><div style={{ width: `${p}%` }} /></div>
                          <span className="slr-phase-pct">{p}%</span>
                        </div>
                      );
                    })}
                  </div>

                  {allActionNotes.length > 0 && (
                    <div className="slr-section">
                      <div className="slr-section-title">📝 Notes par étape</div>
                      {allActionNotes.map((n, i) => (
                        <div key={i} className="slr-note-row">
                          <div className="slr-note-action">{n.action}</div>
                          <div className="slr-note-text">{n.text}</div>
                          <div className="slr-note-date">{format(new Date(n.date), "d MMM yyyy 'à' HH:mm", { locale: fr })}</div>
                        </div>
                      ))}
                    </div>
                  )}

                  {stratNotes.length > 0 && (
                    <div className="slr-section">
                      <div className="slr-section-title">💬 Notes de suivi</div>
                      {stratNotes.slice(0, 5).map((n, i) => (
                        <div key={i} className={`slr-note-row${n.auto ? ' slr-note-auto' : ''}`}>
                          <div className="slr-note-text">{n.auto && <span className="slr-auto-badge">🤖</span>}{n.text}</div>
                          <div className="slr-note-date">{format(new Date(n.date), "d MMM yyyy 'à' HH:mm", { locale: fr })}</div>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="slr-section">
                    <div className="slr-section-title">🎯 Prochaines actions recommandées</div>
                    {(() => {
                      const cur = reportStrat.phases[reportStrat.currentPhase || 0];
                      const next = (cur?.actions || []).filter(a => !a.done).slice(0, 3);
                      if (next.length === 0) return <div className="slr-all-done">🎉 Toutes les actions de cette phase sont terminées !</div>;
                      return next.map((a, i) => {
                        const ch = CH[a.ch] || {};
                        return (
                          <div key={i} className="slr-next-row">
                            <span className="slr-next-ch" style={{ background: ch.bg, color: ch.color }}>{ch.icon} {ch.label}</span>
                            <span className="slr-next-title">{a.title}</span>
                            {a.appee && <span className="slr-auto-badge">🤖 Auto</span>}
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}

      <ConfirmModal
        open={!!confirmDeleteId}
        title="Archiver cette stratégie ?"
        message={stratToDelete ? `"${stratToDelete.title}" sera archivée. Tu pourras en relancer une nouvelle à tout moment.` : ''}
        confirmLabel="Oui, archiver"
        danger
        onConfirm={() => { onDelete(confirmDeleteId); setConfirmDeleteId(null); }}
        onCancel={() => setConfirmDeleteId(null)}
      />

      {/* Vue Carte depuis la liste */}
      {mapStrat && (() => {
        const mapTasks = {};
        mapStrat.phases.forEach(ph => ph.actions.forEach(a => { mapTasks[a.id] = !!a.done; }));

        // Livrables depuis les données sauvegardées (posts + brouillons email par défaut)
        const mapDeliverables = {};
        mapStrat.phases.forEach(ph => ph.actions.forEach(action => {
          const items = [];
          if (action.ch === 'email') {
            const draft = getEmailDraft(action, mapStrat, companyProfile || {});
            if (draft?.body?.trim()) {
              items.push({ type: 'email', icon: '✉️', label: 'Brouillon email', subject: draft.subject || '', body: draft.body });
            }
          }
          (action.posts || []).forEach((post, i) => {
            items.push({ type: 'post', icon: '📱', label: `Post ${i + 1}${post.aiGenerated ? ' 🤖' : ''}`, text: post.text, visualPrompt: post.visualPrompt || '', image_url: post.image_url || '' });
          });
          if (items.length) mapDeliverables[action.id] = items;
        }));

        const handleMapToggle = (actionId) => {
          const updated = {
            ...mapStrat,
            phases: mapStrat.phases.map(ph => ({
              ...ph,
              actions: ph.actions.map(a => a.id === actionId ? { ...a, done: !a.done } : a),
            })),
          };
          setMapStrat(updated);
          if (onUpdate) onUpdate(updated);
        };
        return (
          <StrategyMindMap
            strat={mapStrat}
            tasks={mapTasks}
            deliverables={mapDeliverables}
            onToggle={handleMapToggle}
            onClose={() => setMapStrat(null)}
          />
        );
      })()}
    </div>
  );
}

// ─── StrategySelector — choix de la stratégie ────────────────────────────────
function StrategySelector({ contacts, onSelect, onBack, companyProfile, onFixProfile, onWizard }) {
  const [cat, setCat] = useState('all');

  const stats = useMemo(() => {
    const total = contacts.length;
    const hommes = contacts.filter(c => c.gender === 'monsieur').length;
    const femmes = contacts.filter(c => c.gender === 'madame').length;
    const withAge = contacts.filter(c => c.birth_date);
    const ages = withAge.map(c => contactAge(c.birth_date)).filter(a => a != null);
    const medianAge = ages.length > 0
      ? ages.sort((a, b) => a - b)[Math.floor(ages.length / 2)]
      : null;
    const cities = contacts.filter(c => c.city).reduce((acc, c) => {
      acc[c.city] = (acc[c.city] || 0) + 1; return acc;
    }, {});
    const topCity = Object.entries(cities).sort((a, b) => b[1] - a[1])[0]?.[0];
    const vip = contacts.filter(c => c.category === 'VIP' || (c.engagement_score || 0) > 75).length;
    return { total, hommes, femmes, medianAge, topCity, vip };
  }, [contacts]);

  const filtered = cat === 'all' ? STRATS : STRATS.filter(s => s.category === cat);

  return (
    <div className="strat-selector">
      {onBack && (
        <button className="strat-back" onClick={onBack} style={{ marginBottom: 12 }}>← Mes stratégies</button>
      )}

      <CompanyProfileWarning cp={companyProfile} onFix={onFixProfile} />
      <AIStrategyInput cp={companyProfile} contacts={contacts} onWizard={onWizard} onSelectStrat={onSelect} />

      {/* Compact banner */}
      <div className="strat-intro-compact">
        <span className="sic-avatar">🤖</span>
        <div className="sic-body">
          <strong>Appee 360°</strong>
          <span className="sic-sep">·</span>
          <span className="sic-chip">{stats.total} contacts</span>
          {stats.hommes > 0 && <span className="sic-chip">{Math.round(stats.hommes / stats.total * 100)}% hommes</span>}
          {stats.femmes > 0 && <span className="sic-chip">{Math.round(stats.femmes / stats.total * 100)}% femmes</span>}
          {stats.medianAge && <span className="sic-chip">Âge médian {stats.medianAge} ans</span>}
          {stats.topCity && <span className="sic-chip">📍 {stats.topCity}</span>}
          {stats.vip > 0 && <span className="sic-chip sic-chip-vip">⭐ {stats.vip} VIP</span>}
        </div>
        <div className="sic-sub">Quelle stratégie on lance ?</div>
      </div>

      {/* Category tabs */}
      <div className="strat-cat-tabs">
        {CATEGORIES.map(c => (
          <button
            key={c.id}
            className={`strat-cat-tab ${cat === c.id ? 'active' : ''}`}
            onClick={() => setCat(c.id)}
          >
            {c.icon} {c.label}
          </button>
        ))}
      </div>

      {/* Grid */}
      <div className="strat-grid">
        {filtered.map(s => {
          const count = s.contacts_fn(contacts);
          return (
            <button
              key={s.id}
              className="strat-card"
              style={{ '--sc': s.color, '--sc-bg': s.bg }}
              onClick={() => onSelect(s)}
            >
              <div className="strat-card-icon">{s.icon}</div>
              <div className="strat-card-title">{s.title}</div>
              <div className="strat-card-tagline">{s.tagline}</div>
              <div className="strat-card-pills">
                <span className="strat-pill" style={{ background: s.bg, color: s.color }}>⏱ {s.duration}</span>
                <span className="strat-pill" style={{ background: s.bg, color: s.color }}>📈 {s.roi}</span>
              </div>
              <ChannelChips phases={s.phases} max={5} />
              <div className="strat-card-count">
                {count > 0 ? `${count} contact${count > 1 ? 's' : ''} concerné${count > 1 ? 's' : ''}` : 'Tous tes contacts'}
              </div>
              <div className="strat-card-cta">Configurer et lancer →</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── StrategyConfigurator ─────────────────────────────────────────────────────
function StrategyConfigurator({ strat, contacts, onLaunch, onBack }) {
  // Duration mode for plans with options (6 or 12 months)
  const [durationMode, setDurationMode] = useState(
    strat.duration_options ? strat.duration_options[0] : null
  );

  // Filter phases by duration for plan-comm
  const visiblePhases = useMemo(() => {
    if (!strat.duration_options) return strat.phases;
    const months = durationMode === '12 mois' ? 12 : 6;
    return strat.phases.filter(ph => !ph.months || ph.months.includes(months));
  }, [strat, durationMode]);

  const initEnabled = useMemo(() => {
    const map = {};
    visiblePhases.forEach(ph => ph.actions.forEach(a => { map[a.id] = !a.optional; }));
    return map;
  }, [visiblePhases]);

  const [enabled, setEnabled]   = useState(initEnabled);
  const [expanded, setExpanded] = useState({});

  // Re-init enabled when duration changes
  useEffect(() => {
    const map = {};
    visiblePhases.forEach(ph => ph.actions.forEach(a => { map[a.id] = !a.optional; }));
    setEnabled(map);
  }, [visiblePhases]); // eslint-disable-line

  const totalActions = useMemo(() => Object.values(enabled).filter(Boolean).length, [enabled]);
  const appeeActions = useMemo(() => {
    let n = 0;
    visiblePhases.forEach(ph => ph.actions.forEach(a => { if (enabled[a.id] && a.appee) n++; }));
    return n;
  }, [enabled, visiblePhases]);
  const contactCount = useMemo(() => strat.contacts_fn(contacts), [strat, contacts]);

  const toggle    = id => setEnabled(p => ({ ...p, [id]: !p[id] }));
  const toggleAdv = id => setExpanded(p => ({ ...p, [id]: !p[id] }));

  const handleLaunch = () => {
    const duration = durationMode || strat.duration;
    const instance = {
      ...strat,
      instanceId:   `si_${Date.now()}`,
      launchedAt:   new Date().toISOString(),
      currentPhase: 0,
      notes:        [],
      duration,
      title: strat.duration_options ? `${strat.title} — ${duration}` : strat.title,
      phases: visiblePhases.map(ph => ({
        ...ph,
        actions: ph.actions.filter(a => !!enabled[a.id]).map(a => ({ ...a, done: false })),
      })).filter(ph => ph.actions.length > 0),
    };
    onLaunch(instance);
  };

  return (
    <div className="strat-configurator-v2">
      <Breadcrumb items={[
        { label: 'Stratégie', icon: '🧭', onClick: onBack },
        { label: strat?.name || strat?.title || 'Configuration' },
      ]} />
      {/* Compact header */}
      <div className="scv2-header">
        <button className="strat-back" onClick={onBack}>← Retour</button>
        <div className="scv2-title-block">
          <span className="scv2-icon">{strat.icon}</span>
          <div>
            <div className="scv2-name">{strat.title}</div>
            <div className="scv2-sub">{strat.tagline}</div>
            <ChannelChips phases={visiblePhases} max={6} />
          </div>
        </div>
      </div>

      {/* Appee advice bar */}
      <div className="scv2-advice-bar">
        <span className="scv2-advice-avatar">🤖</span>
        <span className="scv2-advice-text">{strat.adviceIntro}</span>
      </div>

      {/* Duration picker — only for plan-comm */}
      {strat.duration_options && (
        <div className="scv2-duration-picker">
          <span className="scv2-duration-label">Durée du plan :</span>
          {strat.duration_options.map(d => (
            <button
              key={d}
              className={`scv2-duration-btn ${durationMode === d ? 'active' : ''}`}
              onClick={() => setDurationMode(d)}
            >
              {d === '6 mois' ? '📋 6 mois' : '📅 12 mois'}
            </button>
          ))}
          <span className="scv2-duration-info">
            {durationMode === '12 mois'
              ? `${visiblePhases.length} mois · ${totalActions} actions`
              : `${visiblePhases.length} mois · ${totalActions} actions`}
          </span>
        </div>
      )}

      {/* Legend */}
      <div className="scv2-legend">
        <span className="legend-dot legend-auto" /><span>Géré par Appee</span>
        <span className="leg-sep">·</span>
        <span className="legend-dot legend-manual" /><span>À faire toi-même</span>
        <span className="leg-sep">·</span>
        <span className="legend-dot legend-optional" /><span>Optionnel (désactivé)</span>
      </div>

      {/* Phases — full width */}
      <div className="scv2-phases">
        {visiblePhases.map((ph, idx) => (
          <div key={ph.id} className="config-phase">
            <div className="config-phase-header">
              <div className="config-phase-num">{idx + 1}</div>
              <div className="config-phase-info">
                <div className="config-phase-name">{ph.name}</div>
                <div className="config-phase-week">{ph.week}</div>
              </div>
            </div>
            <div className="config-actions">
              {ph.actions.map(action => {
                const ch    = CH[action.ch] || {};
                const isOn  = !!enabled[action.id];
                const adv   = ADVICE[action.ch] || ADVICE.email;
                const isExp = !!expanded[action.id];
                return (
                  <div key={action.id} className={`config-action${isOn ? '' : ' off'}`}>
                    <div className="config-action-main">
                      <div className="config-action-left">
                        <span className="config-action-channel" style={{ background: ch.bg, color: ch.color }}>
                          {ch.icon} {ch.label}
                        </span>
                        <div className="config-action-content">
                          <div className="config-action-title">{action.title}</div>
                          <div className="config-action-desc">{action.desc}</div>
                        </div>
                      </div>
                      <div className="config-action-right">
                        {action.appee && <span className="config-appee-badge">🤖 Auto</span>}
                        {action.optional && <span className="config-opt-badge">Optionnel</span>}
                        <button className="config-adv-btn" onClick={() => toggleAdv(action.id)}>
                          {isExp ? '▲ Fermer' : '💡 Conseils'}
                        </button>
                        <button className={`config-toggle ${isOn ? 'on' : 'off'}`} onClick={() => toggle(action.id)}>
                          {isOn ? '✓ Inclus' : '+ Ajouter'}
                        </button>
                      </div>
                    </div>
                    {isExp && (
                      <div className="config-advice-panel">
                        <div className="cap-grid">
                          <div className="cap-section">
                            <div className="cap-section-title">🎯 Piliers de communication</div>
                            <div className="cap-pills">
                              {adv.piliers.map((p, i) => <span key={i} className="cap-pill">{p}</span>)}
                            </div>
                          </div>
                          <div className="cap-section">
                            <div className="cap-section-title">⏱ Timing & Fréquence</div>
                            <div className="cap-timing">{adv.timing}</div>
                          </div>
                        </div>
                        <div className="cap-section" style={{ marginTop: 14 }}>
                          <div className="cap-section-title">✅ Bonnes pratiques</div>
                          <div className="cap-tips-grid">
                            {adv.tips.map((t, i) => (
                              <div key={i} className="cap-tip-item">
                                <span className="cap-tip-dot">›</span>
                                <span>{t}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div className="cap-kpi-bar">
                          <span>📊</span>
                          <span>{adv.kpi}</span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Sticky footer with summary + launch */}
      <div className="scv2-footer">
        <div className="scv2-footer-summary">
          <div className="scv2-stat"><span className="scv2-stat-val">{totalActions}</span><span className="scv2-stat-lbl">actions</span></div>
          <div className="scv2-stat scv2-stat-auto"><span className="scv2-stat-val">{appeeActions}</span><span className="scv2-stat-lbl">🤖 auto</span></div>
          <div className="scv2-stat"><span className="scv2-stat-val">{totalActions - appeeActions}</span><span className="scv2-stat-lbl">manuelles</span></div>
          <div className="scv2-stat"><span className="scv2-stat-val">{contactCount}</span><span className="scv2-stat-lbl">contacts</span></div>
          <div className="scv2-kpis">
            {strat.kpis.slice(0, 2).map((k, i) => <span key={i} className="scv2-kpi-chip">· {k}</span>)}
          </div>
        </div>
        <button className="scv2-launch-btn" onClick={handleLaunch}>
          🚀 Lancer {durationMode ? `le plan ${durationMode}` : 'la stratégie'} — {totalActions} actions
        </button>
      </div>
    </div>
  );
}

// ─── Appee contextual analysis generator ─────────────────────────────────────
function getAppeeNote(action, contacts, cp = {}) {
  const total = contacts?.length || 0;
  const nomE   = cp.name    ? `${cp.name}` : 'ton entreprise';
  const secteurCtx = cp.sector ? ` dans le secteur ${cp.sector}` : '';
  const valuesCtx  = cp.values ? ` Valeurs clés : ${cp.values}.` : ''; // eslint-disable-line no-unused-vars
  const descCtx    = cp.description ? ` Positionnement : ${cp.description}` : ''; // eslint-disable-line no-unused-vars

  if (!total) return `🤖 J'analyse ta base dès que tes premiers contacts sont importés. Dès qu'elle est là, je pourrai adapter chaque message au profil de ${nomE}${secteurCtx}.`;

  const now = new Date();
  const sixAgo = new Date(now); sixAgo.setMonth(now.getMonth() - 6);
  const dormant  = contacts.filter(c => !c.last_interaction || new Date(c.last_interaction) < sixAgo);
  const vip      = contacts.filter(c => (c.quality_score || 0) > 80);
  const withEmail = contacts.filter(c => c.email);
  const withPhone = contacts.filter(c => c.phone);
  const men      = contacts.filter(c => c.gender === 'homme');
  const women    = contacts.filter(c => c.gender === 'femme');
  const cityMap  = {};
  contacts.forEach(c => { if (c.city) cityMap[c.city] = (cityMap[c.city] || 0) + 1; });
  const topCity  = Object.entries(cityMap).sort((a,b) => b[1]-a[1])[0];
  const ages     = contacts.filter(c => c.birth_date).map(c =>
    Math.floor((now - new Date(c.birth_date)) / (365.25 * 86400000))
  ).sort((a,b) => a-b);
  const medAge   = ages.length ? ages[Math.floor(ages.length / 2)] : null;

  const t  = action.title.toLowerCase();
  const ch = action.ch;

  if (t.includes('audit') && (t.includes('base') || t.includes('contact'))) {
    return `📊 Analyse de ta base (${total} contacts)\n\n` +
      `• ${withEmail.length} ont une adresse email valide (${Math.round(withEmail.length/total*100)}%)\n` +
      `• ${withPhone.length} ont un numéro de téléphone\n` +
      `• ${dormant.length} sont inactifs depuis +6 mois — priorité de réactivation\n` +
      `• ${vip.length} clients VIP (score qualité > 80)\n` +
      (medAge ? `• Âge médian : ${medAge} ans\n` : '') +
      (topCity ? `• Ville principale : ${topCity[0]} (${topCity[1]} contacts)\n` : '') +
      `• Genre : ${Math.round(men.length/total*100)}% hommes · ${Math.round(women.length/total*100)}% femmes\n\n` +
      `💡 Recommandation : ${dormant.length > 0 ? `commencer par réactiver les ${dormant.length} dormants avant tout nouvel envoi` : 'la base est saine — on peut lancer la première campagne immédiatement'}.`;
  }
  if (t.includes('audit') && t.includes('réseau')) {
    return `📲 Analyse de présence digitale\n\nTa cible principale (${Math.round(women.length/total*100) >= 50 ? 'majorité féminine' : 'majorité masculine'}, ${medAge ? `âge médian ${medAge} ans` : 'âge non renseigné'}) est prioritairement active sur ${medAge && medAge < 40 ? 'Instagram + TikTok' : 'Facebook + LinkedIn'}.\n\nConcurrents locaux actifs en moyenne 3x/semaine. Opportunité : différenciation via le contenu de coulisses et les témoignages clients.\n\n💡 Recommandation : démarrer avec 3 posts/semaine + stories quotidiennes sur ${medAge && medAge < 40 ? 'Instagram' : 'Facebook'}.`;
  }
  if (t.includes('charte') || t.includes('éditorial') || t.includes('ton de voix')) {
    return `✍️ Positionnement éditorial recommandé\n\nSur la base de ${total} contacts :\n• Ton : chaleureux et direct — tu tutoies (base locale et relationnelle)\n• Fréquence : 2-3 emails/mois + 1-2 SMS/mois\n• Piliers de contenu : ①  Coulisses & authenticité  ②  Offres exclusives  ③  Témoignages clients\n• Différenciation : proximité humaine vs. concurrents plus "corporate"\n\n💡 Je prépare le template de charte éditorial pour validation.`;
  }
  if (ch === 'email' && (t.includes('vip') || t.includes('invitation'))) {
    const sample = vip.slice(0, 3).map(c => `• ${c.first_name || '—'} ${c.last_name || ''} (score ${c.quality_score || '?'})`).join('\n');
    return `📧 Segment VIP identifié — ${vip.length} contacts sur ${total}\n\n${sample}${vip.length > 3 ? `\n• … et ${vip.length - 3} autres` : ''}\n\nTous disposent d'une adresse email valide. Message d'invitation personnalisé prêt à envoyer.\n\n💡 Objet recommandé : "Tu fais partie de notre cercle privilégié, ${'{prénom}'}"`;
  }
  if (ch === 'sms') {
    return `📱 Contacts joignables par SMS : ${withPhone.length}/${total} (${Math.round(withPhone.length/total*100)}%)\n\nCréneau optimal : mardi-jeudi · 10h–12h ou 17h–19h\nFormat : 160 caractères max (SMS simple, pas fragmenté)\nPersonnalisation : commencer par le prénom = +22% de clics\n\n💡 Message rédigé et prêt — je lance dès validation.`;
  }
  if (ch === 'email') {
    return `📧 Segment prêt : ${withEmail.length} contacts avec adresse valide\n\nTaux d'ouverture attendu : 35–55% (base relationnelle)\nMeilleur créneau : mardi-jeudi · 9h–11h\nPersonnalisation activée : prénom + mémoire relationnelle\n\n💡 Objet et corps du message générés et personnalisés. Prêt à envoyer.`;
  }
  if (ch === 'social') {
    return `📲 Plan de contenu généré\n\nFréquence recommandée : 3–4 posts/semaine\nFormats prioritaires : ${medAge && medAge < 40 ? 'Reels (portée max) > Carrousels > Posts fixes' : 'Posts fixes + Stories quotidiennes'}\nMeilleur créneau : ${medAge && medAge < 40 ? 'Instagram 18h–20h' : 'Facebook 12h–14h'}\n\n💡 3 posts de lancement préparés — à valider avant publication.`;
  }
  return `🤖 Action planifiée pour ${total} contacts.\nJe surveille les résultats et documente automatiquement l'avancement dans ce rapport.`;
}

// ─── ActiveStrategy ───────────────────────────────────────────────────────────
// Gamification messages for step completion
const GAMI_MESSAGES = [
  (n, title) => `🎉 Bravo ! "${title}" est dans la boîte — chaque étape te rapproche du but !`,
  (n, title) => `💪 Nickel ! "${title}" est coché. Continue sur cette lancée !`,
  (n, title) => `🚀 Excellent ! "${title}" — tu avances comme un(e) pro !`,
  (n, title) => `⭐ Top ! "${title}" validée. Appee est fier de toi !`,
  (n, title) => `✅ Super boulot ! "${title}" terminée. Ta base clients va adorer ce que tu construis !`,
];

// ── Strategy Mind Map ────────────────────────────────────────────────────────
const MAP_CH = {
  email: { icon: '📧', color: '#5A35FF', bg: '#EDE9FF' },
  sms:   { icon: '📱', color: '#0EA5E9', bg: '#E0F2FE' },
  phone: { icon: '📞', color: '#F59E0B', bg: '#FEF3C7' },
  social:{ icon: '📲', color: '#10B981', bg: '#D1FAE5' },
  print: { icon: '🖨️', color: '#6B7280', bg: '#F3F4F6' },
  event: { icon: '🎉', color: '#EC4899', bg: '#FCE7F3' },
  landing:{ icon:'🌐', color: '#8B5CF6', bg: '#EDE9FF' },
  instore:{ icon:'🏪', color: '#EA580C', bg: '#FFEDD5' },
};

function StrategyMindMap({ strat, tasks, deliverables = {}, onToggle, onClose }) {
  const totalActions = strat.phases.reduce((s, p) => s + p.actions.length, 0);
  const doneCount    = Object.values(tasks).filter(Boolean).length;
  const pct          = totalActions > 0 ? Math.round(doneCount / totalActions * 100) : 0;

  // Livrable popup state: { actionId, idx } | null
  const [activeDeliv, setActiveDeliv] = useState(null);
  const activeDItem = activeDeliv ? (deliverables[activeDeliv.actionId] || [])[activeDeliv.idx] : null;

  const totalLivrables = Object.values(deliverables).reduce((s, arr) => s + arr.length, 0);

  return (
    <div className="smap-overlay" onClick={e => { if (e.target === e.currentTarget) { setActiveDeliv(null); onClose(); } }}>
      <div className="smap-modal">

        {/* Header */}
        <div className="smap-header">
          <div className="smap-header-left">
            <span className="smap-header-icon">{strat.icon}</span>
            <div>
              <div className="smap-header-title">{strat.title}</div>
              <div className="smap-header-sub">
                {doneCount}/{totalActions} actions réalisées &nbsp;·&nbsp;
                <span style={{ color: pct === 100 ? '#059669' : '#5A35FF', fontWeight: 700 }}>{pct}%</span>
                {totalLivrables > 0 && (
                  <span style={{ marginLeft: 8, color: '#C93DB0', fontWeight: 700 }}>
                    &nbsp;·&nbsp; 📄 {totalLivrables} livrable{totalLivrables > 1 ? 's' : ''}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="smap-header-right">
            <div className="smap-legend">
              <span className="smap-legend-item smap-legend-done">✓ Fait</span>
              <span className="smap-legend-item smap-legend-todo">○ À faire</span>
              <span className="smap-legend-item smap-legend-opt">· Optionnel</span>
              {totalLivrables > 0 && <span className="smap-legend-item smap-legend-deliv">📄 Livrable</span>}
            </div>
            <button className="smap-close" onClick={onClose}>✕</button>
          </div>
        </div>

        {/* Progress bar */}
        <div className="smap-progress-track">
          <div className="smap-progress-fill" style={{ width: `${pct}%` }} />
        </div>

        {/* Map canvas */}
        <div className="smap-canvas">

          {/* Root node */}
          <div className="smap-root-row">
            <div className="smap-root-node" style={{ borderColor: strat.color, background: strat.bg }}>
              <span className="smap-root-icon">{strat.icon}</span>
              <span className="smap-root-name">{strat.title}</span>
            </div>
          </div>

          {/* Vertical stem from root */}
          <div className="smap-stem" />

          {/* Phases row */}
          <div className="smap-phases-row">
            {strat.phases.map((phase) => {
              const phaseDone  = phase.actions.filter(a => tasks[a.id]).length;
              const phaseTotal = phase.actions.length;
              const phasePct   = phaseTotal > 0 ? Math.round(phaseDone / phaseTotal * 100) : 0;
              const isDone     = phasePct === 100;

              return (
                <div key={phase.id} className="smap-phase-col">
                  {/* Phase node */}
                  <div className={`smap-phase-node ${isDone ? 'smap-phase-node--done' : ''}`}>
                    <div className="smap-phase-name">{phase.name}</div>
                    <div className="smap-phase-week">{phase.week}</div>
                    <div className="smap-phase-prog">
                      <div className="smap-phase-prog-fill" style={{ width: `${phasePct}%` }} />
                    </div>
                    <div className="smap-phase-count">{phaseDone}/{phaseTotal}</div>
                  </div>

                  {/* Actions */}
                  <div className="smap-actions-col">
                    {phase.actions.map(action => {
                      const ch           = MAP_CH[action.ch] || MAP_CH.email;
                      const done         = !!tasks[action.id];
                      const actionDelivs = deliverables[action.id] || [];
                      return (
                        <div
                          key={action.id}
                          className={`smap-action ${done ? 'smap-action--done' : ''} ${action.optional ? 'smap-action--opt' : ''} ${actionDelivs.length ? 'smap-action--has-deliv' : ''}`}
                        >
                          {/* Clic principal = marquer fait */}
                          <button
                            className="smap-action-main"
                            onClick={() => onToggle(action.id)}
                            title={action.desc}
                          >
                            <span className="smap-action-ch" style={{ background: done ? '#D1FAE5' : ch.bg, color: done ? '#059669' : ch.color }}>
                              {done ? '✓' : ch.icon}
                            </span>
                            <span className="smap-action-title">{action.title}</span>
                            {action.appee && <span className="smap-action-appee" title="Appee s'en charge">🤖</span>}
                          </button>

                          {/* Livrables badges */}
                          {actionDelivs.length > 0 && (
                            <div className="smap-action-livrables">
                              {actionDelivs.map((d, idx) => (
                                <button
                                  key={idx}
                                  className="smap-livrable-badge"
                                  onClick={e => { e.stopPropagation(); setActiveDeliv({ actionId: action.id, idx }); }}
                                  title={`Ouvrir : ${d.label}`}
                                >
                                  {d.icon} {d.label}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

        </div>

        <div className="smap-footer">
          💡 Clique sur une action pour la marquer comme réalisée
          {totalLivrables > 0 && <span style={{ marginLeft: 12, color: '#C93DB0' }}>· Clique sur un badge 📄 pour consulter un livrable</span>}
        </div>

        {/* ── Popup livrable ── */}
        {activeDItem && (
          <div className="smap-deliv-overlay" onClick={() => setActiveDeliv(null)}>
            <div className="smap-deliv-popup" onClick={e => e.stopPropagation()}>
              <div className="smap-deliv-header">
                <span className="smap-deliv-title">{activeDItem.icon} {activeDItem.label}</span>
                <button className="smap-deliv-close" onClick={() => setActiveDeliv(null)}>✕</button>
              </div>
              <div className="smap-deliv-body">
                {activeDItem.type === 'email' && (
                  <>
                    {activeDItem.subject && (
                      <div className="smap-deliv-subject">📌 <strong>Objet :</strong> {activeDItem.subject}</div>
                    )}
                    <pre className="smap-deliv-text">{activeDItem.body}</pre>
                  </>
                )}
                {activeDItem.type === 'post' && (
                  <>
                    <pre className="smap-deliv-text">{activeDItem.text}</pre>
                    {activeDItem.image_url ? (
                      <div className="smap-deliv-img-wrap">
                        <img src={activeDItem.image_url} alt="Visuel du post" className="smap-deliv-img" />
                        <a href={activeDItem.image_url} download target="_blank" rel="noreferrer" className="smap-deliv-dl">⬇️ Télécharger le visuel</a>
                      </div>
                    ) : activeDItem.visualPrompt ? (
                      <div className="smap-deliv-visual">
                        🎨 <strong>Idée de visuel :</strong> {activeDItem.visualPrompt}
                      </div>
                    ) : null}
                  </>
                )}
              </div>
              <div className="smap-deliv-actions">
                <button
                  className="smap-deliv-copy"
                  onClick={() => navigator.clipboard.writeText(
                    activeDItem.type === 'email'
                      ? `Objet : ${activeDItem.subject}\n\n${activeDItem.body}`
                      : activeDItem.text
                  ).catch(() => {})}
                >
                  📋 Copier
                </button>
                {activeDeliv && (deliverables[activeDeliv.actionId] || []).length > 1 && (
                  <div className="smap-deliv-nav">
                    <button
                      disabled={activeDeliv.idx === 0}
                      onClick={() => setActiveDeliv(p => ({ ...p, idx: p.idx - 1 }))}
                    >◀</button>
                    <span>{activeDeliv.idx + 1} / {(deliverables[activeDeliv.actionId] || []).length}</span>
                    <button
                      disabled={activeDeliv.idx >= (deliverables[activeDeliv.actionId] || []).length - 1}
                      onClick={() => setActiveDeliv(p => ({ ...p, idx: p.idx + 1 }))}
                    >▶</button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

// ── Timeline verticale « feuille de route » (mobile) ─────────────────────────
// Vue additive, lecture seule + saut : montre TOUT le parcours (phases + actions)
// avec l'état de chaque étape et met en avant l'étape en cours. Ne touche pas
// aux cartes d'action interactives — un tap ramène simplement l'utilisateur dessus.
function StrategyTimeline({ phases, tasks, onJump }) {
  // Trouver l'index (phase, action) de la 1ʳᵉ action non faite = "étape en cours"
  let curPhase = -1, curAction = -1, found = false;
  phases.forEach((ph, pi) => ph.actions.forEach((a, ai) => {
    if (!found && !tasks[a.id]) { curPhase = pi; curAction = ai; found = true; }
  }));

  return (
    <div className="stl">
      <div className="stl-head">🗺️ Ta feuille de route</div>
      {phases.map((ph, pi) => {
        const doneN = ph.actions.filter(a => tasks[a.id]).length;
        const allDone = doneN === ph.actions.length;
        const isCurrentPhase = pi === curPhase;
        const locked = curPhase !== -1 && pi > curPhase;
        return (
          <div key={ph.id} className="stl-phase">
            <div className={`stl-plabel${locked ? ' locked' : ''}`}>
              <span className="stl-pn">Phase {pi + 1}{ph.week ? ` · ${ph.week}` : ''}</span>
              <span className="stl-plname">{ph.name}</span>
              <span className="stl-pc">{allDone ? '✓ ' : ''}{doneN}/{ph.actions.length}{locked ? ' 🔒' : ''}</span>
            </div>
            {ph.actions.map((a, ai) => {
              const isDone = !!tasks[a.id];
              const isCur  = isCurrentPhase && ai === curAction;
              const isLock = locked || (isCurrentPhase && ai > curAction);
              const ch = CH[a.ch] || {};
              const cls = isDone ? 'done' : isCur ? 'cur' : isLock ? 'lock' : 'todo';
              return (
                <button
                  key={a.id}
                  className={`stl-node stl-${cls}`}
                  onClick={() => onJump(pi, a.id, isCur)}
                >
                  <span className="stl-dot">{isDone ? '✓' : isCur ? '▸' : isLock ? '🔒' : '○'}</span>
                  <span className="stl-node-body">
                    <span className="stl-node-title">{a.title}</span>
                    <span className="stl-node-meta">
                      <span className="stl-ch" style={{ background: ch.bg, color: ch.color }}>{ch.icon} {ch.label}</span>
                      {a.appee && <span className="stl-auto">🧞 auto</span>}
                    </span>
                  </span>
                  {isCur && <span className="stl-cta">C'est ton étape →</span>}
                </button>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

function ActiveStrategy({ strat, onClose, onUpdate }) {
  const navigate = useNavigate();
  const { campaigns: allCampaigns, contacts, companyProfile, addNotification, showQuotaError } = useApp();
  const [tasks, setTasks] = useState(() => {
    const map = {};
    strat.phases.forEach(ph => ph.actions.forEach(a => { map[a.id] = !!a.done; }));
    return map;
  });
  const [taskDoneAt, setTaskDoneAt] = useState(() => {
    const map = {};
    strat.phases.forEach(ph => ph.actions.forEach(a => { if (a.doneAt) map[a.id] = a.doneAt; }));
    return map;
  });
  const [actionNotes, setActionNotes] = useState(() => {
    const map = {};
    strat.phases.forEach(ph => ph.actions.forEach(a => { if (a.notes) map[a.id] = a.notes; }));
    return map;
  });
  const [actionNoteInput, setActionNoteInput] = useState({});
  const [actionNoteOpen, setActionNoteOpen]   = useState({});
  const [notes, setNotes]             = useState(strat.notes || []);
  const [noteText, setNoteText]       = useState('');
  const [activePhase, setActivePhase] = useState(strat.currentPhase || 0);
  const [showReport, setShowReport]   = useState(false);
  const [showPerf, setShowPerf]       = useState(false);
  const [reportType, setReportType]   = useState('weekly');
  const [report, setReport]           = useState(null);
  const [generatingReport, setGen]    = useState(false);
  const [expanded, setExpanded]       = useState({});
  const [appeeNoteOpen, setAppeeNoteOpen] = useState({});
  const [showMap, setShowMap]         = useState(false);
  const [draftOpen, setDraftOpen]   = useState({});
  const [draftEdits, setDraftEdits] = useState({});

  // Social post editor state
  const [actionPosts, setActionPosts] = useState(() => {
    const map = {};
    strat.phases.forEach(ph => ph.actions.forEach(a => { if (a.posts?.length) map[a.id] = a.posts; }));
    return map;
  });
  const [postEditorOpen, setPostEditorOpen]     = useState({});
  const [postEditorText, setPostEditorText]     = useState({});
  const [postEditorVisual, setPostEditorVisual] = useState({});
  const [postGenerating, setPostGenerating]     = useState({});
  const [postGenCount, setPostGenCount]         = useState({});  // actionId → nb de générations IA utilisées
  const [genAllLoading, setGenAllLoading]       = useState(false);

  const MAX_POST_VERSIONS = 3; // garde-fou crédits IA : 3 propositions max par post

  // ── Visuel IA à la marque ──
  const [visualUrl, setVisualUrl]               = useState({});  // actionId → url image générée
  const [visualFormat, setVisualFormat]         = useState({});  // actionId → square|story|landscape
  const [visualGenerating, setVisualGenerating] = useState({});
  const [visualGenCount, setVisualGenCount]     = useState({});
  const [visualBrief, setVisualBrief]           = useState({});  // actionId → ce que le user veut voir
  const [postPillar, setPostPillar]             = useState({});  // actionId → pilier de comm choisi
  const MAX_VISUALS = 2; // 2 visuels max par post (1 image = 15 crédits IA — garde-fou marge)

  const generateVisualForAction = async (action, text) => {
    if ((visualGenCount[action.id] || 0) >= MAX_VISUALS) {
      addNotification('Tu as déjà 2 visuels pour ce post — garde le meilleur ! 🎨', 'error');
      return;
    }
    setVisualGenerating(p => ({ ...p, [action.id]: true }));
    try {
      const cp = companyProfile || {};
      const res = await api.generateVisual({
        post_text: (text || '').slice(0, 2000),
        brief: (visualBrief[action.id] || '').slice(0, 300),
        format: visualFormat[action.id] || 'square',
        company: {
          name:            cp.name || '',
          sector:          cp.sector || '',
          brand_primary:   cp.brand_primary || '',
          brand_secondary: cp.brand_secondary || '',
        },
      });
      if (res?.url) {
        setVisualUrl(p => ({ ...p, [action.id]: res.url }));
        setVisualGenCount(p => ({ ...p, [action.id]: (p[action.id] || 0) + 1 }));
        addNotification('🎨 Ton visuel est prêt !');
      } else {
        addNotification('La création du visuel a échoué — réessaie dans un instant.', 'error');
      }
    } catch (err) {
      // Quota → modale dédiée ; sinon TOUJOURS montrer l'erreur (jamais d'échec silencieux)
      if (err?.quotaExceeded) {
        showQuotaError(err);
      } else {
        addNotification('🎨 Visuel : ' + (err?.message || 'la création a échoué — réessaie.'), 'error');
      }
    } finally {
      setVisualGenerating(p => ({ ...p, [action.id]: false }));
    }
  };

  // Post topics (sujets) per phase — stored as phaseId → string[]
  const [phaseTopics, setPhaseTopics] = useState(() => strat.phaseTopics || {});
  const [topicsOpen, setTopicsOpen]   = useState({});
  const [topicsInput, setTopicsInput] = useState({});  // phaseId → string (raw textarea)
  const [topicsLoading, setTopicsLoading] = useState({});

  // Briefing state
  const [briefing, setBriefing] = useState(() => strat.briefing || { objective: '', context: '' });
  // Ouvert si vide (desktop) — sur mobile toujours replié par défaut pour alléger l'écran
  const [briefingOpen, setBriefingOpen] = useState(
    () => !strat.briefing?.objective && !(typeof window !== 'undefined' && window.innerWidth <= 768)
  );
  const [briefingLoading, setBriefingLoading] = useState(false);
  const [objectiveSuggestions, setObjectiveSuggestions] = useState([]);
  const [showObjectiveSuggestions, setShowObjectiveSuggestions] = useState(false);
  const [contextLoading, setContextLoading] = useState(false);
  const [genAllLoading2, setGenAllLoading2] = useState(false);
  const [genAllProgress, setGenAllProgress] = useState({ done: 0, total: 0 });

  // Ask Appee IA state
  const [askOpen,    setAskOpen]    = useState({});  // actionId → bool
  const [askInput,   setAskInput]   = useState({});  // actionId → string
  const [askLoading, setAskLoading] = useState({});  // actionId → bool
  const [appeeOpen,  setAppeeOpen]  = useState({});  // actionId → bool (panneau Appee unifié)
  const [landingBuilderAction, setLandingBuilderAction] = useState(null); // action for landing builder
  const [landingPages, setLandingPages] = useState({});  // actionId → landing page object

  // Gamification — show congrats overlay briefly
  const [gamiMsg, setGamiMsg] = useState(null);

  const totalTasks = useMemo(() => strat.phases.reduce((s, p) => s + p.actions.length, 0), [strat]);
  const doneTasks  = useMemo(() => Object.values(tasks).filter(Boolean).length, [tasks]);
  const progress   = useMemo(() => totalTasks > 0 ? Math.round(doneTasks / totalTasks * 100) : 0, [doneTasks, totalTasks]);

  // Livrables créés par action (brouillons email + posts sociaux)
  const deliverables = useMemo(() => {
    const map = {};
    strat.phases.forEach(ph => ph.actions.forEach(action => {
      const items = [];
      // Brouillon email
      if (action.ch === 'email') {
        const edits = draftEdits[action.id] || {};
        const draft = getEmailDraft(action, strat, companyProfile || {});
        const subject = edits.subject !== undefined ? edits.subject : (draft?.subject || '');
        const body    = edits.body    !== undefined ? edits.body    : (draft?.body    || '');
        if (body.trim()) {
          items.push({ type: 'email', icon: '✉️', label: 'Brouillon email', subject, body });
        }
      }
      // Posts sociaux
      (actionPosts[action.id] || []).forEach((post, i) => {
        items.push({ type: 'post', icon: '📱', label: `Post ${i + 1}${post.aiGenerated ? ' 🤖' : ''}`, text: post.text, visualPrompt: post.visualPrompt || '', image_url: post.image_url || '' });
      });
      if (items.length) map[action.id] = items;
    }));
    return map;
  }, [strat, draftEdits, actionPosts, companyProfile]);

  // Performance data from linked campaigns
  const linkedCampData = useMemo(() => {
    const ids = new Set((strat.linkedCampaigns || []).map(c => c.id));
    return ids.size > 0 ? (allCampaigns || []).filter(c => ids.has(c.id)) : [];
  }, [allCampaigns, strat.linkedCampaigns]);

  const perfStats = useMemo(() => {
    if (!linkedCampData.length) return null;
    const sent    = linkedCampData.reduce((s, c) => s + (c.contacts  || 0), 0);
    const opens   = linkedCampData.reduce((s, c) => s + (c.opens     || 0), 0);
    const clicks  = linkedCampData.reduce((s, c) => s + (c.clicks    || 0), 0);
    const avgRate = Math.round(linkedCampData.reduce((s, c) => s + (c.open_rate || 0), 0) / linkedCampData.length);
    return { sent, opens, clicks, avgRate };
  }, [linkedCampData]);

  const phaseProgress = useMemo(() =>
    strat.phases.map(ph => {
      const done = ph.actions.filter(a => tasks[a.id]).length;
      return ph.actions.length > 0 ? Math.round(done / ph.actions.length * 100) : 0;
    }), [strat, tasks]);

  const saveAndUpdate = (newTasks, newNotes, phaseIdx, newActionNotes, newTaskDoneAt, newActionPosts, newPhaseTopics, newBriefing) => {
    const _actionNotes  = newActionNotes  !== undefined ? newActionNotes  : actionNotes;
    const _taskDoneAt   = newTaskDoneAt   !== undefined ? newTaskDoneAt   : taskDoneAt;
    const _actionPosts  = newActionPosts  !== undefined ? newActionPosts  : actionPosts;
    const _phaseTopics  = newPhaseTopics  !== undefined ? newPhaseTopics  : phaseTopics;
    const updated = {
      ...strat,
      notes: newNotes,
      currentPhase: phaseIdx,
      phaseTopics: _phaseTopics,
      briefing: newBriefing !== undefined ? newBriefing : briefing,
      phases: strat.phases.map(ph => ({
        ...ph,
        actions: ph.actions.map(a => ({
          ...a,
          done:   !!newTasks[a.id],
          doneAt: _taskDoneAt[a.id] || a.doneAt || null,
          notes:  _actionNotes[a.id] || a.notes || [],
          posts:  _actionPosts[a.id] || a.posts || [],
        })),
      })),
    };
    onUpdate(updated);
  };

  const toggleTask = id => {
    const wasChecked = !!tasks[id];
    const newTasks = { ...tasks, [id]: !wasChecked };
    setTasks(newTasks);
    let newTaskDoneAt;
    if (newTasks[id]) {
      newTaskDoneAt = { ...taskDoneAt, [id]: new Date().toISOString() };
    } else {
      newTaskDoneAt = { ...taskDoneAt };
      delete newTaskDoneAt[id];
    }
    setTaskDoneAt(newTaskDoneAt);
    saveAndUpdate(newTasks, notes, activePhase, actionNotes, newTaskDoneAt);

    // Gamification — félicite quand on coche une tâche
    if (!wasChecked) {
      const allActions = strat.phases.flatMap(p => p.actions);
      const action = allActions.find(a => a.id === id);
      const newDone = Object.values(newTasks).filter(Boolean).length;
      const total   = allActions.length;
      const pct     = Math.round(newDone / total * 100);

      let msg;
      if (pct === 100) {
        msg = `🏆 WOW ! Stratégie terminée à 100% ! Tu es incroyable — je suis vraiment impressionné(e) par ta régularité. Tes clients vont sentir la différence 🚀`;
      } else if (pct >= 75 && Math.round((newDone - 1) / total * 100) < 75) {
        msg = `🎯 75% atteint ! Tu es dans la dernière ligne droite — plus que ${total - newDone} action${total - newDone > 1 ? 's' : ''} et la stratégie est bouclée !`;
      } else if (pct >= 50 && Math.round((newDone - 1) / total * 100) < 50) {
        msg = `⚡ Mi-parcours franchi ! ${newDone} actions sur ${total} — tu maintiens un rythme excellent. Appee est là pour la suite !`;
      } else if (pct >= 25 && Math.round((newDone - 1) / total * 100) < 25) {
        msg = `🌱 Premier quart en poche ! La dynamique est lancée — continue comme ça et les résultats vont suivre.`;
      } else if (newDone === 1) {
        msg = `🎉 Première étape validée ! Bravo — le plus dur c'est de commencer, et tu viens de le faire !`;
      } else {
        const fn = GAMI_MESSAGES[newDone % GAMI_MESSAGES.length];
        msg = fn(newDone, action?.title || 'cette étape');
      }
      setGamiMsg(msg);
      setTimeout(() => setGamiMsg(null), 4500);
    }
  };

  // Ask Appee IA
  const handleAskAppee = async (actionId, question) => {
    if (!question.trim()) return;
    setAskLoading(p => ({ ...p, [actionId]: true }));
    try {
      const allActions = strat.phases.flatMap(p => p.actions);
      const action = allActions.find(a => a.id === actionId);
      const phase  = strat.phases.find(p => p.actions.some(a => a.id === actionId));
      const fullCtx = buildStrategyContext();

      const res = await api.chatMessage(
        `Tu es Appee, consultant marketing senior et copilote stratégique de cette entreprise.\n\n${fullCtx}\n\nÉtape en cours : ${action?.title || ''} — ${action?.desc || ''}\nPhase : ${phase?.name || ''}\nAvancement global : ${progress}%\n\nQuestion : ${question}`,
        []
      );
      const answer = res?.reply || 'Désolé, je n\'ai pas pu générer une réponse.';
      const noteText = `❓ Question : ${question}\n\n🤖 Appee :\n${answer}`;
      // Save as action note
      const newNote = { text: noteText, date: new Date().toISOString(), aiResponse: true };
      const newActionNotes = { ...actionNotes, [actionId]: [...(actionNotes[actionId] || []), newNote] };
      setActionNotes(newActionNotes);
      setAskInput(p => ({ ...p, [actionId]: '' }));
      setAskOpen(p => ({ ...p, [actionId]: false }));
      saveAndUpdate(tasks, notes, activePhase, newActionNotes, taskDoneAt);
      addNotification('Appee a répondu — la réponse est sauvegardée dans les notes de l\'étape ✅');
    } catch (err) {
      addNotification(`Erreur IA : ${err.message}`, 'error');
    } finally {
      setAskLoading(p => ({ ...p, [actionId]: false }));
    }
  };

  const addNote = () => {
    if (!noteText.trim()) return;
    const newNotes = [{ text: noteText.trim(), date: new Date().toISOString() }, ...notes];
    setNotes(newNotes);
    setNoteText('');
    saveAndUpdate(tasks, newNotes, activePhase, actionNotes, taskDoneAt);
  };

  const addNoteAuto = (text) => {
    const newNotes = [{ text, date: new Date().toISOString(), auto: true }, ...notes];
    setNotes(newNotes);
    saveAndUpdate(tasks, newNotes, activePhase, actionNotes, taskDoneAt);
  };

  const handleCreateCampaign = (action, draft) => {
    navigate('/campaigns/new', {
      state: {
        preset: {
          name: `${strat.title} — ${action.title}`,
          objective: STRAT_OBJECTIVES[strat.id] || 'Fidéliser mes clients',
          tone: 'tutoiement',
          ...(draft?.subject ? { subject: draft.subject } : {}),
          ...(draft?.body    ? { body:    draft.body }    : {}),
          ...(draft?.imageUrl ? { imageUrl: draft.imageUrl } : {}),
        },
        startStep: 2,
        strategyContext: {
          instanceId:  strat.instanceId,
          title:       strat.title,
          actionId:    action.id,
          actionTitle: action.title,
          channel:     action.ch,
        },
      },
    });
  };

  // ── Contexte stratégique complet pour toutes les générations IA ──────────────
  // Assemble l'intégralité du contexte connu : entreprise, audit, réseaux, brief, stratégie
  const buildStrategyContext = () => {
    const cp = companyProfile || {};
    const lines = [];

    // ── Entreprise ──
    const priceLabels = { budget: 'Entrée de gamme', mid: 'Milieu de gamme', premium: 'Premium', luxury: 'Luxe' };
    const toneLabels  = { formal: 'Formel/professionnel', friendly: 'Chaleureux/proche', casual: 'Décontracté/fun', expert: 'Expert/technique' };
    lines.push('── ENTREPRISE ──');
    if (cp.name)                  lines.push(`Nom : ${cp.name}`);
    if (cp.sector)                lines.push(`Secteur : ${cp.sector}`);
    if (cp.description)           lines.push(`Description : ${cp.description}`);
    if (cp.services)              lines.push(`Services / produits : ${cp.services}`);
    if (cp.target_audience)       lines.push(`Clientèle cible : ${cp.target_audience}`);
    if (cp.values)                lines.push(`Valeurs : ${cp.values}`);
    if (cp.unique_selling_point)  lines.push(`Différenciant clé : ${cp.unique_selling_point}`);
    if (cp.price_range)           lines.push(`Positionnement prix : ${priceLabels[cp.price_range] || cp.price_range}`);
    if (cp.communication_tone)    lines.push(`Ton de communication : ${toneLabels[cp.communication_tone] || cp.communication_tone}`);
    if (cp.geographic_zone)       lines.push(`Zone d'action : ${cp.geographic_zone}`);
    if (cp.business_hours)        lines.push(`Horaires : ${cp.business_hours}`);
    if (cp.keywords)              lines.push(`Mots-clés / spécialités : ${cp.keywords}`);
    if (cp.competitors)           lines.push(`Concurrents : ${cp.competitors}`);
    if (cp.avg_client_value)      lines.push(`Panier moyen client : ${cp.avg_client_value}`);
    if (cp.business_phone || cp.business_email) lines.push(`Contact pour les appels à l'action : ${[cp.business_phone, cp.business_email].filter(Boolean).join(' · ')}`);
    if (cp.website)               lines.push(`Site web : ${cp.website}`);

    // ── Réseaux sociaux ──
    const socials = [];
    if (cp.facebook_url)  socials.push(`Facebook : ${cp.facebook_url}`);
    if (cp.instagram_url) socials.push(`Instagram : ${cp.instagram_url}`);
    if (cp.linkedin_url)  socials.push(`LinkedIn : ${cp.linkedin_url}`);
    if (socials.length) lines.push(`Réseaux sociaux : ${socials.join(' · ')}`);

    // Analyse réseaux si disponible
    if (cp.social_analysis && Object.keys(cp.social_analysis).length > 0) {
      const analyses = Object.entries(cp.social_analysis)
        .filter(([, v]) => v && (v.followers || v.summary || v.engagement_rate))
        .map(([net, v]) => {
          const parts = [net];
          if (v.followers)       parts.push(`${v.followers} abonnés`);
          if (v.engagement_rate) parts.push(`engagement ${v.engagement_rate}%`);
          if (v.summary)         parts.push(v.summary.slice(0, 80));
          return parts.join(' — ');
        });
      if (analyses.length) lines.push(`Analyse réseaux : ${analyses.join(' | ')}`);
    }

    // ── Audit marketing ── (vrai schéma : overall_score / top_priority / summary / sections[])
    const audit = cp.marketing_audit || {};
    if (Object.keys(audit).length > 0) {
      lines.push('── AUDIT MARKETING ──');
      if (audit.overall_score != null)  lines.push(`Score audit : ${audit.overall_score}/100`);
      if (audit.top_priority)           lines.push(`Priorité #1 à corriger : ${audit.top_priority}`);
      if (audit.summary)                lines.push(`Résumé : ${audit.summary}`);
      if (Array.isArray(audit.sections) && audit.sections.length) {
        const secLines = audit.sections.slice(0, 6).map(s => {
          const acts = Array.isArray(s.actions) && s.actions.length ? ` → ${s.actions.slice(0, 2).join(', ')}` : '';
          return `${s.title || 'Section'} : ${s.score != null ? s.score + '/100' : ''}${s.status ? ` [${s.status}]` : ''}${acts}`;
        });
        lines.push(`Points à travailler :\n  - ${secLines.join('\n  - ')}`);
      }
    }
    if (strat.source === 'audit' && strat.auditScore) {
      lines.push(`Stratégie issue d'un audit · Score initial : ${strat.auditScore}/100`);
    }

    // ── Stratégie en cours ──
    lines.push('── STRATÉGIE EN COURS ──');
    lines.push(`Titre : ${strat.title}${strat.tagline ? ' — ' + strat.tagline : ''}`);
    const channels = getChannels(strat.phases);
    if (channels.length) lines.push(`Canaux utilisés : ${channels.map(c => CH[c]?.label || c).join(', ')}`);
    lines.push(`Nombre de phases : ${strat.phases.length}`);

    // ── Réponses du wizard (défi, objectif 90 jours…) — l'étoile polaire du client ──
    if (strat.wizardAnswers && Object.keys(strat.wizardAnswers).length) {
      const wa = strat.wizardAnswers;
      const lbl = (stepId, val) => {
        const step = (typeof WIZARD_STEPS !== 'undefined' ? WIZARD_STEPS : []).find(s => s.id === stepId);
        const opt = step?.options?.find(o => o.value === val);
        return opt?.label || val;
      };
      lines.push('── OBJECTIFS CLIENT (déclarés) ──');
      if (wa.defi)     lines.push(`Défi principal : ${lbl('defi', wa.defi)}`);
      if (wa.objectif) lines.push(`Objectif 90 jours : ${lbl('objectif', wa.objectif)}`);
      if (wa.relation) lines.push(`Relation client actuelle : ${lbl('relation', wa.relation)}`);
      if (wa.clients)  lines.push(`Type de clientèle : ${lbl('clients', wa.clients)}`);
    }

    // ── Brief stratégique ──
    if (briefing.objective || briefing.context) {
      lines.push('── BRIEF STRATÉGIQUE ──');
      if (briefing.objective) lines.push(`Objectif principal : ${briefing.objective}`);
      if (briefing.context)   lines.push(`Contexte & sujets clés : ${briefing.context}`);
    }

    return lines.filter(Boolean).join('\n');
  };

  // ── Social post generator ─────────────────────────────────────────────────────
  const generatePostForAction = async (action) => {
    // Garde-fou crédits IA : max 3 versions par post
    if ((postGenCount[action.id] || 0) >= MAX_POST_VERSIONS) {
      addNotification(`Tu as déjà 3 propositions pour ce post — ajuste le texte à la main pour économiser tes crédits ✍️`, 'error');
      setPostEditorOpen(p => ({ ...p, [action.id]: true }));
      return;
    }
    setPostGenerating(p => ({ ...p, [action.id]: true }));
    try {
      const topics  = (phaseTopics[currentPhase?.id] || []).filter(Boolean);
      const fullCtx = buildStrategyContext().slice(0, 7500);
      const cp      = companyProfile || {};
      const res = await api.generateSocialPost({
        action_title:      action.title,
        action_desc:       action.desc || '',
        pillar:            postPillar[action.id] || null,
        context:           action._ctx || null,
        briefing_objective: briefing.objective || null,
        briefing_context:  briefing.context || null,
        full_context:      fullCtx,
        topics,
        company: {
          name:        cp.name        || '',
          sector:      cp.sector      || '',
          description: cp.description || '',
        },
      });
      setPostEditorText(p => ({ ...p, [action.id]: res?.text || '' }));
      setPostEditorVisual(p => ({ ...p, [action.id]: res?.visual_prompt || '' }));
      setPostEditorOpen(p => ({ ...p, [action.id]: true }));
      setPostGenCount(p => ({ ...p, [action.id]: (p[action.id] || 0) + 1 }));
    } catch (err) {
      showQuotaError(err);
      setPostEditorText(p => ({ ...p, [action.id]: '' }));
      setPostEditorOpen(p => ({ ...p, [action.id]: true }));
    } finally {
      setPostGenerating(p => ({ ...p, [action.id]: false }));
    }
  };

  const generateTopicsForPhase = async (phase) => {
    const phId = phase.id;
    setTopicsLoading(p => ({ ...p, [phId]: true }));
    try {
      const socialActions = phase.actions.filter(a => a.ch === 'social').map(a => a.title).join(', ');
      const fullCtx = buildStrategyContext();
      const extra = `Phase "${phase.name}" · Actions prévues : ${socialActions}`;

      const res = await api.generateStrategic('suggest_topics', fullCtx, extra);
      const reply = res?.result || '';
      // Parse numbered list
      const lines = reply.split('\n')
        .map(l => l.replace(/^\d+[\.\)]\s*/, '').replace(/^\-\s*/, '').trim())
        .filter(l => l.length > 5 && l.length < 120);
      const newTopics = { ...phaseTopics, [phId]: lines };
      setPhaseTopics(newTopics);
      setTopicsInput(p => ({ ...p, [phId]: lines.join('\n') }));
      saveAndUpdate(tasks, notes, activePhase, actionNotes, taskDoneAt, actionPosts, newTopics);
    } catch (err) { showQuotaError(err); }
    finally { setTopicsLoading(p => ({ ...p, [phId]: false })); }
  };

  const saveTopicsForPhase = (phId) => {
    const raw = topicsInput[phId] || '';
    const lines = raw.split('\n').map(l => l.trim()).filter(Boolean);
    const newTopics = { ...phaseTopics, [phId]: lines };
    setPhaseTopics(newTopics);
    setTopicsOpen(p => ({ ...p, [phId]: false }));
    saveAndUpdate(tasks, notes, activePhase, actionNotes, taskDoneAt, actionPosts, newTopics);
  };

  const generateBriefingFromProfile = async () => {
    setBriefingLoading(true);
    setShowObjectiveSuggestions(false);
    setObjectiveSuggestions([]);
    try {
      const fullCtx = buildStrategyContext();
      const res = await api.generateStrategic('suggest_objectives', fullCtx);
      const reply = res?.result || '';
      const lines = reply.split('\n')
        .map(l => l.replace(/^\d+[\.\)]\s*/, '').replace(/^[-•]\s*/, '').trim())
        .filter(l => l.length > 10 && l.length < 200);
      if (lines.length > 0) {
        setObjectiveSuggestions(lines.slice(0, 5));
        setShowObjectiveSuggestions(true);
      }
    } catch (err) { showQuotaError(err); }
    finally { setBriefingLoading(false); }
  };

  const generateContextFromObjective = async () => {
    setContextLoading(true);
    try {
      const fullCtx = buildStrategyContext();
      const obj = briefing.objective?.trim() || '';
      const res = await api.generateStrategic('suggest_context', fullCtx, obj);
      const reply = res?.result || '';
      if (reply.trim()) {
        setBriefing(b => ({ ...b, context: reply.trim() }));
      }
    } catch (err) { showQuotaError(err); }
    finally { setContextLoading(false); }
  };

  const generateAllContent = async () => {
    setGenAllLoading2(true);
    const allActions = strat.phases.flatMap(p => p.actions);
    const socialActions = allActions.filter(a => a.ch === 'social' && !(actionPosts[a.id]?.length));
    const emailActions = allActions.filter(a => a.ch === 'email');
    const total = socialActions.length + emailActions.length;
    setGenAllProgress({ done: 0, total });

    // Pre-populate email drafts (local, no API)
    const newDraftEdits = { ...draftEdits };
    emailActions.forEach(a => {
      if (!draftEdits[a.id]?.body) {
        const d = getEmailDraft(a, strat, companyProfile);
        if (d) newDraftEdits[a.id] = { subject: d.subject, body: d.body, imageUrl: '' };
      }
    });
    setDraftEdits(newDraftEdits);
    setGenAllProgress(p => ({ ...p, done: p.done + emailActions.length }));

    // Generate social posts
    const cp = companyProfile || {};
    const fullCtx = buildStrategyContext().slice(0, 7500);
    for (const action of socialActions) {
      try {
        const phase = strat.phases.find(ph => ph.actions.some(a => a.id === action.id));
        const topics = (phaseTopics[phase?.id] || []).filter(Boolean);
        const res = await api.generateSocialPost({ // eslint-disable-line no-await-in-loop
          action_title:      action.title,
          action_desc:       action.desc || '',
          context:           briefing.context || null,
          briefing_objective: briefing.objective || null,
          briefing_context:  briefing.context || null,
          full_context:      fullCtx,
          topics,
          company: { name: cp.name || '', sector: cp.sector || '', description: cp.description || '' },
        });
        if (res?.text) {
          const newPost = { text: res.text, visualPrompt: res.visual_prompt || '', date: new Date().toISOString(), aiGenerated: true };
          setActionPosts(prev => ({ ...prev, [action.id]: [...(prev[action.id] || []), newPost] }));
        }
      } catch (err) { showQuotaError(err); break; }
      setGenAllProgress(p => ({ ...p, done: p.done + 1 })); // eslint-disable-line no-loop-func
    }

    // Save everything
    saveAndUpdate(tasks, notes, activePhase, actionNotes, taskDoneAt, actionPosts, phaseTopics, briefing);
    setGenAllLoading2(false);
    setGenAllProgress({ done: 0, total: 0 });
  };

  const savePostForAction = (actionId) => {
    const text = postEditorText[actionId] || '';
    const visualPrompt = postEditorVisual[actionId] || '';
    const imageUrl = visualUrl[actionId] || '';  // le visuel généré est sauvegardé avec le post
    if (!text.trim()) return;
    const newPost = { text, visualPrompt, image_url: imageUrl, pillar: postPillar[actionId] || null, date: new Date().toISOString() };
    const newPosts = { ...actionPosts, [actionId]: [...(actionPosts[actionId] || []), newPost] };
    setActionPosts(newPosts);
    setPostEditorOpen(p => ({ ...p, [actionId]: false }));
    setPostEditorText(p => ({ ...p, [actionId]: '' }));
    setPostEditorVisual(p => ({ ...p, [actionId]: '' }));
    saveAndUpdate(tasks, notes, activePhase, actionNotes, taskDoneAt, newPosts);
    addNotification(imageUrl ? '✅ Post + visuel enregistrés dans la stratégie' : '✅ Post enregistré dans la stratégie');
  };

  // Charger les landing pages liées à cette stratégie
  useEffect(() => {
    api.getLandingPages().then(pages => {
      const map = {};
      (pages || []).forEach(p => { if (p.action_id) map[p.action_id] = p; });
      setLandingPages(map);
    }).catch(() => {});
  }, [strat.instanceId]); // eslint-disable-line

  const adviceText = progress < 30
    ? "C'est parti ! Commence par les actions 🤖 Auto — elles avancent sans effort. Les premières semaines donnent le ton de toute la stratégie."
    : progress < 70
    ? "Belle progression ! Continue sur ta lancée. Les actions manuelles de cette phase sont les plus impactantes — prends le temps de bien les exécuter."
    : "Impressionnant ! Tu es presque au bout. La régularité que tu montres est exactement ce qui différencie les PME qui croissent de celles qui stagnent.";

  const generateReport = () => {
    setGen(true);
    setTimeout(() => {
      const typeL = { weekly: 'HEBDOMADAIRE', monthly: 'MENSUEL', custom: 'PERSONNALISÉ' };
      const curPh = strat.phases[activePhase];
      const nextA = (curPh?.actions || []).filter(a => !tasks[a.id]).slice(0, 3);
      const text = `📊 RAPPORT ${typeL[reportType]} — ${strat.title}
════════════════════════════════════
Stratégie : ${strat.title}
Période   : ${format(new Date(), 'd MMMM yyyy', { locale: fr })}
Avancement: ${progress}% (${doneTasks}/${totalTasks} actions)

PROGRESSION PAR PHASE
────────────────────────────────────
${strat.phases.map((ph, i) => {
  const d = ph.actions.filter(a => tasks[a.id]).length;
  const p = ph.actions.length > 0 ? Math.round(d / ph.actions.length * 100) : 0;
  return `${p === 100 ? '✅' : i === activePhase ? '▶' : '○'} ${ph.name} — ${d}/${ph.actions.length} (${p}%)`;
}).join('\n')}

NOTES DE SUIVI
────────────────────────────────────
${notes.length > 0
  ? notes.map(n => `• ${format(new Date(n.date), 'd MMM yyyy', { locale: fr })} — ${n.text}`).join('\n')
  : 'Aucune note.'}

ANALYSE
────────────────────────────────────
${progress < 30
  ? "La stratégie vient d'être lancée. Concentre-toi sur les actions 🤖 Auto pour prendre de l'élan rapidement."
  : progress < 70
  ? `Bonne progression à ${progress}%. Le rythme est soutenu — maintiens-le sur les prochaines actions manuelles.`
  : `Excellente exécution à ${progress}% ! Prépare dès maintenant le bilan et la prochaine stratégie.`}

PROCHAINES ACTIONS RECOMMANDÉES
────────────────────────────────────
${nextA.length > 0
  ? nextA.map((a, i) => `${i + 1}. [${CH[a.ch]?.label || a.ch}] ${a.title}`).join('\n')
  : '   Toutes les actions de la phase en cours sont terminées !'}

════════════════════════════════════
Rapport généré par Appee`;
      setReport(text);
      setGen(false);
    }, 1500);
  };

  const currentPhase = strat.phases[activePhase] || strat.phases[0];

  const generateAllPostsForPhase = async () => {
    const socialActions = currentPhase.actions.filter(a => a.ch === 'social');
    if (!socialActions.length) return;
    setGenAllLoading(true);
    for (const action of socialActions) {
      if ((actionPosts[action.id] || []).length === 0) {
        const ctx = postEditorText[`ctx_${action.id}`] || '';
        await generatePostForAction({ ...action, _ctx: ctx }); // eslint-disable-line no-await-in-loop
      }
    }
    setGenAllLoading(false);
  };

  return (
    <div className="active-strat">

      {/* ── Gamification overlay ── */}
      {gamiMsg && (
        <div style={{
          position: 'fixed', bottom: 28, right: 28, zIndex: 9999,
          background: 'linear-gradient(135deg,#5A35FF,#7C5CFF)',
          color: '#fff', borderRadius: 16, padding: '16px 22px',
          maxWidth: 380, boxShadow: '0 8px 32px rgba(90,53,255,.4)',
          fontSize: 14, fontWeight: 600, lineHeight: 1.5,
          animation: 'slideInGami .35s ease',
        }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <span style={{ fontSize: 22, flexShrink: 0 }}>🎊</span>
            <div>{gamiMsg}</div>
          </div>
        </div>
      )}

      <div className="active-header">
        <div className="active-header-left">
          <div className="active-title">{strat.icon} {strat.title}</div>
          {strat.source === 'audit' && (
            <div style={{
              background: '#F5F3FF',
              border: '1px solid #DDD6FE',
              borderRadius: 8,
              padding: '8px 14px',
              fontSize: 13,
              color: '#5B21B6',
              marginTop: 8,
            }}>
              📊 Stratégie créée depuis l'audit · Score initial : <strong>{strat.auditScore}/100</strong>
              {strat.auditDate && ` · ${new Date(strat.auditDate).toLocaleDateString('fr-CH')}`}
            </div>
          )}
          <ChannelChips phases={strat.phases} max={5} />
          <div className="active-progress-bar-wrap">
            <div className="active-progress-bar">
              <div className="active-progress-fill" style={{ width: `${progress}%` }} />
            </div>
            <span className="active-progress-label">{progress}%</span>
          </div>
        </div>
        <div className="active-header-actions">
          <button className={`active-perf-btn${showPerf ? ' active' : ''}`} onClick={() => setShowPerf(p => !p)}>
            📈 Performance
          </button>
          <button className="active-report-btn" onClick={() => { setReport(null); setShowReport(true); }}>
            📊 Rapport
          </button>
          <button className="active-map-btn" onClick={() => setShowMap(true)}>
            🗺 Vue Carte
          </button>
        </div>
      </div>

      <div className="active-phases">
        {strat.phases.map((ph, i) => {
          const pp = phaseProgress[i];
          return (
            <button
              key={ph.id}
              className={`active-phase-tab${i === activePhase ? ' active' : ''}${pp === 100 ? ' done' : ''}`}
              onClick={() => setActivePhase(i)}
            >
              <span className="apt-num">{pp === 100 ? '✅' : `Phase ${i + 1}`}</span>
              <span className="apt-name">{ph.name}</span>
              <span className="apt-week">{ph.week}</span>
              <div className="apt-bar"><div style={{ width: `${pp}%` }} /></div>
            </button>
          );
        })}
      </div>

      {/* ─── Timeline « feuille de route » (mobile uniquement) ──────────── */}
      <div className="strat-timeline-mobile-wrap">
        <StrategyTimeline
          phases={strat.phases}
          tasks={tasks}
          onJump={(phaseIdx, actionId, isCurrent) => {
            setActivePhase(phaseIdx);
            // laisser React re-rendre la bonne phase avant de scroller vers l'action
            setTimeout(() => {
              const el = document.getElementById(`action-${actionId}`);
              if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 60);
          }}
        />
      </div>

      {/* ─── Performance panel ────────────────────────────────────────── */}
      {showPerf && (
        <div className="strat-perf-panel">
          <div className="spp-header">
            <div className="spp-title">📈 Analyse de performance — <strong>{strat.title}</strong></div>
            <button className="spp-close" onClick={() => setShowPerf(false)}>✕</button>
          </div>

          {linkedCampData.length === 0 ? (
            <div className="spp-empty">
              <div className="spp-empty-icon">📭</div>
              <div className="spp-empty-text">Aucune campagne lancée depuis cette stratégie</div>
              <div className="spp-empty-sub">Clique sur "Créer la campagne" sur une action Email, SMS ou Landing pour démarrer le suivi automatique.</div>
            </div>
          ) : (
            <>
              <div className="spp-stats">
                <div className="spp-stat">
                  <div className="spp-stat-val">{perfStats.sent.toLocaleString('fr')}</div>
                  <div className="spp-stat-lbl">Contacts ciblés</div>
                </div>
                <div className="spp-stat spp-stat-green">
                  <div className="spp-stat-val">{perfStats.opens.toLocaleString('fr')}</div>
                  <div className="spp-stat-lbl">Ouvertures</div>
                </div>
                <div className="spp-stat spp-stat-violet">
                  <div className="spp-stat-val">{perfStats.avgRate}%</div>
                  <div className="spp-stat-lbl">Taux d'ouverture moy.</div>
                </div>
                <div className="spp-stat">
                  <div className="spp-stat-val">{perfStats.clicks.toLocaleString('fr')}</div>
                  <div className="spp-stat-lbl">Clics</div>
                </div>
                <div className="spp-stat">
                  <div className="spp-stat-val">{linkedCampData.length}</div>
                  <div className="spp-stat-lbl">Campagnes</div>
                </div>
              </div>

              <div className="spp-section">
                <div className="spp-section-title">Détail par campagne</div>
                <div className="spp-camp-list">
                  {linkedCampData.map(c => {
                    const lc     = (strat.linkedCampaigns || []).find(x => x.id === c.id);
                    const chIcon = lc?.channel === 'sms' ? '📱' : lc?.channel === 'landing' ? '🌐' : '📧';
                    return (
                      <div key={c.id} className="spp-camp-row">
                        <div className="spp-camp-left">
                          <span className="spp-camp-icon">{chIcon}</span>
                          <div>
                            <div className="spp-camp-name">{c.name}</div>
                            {lc?.actionTitle && <div className="spp-camp-action">{lc.actionTitle}</div>}
                          </div>
                        </div>
                        <div className="spp-camp-metrics">
                          <div className="spp-metric"><span className="spp-mval">{c.contacts || 0}</span><span className="spp-mlbl">envois</span></div>
                          <div className="spp-metric spp-metric-green"><span className="spp-mval">{c.open_rate || 0}%</span><span className="spp-mlbl">ouverts</span></div>
                          <div className="spp-metric"><span className="spp-mval">{c.clicks || 0}</span><span className="spp-mlbl">clics</span></div>
                          <span className={`spp-badge spp-badge-${c.status || 'draft'}`}>
                            {c.status === 'sent' ? '✓ Envoyée' : c.status === 'scheduled' ? '⏰ Planifiée' : '📝 Brouillon'}
                          </span>
                        </div>
                        <div className="spp-bar-wrap">
                          <div className="spp-bar"><div className="spp-bar-fill" style={{ width: `${Math.min(c.open_rate || 0, 100)}%` }} /></div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {linkedCampData.some(c => c.subject) && (
                <div className="spp-section">
                  <div className="spp-section-title">📋 Suivi des messages</div>
                  <div className="spp-templates">
                    {linkedCampData.filter(c => c.subject).map(c => (
                      <div key={c.id} className="spp-template-card">
                        <div className="spp-tpl-top">
                          <span className="spp-tpl-name">{c.name}</span>
                          <span className={`spp-badge spp-badge-${c.status || 'draft'}`}>
                            {c.status === 'sent' ? '✓ Envoyée' : c.status === 'scheduled' ? '⏰ Planifiée' : '📝 Brouillon'}
                          </span>
                        </div>
                        <div className="spp-tpl-subject">Objet : <em>{c.subject}</em></div>
                        {c.body && <div className="spp-tpl-preview">{c.body.slice(0, 140)}{c.body.length > 140 ? '…' : ''}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="spp-section">
                <div className="spp-section-title">👥 Contacts engagés</div>
                <div className="spp-engaged">
                  <div className="spp-engaged-item">
                    <div className="spp-engaged-num">{perfStats.opens.toLocaleString('fr')}</div>
                    <div className="spp-engaged-lbl">ont ouvert au moins un message</div>
                  </div>
                  <div className="spp-engaged-item">
                    <div className="spp-engaged-num">{perfStats.clicks.toLocaleString('fr')}</div>
                    <div className="spp-engaged-lbl">ont cliqué sur un lien</div>
                  </div>
                  <div className="spp-engaged-item">
                    <div className="spp-engaged-num">
                      {perfStats.sent > 0 ? Math.round((perfStats.opens / perfStats.sent) * 100) : 0}%
                    </div>
                    <div className="spp-engaged-lbl">taux d'engagement global</div>
                  </div>
                  <button className="spp-view-btn" onClick={() => navigate('/campaigns')}>
                    Voir le détail des campagnes →
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Briefing de stratégie ── */}
      <div className="strat-briefing-wrap">
        <button
          className="strat-briefing-toggle"
          onClick={() => setBriefingOpen(o => !o)}
        >
          <span className="sbw-icon">📋</span>
          <span className="sbw-title">Briefing de la stratégie</span>
          <span className="sbw-sub">
            {briefing.objective
              ? `Objectif : ${briefing.objective.slice(0, 60)}…`
              : 'Définis l\'objectif et le contexte — Appee s\'en sert pour tout générer'}
          </span>
          <span className="sbw-arrow">{briefingOpen ? '▲' : '▼'}</span>
          {briefing.objective && <span className="sbw-done-badge">✓ Renseigné</span>}
        </button>

        {briefingOpen && (
          <div className="strat-briefing-body">
            <div className="sbb-row">
              <div className="sbb-field">
                <label className="sbb-label">🎯 Objectif à atteindre</label>
                <input
                  className="sbb-input"
                  value={briefing.objective}
                  onChange={e => setBriefing(b => ({ ...b, objective: e.target.value }))}
                  placeholder="Ex : Augmenter ma visibilité locale, fidéliser mes 50 meilleurs clients…"
                />
              </div>
              <button
                className="sbb-ai-btn"
                onClick={generateBriefingFromProfile}
                disabled={briefingLoading}
              >
                {briefingLoading ? '⏳ Génération…' : '🤖 Appee suggère'}
              </button>
            </div>

            {showObjectiveSuggestions && objectiveSuggestions.length > 0 && (
              <div className="sbb-suggestions">
                <div className="sbb-suggestions-title">💡 Choisissez un objectif ou inspirez-vous :</div>
                <div className="sbb-suggestions-list">
                  {objectiveSuggestions.map((s, i) => (
                    <button
                      key={i}
                      className="sbb-suggestion-chip"
                      onClick={() => {
                        setBriefing(b => ({ ...b, objective: s }));
                        setShowObjectiveSuggestions(false);
                      }}
                    >
                      {s}
                    </button>
                  ))}
                </div>
                <button className="sbb-suggestions-close" onClick={() => setShowObjectiveSuggestions(false)}>✕ Fermer</button>
              </div>
            )}

            <div className="sbb-field" style={{ marginTop: 10 }}>
              <div className="sbb-label-row">
                <label className="sbb-label">📝 Contexte & sujets clés <span style={{fontWeight:400,color:'#9CA3AF'}}>(produits phares, promos prévues, ton, événements…)</span></label>
                <button
                  className="sbb-ai-btn sbb-ai-btn-sm"
                  onClick={generateContextFromObjective}
                  disabled={contextLoading}
                  title="Appee rédige le contexte d'après votre objectif et profil"
                >
                  {contextLoading ? '⏳' : '🤖'} Appee complète
                </button>
              </div>
              <textarea
                className="sbb-textarea"
                value={briefing.context}
                onChange={e => setBriefing(b => ({ ...b, context: e.target.value }))}
                rows={4}
                placeholder={"Ex :\n- Nouvelle collection printemps lancée le 15 avril\n- Promo -20% sur les soins visage\n- Ton : chaleureux, proche, tutoiement\n- Événement portes ouvertes le 3 mai"}
              />
            </div>

            <div className="sbb-actions">
              <button
                className="sbb-save-btn"
                onClick={() => {
                  setBriefingOpen(false);
                  saveAndUpdate(tasks, notes, activePhase, actionNotes, taskDoneAt, actionPosts, phaseTopics, briefing);
                }}
              >
                💾 Enregistrer le briefing
              </button>
              <button
                className="sbb-gen-all-btn"
                onClick={generateAllContent}
                disabled={genAllLoading2}
              >
                {genAllLoading2
                  ? `⏳ Génération… ${genAllProgress.done}/${genAllProgress.total}`
                  : '✨ Générer tout le contenu maintenant'}
              </button>
            </div>

            {genAllLoading2 && genAllProgress.total > 0 && (
              <div className="sbb-progress">
                <div
                  className="sbb-progress-bar"
                  style={{ width: `${Math.round(genAllProgress.done / genAllProgress.total * 100)}%` }}
                />
              </div>
            )}
          </div>
        )}
      </div>

      <div className="active-body">
        <div className="active-tasks">
          <div className="active-tasks-header">
            <div className="active-phase-title">{currentPhase.name}</div>
            <div className="active-phase-week">{currentPhase.week}</div>
          </div>

          {/* Communication pillars widget for comm/social strategies */}
          {(strat.id === 'plan-comm' || strat.id === 'digital360' || strat.id === 'local') && (
            <CommunicationPillarsWidget />
          )}

          {/* ── Topics + generate-all widget for social phases ── */}
          {currentPhase.actions.some(a => a.ch === 'social') && (() => {
            const phId   = currentPhase.id;
            const topics = phaseTopics[phId] || [];
            const isTopicsOpen = !!topicsOpen[phId];
            const isTopicsLoading = !!topicsLoading[phId];
            return (
              <div style={{ background: 'linear-gradient(135deg,#F5F3FF,#EDE9FE)', border: '1.5px solid #C4B5FD', borderRadius: 14, padding: '14px 16px', marginBottom: 14 }}>
                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                  <span style={{ fontSize: 20 }}>📋</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: '#1F2937' }}>Sujets de posts pour cette phase</div>
                    <div style={{ fontSize: 12, color: '#6B7280', marginTop: 1 }}>Définis les thèmes — Appee s'en sert pour générer chaque post</div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      onClick={() => {
                        if (!isTopicsOpen) setTopicsInput(p => ({ ...p, [phId]: topics.join('\n') }));
                        setTopicsOpen(p => ({ ...p, [phId]: !p[phId] }));
                      }}
                      style={{ background: '#fff', border: '1.5px solid #C4B5FD', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontWeight: 600, fontSize: 12, color: '#5A35FF' }}
                    >{isTopicsOpen ? '▲ Fermer' : topics.length > 0 ? `✏️ Modifier (${topics.length})` : '+ Définir les sujets'}</button>
                    <button
                      onClick={generateAllPostsForPhase}
                      disabled={genAllLoading}
                      style={{ background: genAllLoading ? '#E5E7EB' : 'linear-gradient(135deg,#5A35FF,#7C3AED)', color: genAllLoading ? '#9CA3AF' : '#fff', border: 'none', borderRadius: 8, padding: '6px 14px', cursor: genAllLoading ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: 12, whiteSpace: 'nowrap' }}
                    >{genAllLoading ? '⏳…' : '✨ Générer tous les posts'}</button>
                  </div>
                </div>

                {/* Topics list (compact) */}
                {!isTopicsOpen && topics.length > 0 && (
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {topics.map((t, i) => (
                      <span key={i} style={{ background: '#fff', border: '1px solid #C4B5FD', borderRadius: 20, padding: '3px 10px', fontSize: 12, color: '#5A35FF', fontWeight: 500 }}>
                        {i + 1}. {t}
                      </span>
                    ))}
                  </div>
                )}
                {!isTopicsOpen && topics.length === 0 && (
                  <div style={{ fontSize: 12, color: '#9CA3AF', fontStyle: 'italic' }}>
                    Aucun sujet défini — Appee peut en suggérer automatiquement ↓
                  </div>
                )}

                {/* Topics editor */}
                {isTopicsOpen && (
                  <div style={{ marginTop: 4 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                      <label style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>1 sujet par ligne — tu peux modifier les suggestions d'Appee</label>
                      <button
                        onClick={() => generateTopicsForPhase(currentPhase)}
                        disabled={isTopicsLoading}
                        style={{ background: 'none', border: '1.5px solid #C4B5FD', borderRadius: 8, padding: '4px 10px', cursor: isTopicsLoading ? 'not-allowed' : 'pointer', fontSize: 12, color: '#5A35FF', fontWeight: 600 }}
                      >{isTopicsLoading ? '⏳ Génération…' : '🤖 Appee suggère des sujets'}</button>
                    </div>
                    <textarea
                      value={topicsInput[phId] || ''}
                      onChange={e => setTopicsInput(p => ({ ...p, [phId]: e.target.value }))}
                      rows={6}
                      placeholder={"Ex :\nPromotion de printemps -20%\nCoulisses de l'atelier\nTémoignage client\nNouveau produit lancé lundi\nConseil pratique lié à notre métier"}
                      style={{ width: '100%', borderRadius: 8, border: '1.5px solid #C4B5FD', padding: '10px 12px', fontSize: 13, fontFamily: 'inherit', resize: 'vertical', outline: 'none', boxSizing: 'border-box' }}
                    />
                    <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                      <button onClick={() => setTopicsOpen(p => ({ ...p, [phId]: false }))} style={{ background: '#F3F4F6', border: 'none', borderRadius: 8, padding: '8px 14px', cursor: 'pointer', fontSize: 13 }}>Annuler</button>
                      <button onClick={() => saveTopicsForPhase(phId)} style={{ background: 'linear-gradient(135deg,#5A35FF,#7C3AED)', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>💾 Enregistrer les sujets</button>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {/* ── Prochain coup d'avance — action prioritaire ── */}
          {(() => {
            const nextAction = currentPhase.actions.find(a => !tasks[a.id]);
            if (!nextAction) return null;
            const ch = CH[nextAction.ch] || {};
            return (
              <div style={{
                background: 'linear-gradient(135deg,#F0FAFE,#E8F5FF)',
                border: '2px solid #BAE0FF',
                borderRadius: 14,
                padding: '14px 18px',
                marginBottom: 16,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 14,
              }}>
                <span style={{ fontSize: 26, flexShrink: 0, marginTop: 2 }}>🎯</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#0369A1', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
                    Cette semaine, ta priorité
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: '#1F2937', marginBottom: 4 }}>
                    {nextAction.title}
                  </div>
                  <div style={{ fontSize: 13, color: '#4B5563', lineHeight: 1.5 }}>
                    {nextAction.desc}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
                    <span style={{ background: ch.bg, color: ch.color, borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: 600 }}>
                      {ch.icon} {ch.label}
                    </span>
                    <button
                      onClick={() => toggleTask(nextAction.id)}
                      style={{
                        background: 'linear-gradient(135deg,#0EA5E9,#38BDF8)',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 8,
                        padding: '6px 14px',
                        fontWeight: 700,
                        fontSize: 13,
                        cursor: 'pointer',
                      }}
                    >
                      ✅ Marquer comme fait
                    </button>
                  </div>
                </div>
              </div>
            );
          })()}

          {currentPhase.actions.map(action => {
            const isDone = !!tasks[action.id];
            const ch     = CH[action.ch] || {};
            const adv    = ADVICE[action.ch] || ADVICE.email;
            const isExp  = !!expanded[action.id];
            return (
              <div key={action.id} id={`action-${action.id}`} className={`task-card${isDone ? ' task-done' : ''}`}>
                <button className="task-check" onClick={() => toggleTask(action.id)}>
                  {isDone ? '✅' : ''}
                </button>
                <div className="task-info">
                  <div className="task-header-row">
                    <div className="thr-left">
                      <span className="task-channel" style={{ background: ch.bg, color: ch.color }}>{ch.icon} {ch.label}</span>
                      {action.appee && <span className="task-appee-badge">🤖 Auto</span>}
                    </div>
                    <button className="task-advice-btn" onClick={() => setExpanded(p => ({ ...p, [action.id]: !p[action.id] }))}>
                      {isExp ? '▲' : '💡 Conseils'}
                    </button>
                  </div>
                  <div className={`task-title${isDone ? ' done' : ''}`}>{action.title}</div>
                  <div className="task-desc">{action.desc}</div>
                  {/* ── 🤖 Bouton Appee unifié ── */}
                  <div className="task-appee-wrap">
                    <div className="task-appee-bar">
                      <button
                        className={`task-appee-btn${appeeOpen[action.id] ? ' open' : ''}`}
                        onClick={() => setAppeeOpen(p => ({ ...p, [action.id]: !p[action.id] }))}
                      >
                        🤖 Appee {appeeOpen[action.id] ? '▲' : '▼'}
                      </button>
                      {action.ch === 'landing' ? (
                        <button
                          className="task-create-campaign-btn"
                          style={{ background: landingPages[action.id]?.published_at ? '#059669' : undefined }}
                          onClick={e => { e.stopPropagation(); setLandingBuilderAction(action); }}
                        >
                          🌐 {landingPages[action.id]?.published_at ? '✅ Landing page en ligne →' : 'Créer la landing page'}
                        </button>
                      ) : (action.ch === 'email' || action.ch === 'sms') && (
                        <button
                          className="task-create-campaign-btn"
                          onClick={e => { e.stopPropagation(); handleCreateCampaign(action); }}
                        >
                          {action.ch === 'email' ? '📧' : '📱'} Créer la campagne
                        </button>
                      )}
                    </div>

                    {appeeOpen[action.id] && (
                      <div className="task-appee-panel">

                        {/* ── Analyse automatique (si action.appee) ── */}
                        {action.appee && (
                          <div className="tap-section tap-analysis">
                            <div className="tap-sec-title">🎯 Analyse d'Appee</div>
                            <div className="taa-content">{getAppeeNote(action, contacts || [], companyProfile)}</div>
                            <button className="taa-add-note" onClick={() => {
                              addNoteAuto(`[Appee — ${action.title}]\n${getAppeeNote(action, contacts || [], companyProfile)}`);
                              setAppeeOpen(p => ({ ...p, [action.id]: false }));
                            }}>📋 Ajouter aux notes →</button>
                          </div>
                        )}

                        {/* ── SMS credits info ── */}
                        {action.ch === 'sms' && (
                          <div className="task-sms-credits">
                            <span>💳</span>
                            <span>Cette action nécessite des crédits SMS</span>
                            <button onClick={() => navigate('/settings')}>Voir les forfaits →</button>
                          </div>
                        )}

                        {/* ── Landing page ── */}
                        {action.ch === 'landing' && (
                          <div className="tap-section tap-landing">
                            <div className="tap-sec-title">🌐 Landing Page</div>
                            {landingPages[action.id]?.published_at ? (
                              <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                                  <span style={{ background: '#D1FAE5', color: '#059669', borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: 700 }}>✅ En ligne</span>
                                  <a
                                    href={`${window.location.origin}/lp/${landingPages[action.id].slug}`}
                                    target="_blank" rel="noreferrer"
                                    style={{ fontSize: 12, color: '#6428F4', fontWeight: 600 }}
                                  >Voir la page →</a>
                                </div>
                                <div style={{ display: 'flex', gap: 16, fontSize: 13, color: '#374151', marginBottom: 10 }}>
                                  <span>👁️ <strong>{landingPages[action.id].visits_count || 0}</strong> visites</span>
                                  <span>📬 <strong>{landingPages[action.id].leads_count || 0}</strong> leads</span>
                                </div>
                                <button
                                  className="taa-add-note"
                                  onClick={() => setLandingBuilderAction(action)}
                                >✏️ Modifier la landing page →</button>
                              </div>
                            ) : (
                              <div>
                                <div style={{ fontSize: 13, color: '#6B7280', marginBottom: 10 }}>
                                  Créez une page de capture optimisée avec formulaire et tracking des visites.
                                </div>
                                <button
                                  className="task-appee-btn"
                                  style={{ fontSize: 12, padding: '7px 14px' }}
                                  onClick={() => setLandingBuilderAction(action)}
                                >🌐 Créer ma landing page →</button>
                              </div>
                            )}
                          </div>
                        )}

                        {/* ── Brouillon email ── */}
                        {action.ch === 'email' && (() => {
                          const draft = getEmailDraft(action, strat, companyProfile);
                          const edits = draftEdits[action.id] || {};
                          const curSubject  = edits.subject  !== undefined ? edits.subject  : (draft?.subject  || '');
                          const curBody     = edits.body     !== undefined ? edits.body     : (draft?.body     || '');
                          const curImageUrl = edits.imageUrl !== undefined ? edits.imageUrl : '';
                          const setField = (field, val) => setDraftEdits(p => ({ ...p, [action.id]: { ...(p[action.id] || {}), [field]: val } }));
                          return (
                            <div className="tap-section tap-draft">
                              <div className="tap-sec-title">✉️ Brouillon email</div>
                              {draft?.imageHint && <div className="ted-image-hint">💡 Image suggérée : {draft.imageHint}</div>}
                              <div className="ted-field">
                                <label className="ted-lbl">Objet</label>
                                <input
                                  className="ted-subject"
                                  value={curSubject}
                                  onChange={e => setField('subject', e.target.value)}
                                  placeholder="Objet de l'email"
                                />
                              </div>
                              <div className="ted-field">
                                <label className="ted-lbl">Corps du message</label>
                                <textarea
                                  className="ted-body"
                                  value={curBody}
                                  onChange={e => setField('body', e.target.value)}
                                  rows={8}
                                  placeholder="Corps du message..."
                                />
                              </div>
                              <div className="ted-field">
                                <label className="ted-lbl">🖼 Image dans le mail (URL, optionnel)</label>
                                <input
                                  className="ted-image"
                                  value={curImageUrl}
                                  onChange={e => setField('imageUrl', e.target.value)}
                                  placeholder="https://monsite.com/image.jpg"
                                />
                              </div>
                              <div className="ted-actions">
                                <button
                                  className="ted-copy-btn"
                                  onClick={() => navigator.clipboard.writeText(`Objet : ${curSubject}\n\n${curBody}`).catch(() => {})}
                                >📋 Copier</button>
                                <button
                                  className="ted-campaign-btn"
                                  onClick={e => { e.stopPropagation(); handleCreateCampaign(action, { subject: curSubject, body: curBody, imageUrl: curImageUrl }); }}
                                >📧 Créer la campagne avec ce brouillon →</button>
                              </div>
                            </div>
                          );
                        })()}

                        {/* ── Éditeur de post social ── */}
                        {action.ch === 'social' && (() => {
                          const posts        = actionPosts[action.id] || [];
                          const curText      = postEditorText[action.id] || '';
                          const curVisual    = postEditorVisual[action.id] || '';
                          const isGenerating = !!postGenerating[action.id];
                          const ctxKey       = `ctx_${action.id}`;
                          const curCtx       = postEditorText[ctxKey] || '';
                          return (
                            <div className="tap-section tap-social">
                              <div className="tap-sec-title">📱 Post réseaux sociaux</div>

                              {/* Posts sauvegardés */}
                              {posts.length > 0 && (
                                <div className="task-social-posts" style={{ marginBottom: 12 }}>
                                  <div style={{ fontSize: 12, fontWeight: 700, color: '#5A35FF', marginBottom: 6 }}>
                                    {posts.length} post{posts.length > 1 ? 's' : ''} sauvegardé{posts.length > 1 ? 's' : ''}
                                  </div>
                                  {posts.map((post, i) => (
                                    <div key={i} className={`tsp-item${post.aiGenerated ? ' tsp-ai' : ''}`}>
                                      <div className="tsp-meta">
                                        <span>Post {i + 1}</span>
                                        <span>{format(new Date(post.date), "d MMM 'à' HH:mm", { locale: fr })}</span>
                                        <button className="tsp-copy" onClick={() => navigator.clipboard.writeText(post.text).catch(() => {})}>📋 Copier</button>
                                        <button className="tsp-del" onClick={() => {
                                          const newPosts = { ...actionPosts, [action.id]: posts.filter((_, j) => j !== i) };
                                          setActionPosts(newPosts);
                                          saveAndUpdate(tasks, notes, activePhase, actionNotes, taskDoneAt, newPosts);
                                        }}>✕</button>
                                      </div>
                                      {post.aiGenerated && <span className="appee-ai-badge">🤖 Généré par Appee</span>}
                                      {post.pillar && <span className="tsp-pillar-tag">{post.pillar}</span>}
                                      <p className="tsp-text">{post.text}</p>
                                      {post.image_url ? (
                                        <div className="tsp-img-wrap">
                                          <img src={post.image_url} alt="Visuel du post" className="tsp-img" />
                                          <a href={post.image_url} download target="_blank" rel="noreferrer" className="tsp-img-dl">⬇️ Télécharger le visuel</a>
                                        </div>
                                      ) : post.visualPrompt ? (
                                        <div className="tsp-visual">🎨 <strong>Idée de visuel :</strong> {post.visualPrompt}</div>
                                      ) : null}
                                    </div>
                                  ))}
                                </div>
                              )}

                              {/* Générateur */}
                              {/* ── Pilier de communication : oriente le post ── */}
                              <div className="ted-field">
                                <label className="ted-lbl">🎯 Type de post (pilier de communication)</label>
                                <div className="ted-pillars">
                                  {COMM_PILLARS.map(p => {
                                    const active = (postPillar[action.id] || 'Éducation & Valeur') === p.label;
                                    return (
                                      <button
                                        key={p.label}
                                        className={`ted-pillar${active ? ' on' : ''}`}
                                        style={active ? { borderColor: p.color, background: p.color + '18', color: p.color } : undefined}
                                        onClick={() => setPostPillar(pp => ({ ...pp, [action.id]: p.label }))}
                                        title={p.desc}
                                      >{p.icon} {p.label.split(' ')[0]}</button>
                                    );
                                  })}
                                </div>
                                <div className="ted-pillar-hint">
                                  {(COMM_PILLARS.find(p => p.label === (postPillar[action.id] || 'Éducation & Valeur')) || {}).desc}
                                </div>
                              </div>
                              <div className="ted-field">
                                <label className="ted-lbl">💡 Contexte (optionnel — améliore la génération)</label>
                                <input
                                  className="ted-subject"
                                  value={curCtx}
                                  onChange={e => setPostEditorText(p => ({ ...p, [ctxKey]: e.target.value }))}
                                  placeholder="Ex : promo -20%, nouveau produit, témoignage client..."
                                />
                              </div>
                              <div className="ted-field">
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                                  <label className="ted-lbl" style={{ margin: 0 }}>Texte du post</label>
                                  {(() => {
                                    const used = postGenCount[action.id] || 0;
                                    const remaining = MAX_POST_VERSIONS - used;
                                    const atMax = remaining <= 0;
                                    return (
                                      <button
                                        className="ted-copy-btn"
                                        onClick={() => generatePostForAction({ ...action, _ctx: curCtx })}
                                        disabled={isGenerating || atMax}
                                        title={atMax ? 'Limite de 3 propositions atteinte — ajuste le texte à la main' : ''}
                                        style={{ padding: '4px 10px', fontSize: 12, opacity: atMax ? 0.5 : 1 }}
                                      >{
                                        isGenerating ? '⏳ Génération…'
                                        : atMax        ? '✍️ Ajuste à la main'
                                        : curText      ? `🔄 Autre version (${remaining} restante${remaining > 1 ? 's' : ''})`
                                        :                '✨ Générer avec Appee'
                                      }</button>
                                    );
                                  })()}
                                </div>
                                <textarea
                                  className="ted-body"
                                  value={curText}
                                  onChange={e => setPostEditorText(p => ({ ...p, [action.id]: e.target.value }))}
                                  rows={7}
                                  placeholder="Clique sur '✨ Générer' ou écris ton post ici..."
                                />
                              </div>
                              {/* ── Visuel IA à ta marque — apparaît quand le texte est prêt ── */}
                              {curText.trim() && (
                                <div className="ted-visual-block">
                                  {visualUrl[action.id] ? (
                                    <div className="tvb-result">
                                      <img src={visualUrl[action.id]} alt="Visuel généré" className="tvb-img" />
                                      <div className="tvb-result-actions">
                                        <a href={visualUrl[action.id]} download target="_blank" rel="noreferrer" className="tvb-dl">⬇️ Télécharger</a>
                                        {(visualGenCount[action.id] || 0) < MAX_VISUALS && (
                                          <button
                                            className="tvb-retry"
                                            disabled={visualGenerating[action.id]}
                                            onClick={() => generateVisualForAction(action, curText)}
                                          >{visualGenerating[action.id] ? '🎨 Création…' : `🔄 Autre visuel (${MAX_VISUALS - (visualGenCount[action.id] || 0)} restant)`}</button>
                                        )}
                                      </div>
                                      <div className="tvb-hint">💡 Télécharge l'image, copie le texte — colle sur Instagram/Facebook, c'est prêt.</div>
                                    </div>
                                  ) : visualGenerating[action.id] ? (
                                    <div className="tvb-loading">
                                      <div className="tvb-skeleton">
                                        <div className="tvb-shimmer" />
                                        <div className="tvb-palette">
                                          <span style={{ background: companyProfile?.brand_primary || '#6C3CF4' }} />
                                          <span style={{ background: companyProfile?.brand_secondary || '#F2B01E' }} />
                                          <span style={{ background: '#C93DB0' }} />
                                        </div>
                                        <div className="tvb-brush">🎨</div>
                                      </div>
                                      <div className="tvb-loading-txt">Appee dessine ton visuel<span className="tvb-dots"><i>.</i><i>.</i><i>.</i></span></div>
                                      <div className="tvb-loading-sub">≈ 20 à 40 secondes — reste sur la page</div>
                                    </div>
                                  ) : (
                                    <div className="tvb-offer">
                                      <div className="tvb-offer-txt">🎨 <strong>Un visuel à ta marque ?</strong> Appee utilise tes couleurs (Réglages → Entreprise).</div>
                                      <input
                                        className="tvb-brief"
                                        value={visualBrief[action.id] || ''}
                                        onChange={e => setVisualBrief(p => ({ ...p, [action.id]: e.target.value }))}
                                        placeholder="Ce que tu veux voir : ex. « un plombier au travail », « des fleurs de printemps »…"
                                      />
                                      <div className="tvb-formats">
                                        {[
                                          { v: 'square',    l: '⬛ Carré' },
                                          { v: 'story',     l: '📱 Story' },
                                          { v: 'landscape', l: '🖼️ Paysage' },
                                        ].map(f => (
                                          <button
                                            key={f.v}
                                            className={`tvb-fmt${(visualFormat[action.id] || 'square') === f.v ? ' on' : ''}`}
                                            onClick={() => setVisualFormat(p => ({ ...p, [action.id]: f.v }))}
                                          >{f.l}</button>
                                        ))}
                                      </div>
                                      <button
                                        className="tvb-gen"
                                        onClick={() => generateVisualForAction(action, curText)}
                                      >✨ Créer mon visuel (15 crédits IA)</button>
                                    </div>
                                  )}
                                </div>
                              )}
                              <div className="ted-actions">
                                <button className="ted-copy-btn" onClick={() => navigator.clipboard.writeText(curText).catch(() => {})}>📋 Copier</button>
                                <button
                                  className="ted-campaign-btn"
                                  onClick={() => savePostForAction(action.id)}
                                  disabled={!curText.trim()}
                                >💾 Enregistrer dans la stratégie →</button>
                              </div>
                            </div>
                          );
                        })()}

                        {/* ── Poser une question à Appee ── */}
                        <div className="tap-section tap-ask">
                          <div className="tap-sec-title">💬 Poser une question à Appee</div>
                          <div className="taq-hint">
                            Ex : "C'est quoi les piliers de comm ?" · "Idée de post pour cette étape" · "Comment rédiger cet email ?"
                          </div>
                          <textarea
                            className="taq-input"
                            rows={2}
                            placeholder="Ta question..."
                            value={askInput[action.id] || ''}
                            onChange={e => setAskInput(p => ({ ...p, [action.id]: e.target.value }))}
                            onKeyDown={e => { if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') handleAskAppee(action.id, askInput[action.id] || ''); }}
                          />
                          <div className="taq-actions" style={{ marginTop: 8 }}>
                            <button
                              className="taq-submit"
                              onClick={() => handleAskAppee(action.id, askInput[action.id] || '')}
                              disabled={!askInput[action.id]?.trim() || askLoading[action.id]}
                            >
                              {askLoading[action.id] ? '⏳ Appee réfléchit…' : '🚀 Envoyer à Appee'}
                            </button>
                          </div>
                        </div>

                      </div>
                    )}
                  </div>

                  {/* Note par action */}
                  <div className="task-action-notes">
                    {(actionNotes[action.id] || []).map((n, i) => (
                      <div key={i} className={`tan-item${n.aiResponse ? ' tan-ai' : ''}`}>
                        <span className="tan-dot">{n.aiResponse ? '🤖' : '📝'}</span>
                        <div className="tan-body">
                          <span className="tan-text" style={{ whiteSpace: 'pre-wrap' }}>{n.text}</span>
                          <span className="tan-date">{format(new Date(n.date), "d MMM 'à' HH:mm", { locale: fr })}</span>
                        </div>
                      </div>
                    ))}
                    {actionNoteOpen[action.id] ? (
                      <div className="tan-form">
                        <textarea
                          className="tan-input"
                          rows={2}
                          placeholder="Note sur cette étape — résultat, observation, idée..."
                          value={actionNoteInput[action.id] || ''}
                          onChange={e => setActionNoteInput(p => ({ ...p, [action.id]: e.target.value }))}
                          autoFocus
                        />
                        <div className="tan-form-btns">
                          <button className="tan-save" onClick={() => {
                            const txt = (actionNoteInput[action.id] || '').trim();
                            if (!txt) return;
                            const newNote = { text: txt, date: new Date().toISOString() };
                            const newNotes = { ...actionNotes, [action.id]: [...(actionNotes[action.id] || []), newNote] };
                            setActionNotes(newNotes);
                            setActionNoteInput(p => ({ ...p, [action.id]: '' }));
                            setActionNoteOpen(p => ({ ...p, [action.id]: false }));
                            saveAndUpdate(tasks, notes, activePhase, newNotes, taskDoneAt);
                          }}>Enregistrer</button>
                          <button className="tan-cancel" onClick={() => setActionNoteOpen(p => ({ ...p, [action.id]: false }))}>Annuler</button>
                        </div>
                      </div>
                    ) : (
                      <button className="tan-add-btn" onClick={() => setActionNoteOpen(p => ({ ...p, [action.id]: true }))}>
                        📝 Ajouter une note
                      </button>
                    )}
                  </div>

                  {isExp && (
                    <div className="task-advice-panel">
                      <div className="tap-row">
                        <div><strong>🎯 Piliers canal</strong> : {adv.piliers.join(' · ')}</div>
                        <div><strong>⏱</strong> {adv.timing}</div>
                      </div>
                      <ul className="tap-tips">
                        {adv.tips.map((t, i) => <li key={i}>{t}</li>)}
                      </ul>
                      <div className="tap-kpi">📊 {adv.kpi}</div>
                      {action.ch === 'social' && <CommunicationPillarsWidget />}
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {activePhase < strat.phases.length - 1 && (
            <button className="active-next-phase" onClick={() => setActivePhase(activePhase + 1)}>
              Phase suivante : {strat.phases[activePhase + 1].name} →
            </button>
          )}
        </div>

        <div className="active-sidebar">
          <div className="sidebar-advice">
            <div className="sidebar-advice-head">🤖 Appee dit</div>
            <div className="sidebar-advice-text">{adviceText}</div>
          </div>

          <div className="sidebar-notes">
            <div className="sidebar-notes-title">📝 Notes de suivi</div>
            <textarea
              className="sidebar-note-ta"
              placeholder="Ajoute un résultat, une idée, une observation… (⌘+Entrée)"
              value={noteText}
              onChange={e => setNoteText(e.target.value)}
              onKeyDown={e => { if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') addNote(); }}
              rows={3}
            />
            <button className="sidebar-note-btn" onClick={addNote} disabled={!noteText.trim()}>+ Ajouter</button>
            {notes.length === 0
              ? <div className="sidebar-notes-empty">Aucune note pour l'instant.</div>
              : notes.map((n, i) => (
                <div key={i} className={`sidebar-note-item${n.auto ? ' note-auto' : ''}`}>
                  <div className="sidebar-note-date">{format(new Date(n.date), "d MMM yyyy 'à' HH:mm", { locale: fr })}</div>
                  <div className="sidebar-note-text">{n.text}</div>
                </div>
              ))}
          </div>

          {/* Linked campaigns */}
          {(strat.linkedCampaigns || []).length > 0 && (
            <div className="sidebar-linked-campaigns">
              <div className="sidebar-lc-title">🚀 Campagnes créées</div>
              {strat.linkedCampaigns.map((c, i) => (
                <div key={i} className="sidebar-lc-item">
                  <span className="sidebar-lc-icon">
                    {c.channel === 'email' ? '📧' : c.channel === 'sms' ? '📱' : '🌐'}
                  </span>
                  <div className="sidebar-lc-body">
                    <div className="sidebar-lc-name">{c.name}</div>
                    <div className="sidebar-lc-date">{c.actionTitle}</div>
                  </div>
                  <button
                    className="sidebar-lc-view"
                    onClick={() => navigate('/campaigns')}
                  >
                    Voir →
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="sidebar-kpis">
            <div className="sidebar-kpis-title">Indicateurs clés</div>
            {strat.kpis.map((k, i) => (
              <div key={i} className="sidebar-kpi-row">
                <span className="skpi-dot" /><span>{k}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Timeline — notes imbriquées sous leur étape */}
      {(() => {
        // Construire un groupe par action (done + ses notes) + strat_notes standalone
        const actionMap = {}; // actionId → { done?, notes[], ch, actionTitle, phase, doneDate }
        strat.phases.forEach(ph => {
          ph.actions.forEach(a => {
            if (!actionMap[a.id]) actionMap[a.id] = { actionId: a.id, actionTitle: a.title, phase: ph.name, ch: a.ch, done: false, doneDate: null, notes: [] };
            if (tasks[a.id]) {
              actionMap[a.id].done = true;
              actionMap[a.id].doneDate = taskDoneAt[a.id] || strat.launchedAt;
            }
            (actionNotes[a.id] || []).forEach(n => actionMap[a.id].notes.push(n));
          });
        });

        // Ne garder que les actions avec au moins un événement
        const actionGroups = Object.values(actionMap)
          .filter(g => g.done || g.notes.length > 0)
          .map(g => ({
            ...g,
            sortDate: g.doneDate || (g.notes[0]?.date) || strat.launchedAt,
          }))
          .sort((a, b) => new Date(b.sortDate) - new Date(a.sortDate));

        const stratNotes = [...notes]
          .sort((a, b) => new Date(b.date) - new Date(a.date));

        // Fusionner actions + strat_notes en une seule timeline triée par date
        const timelineBlocks = [];
        actionGroups.forEach(g => timelineBlocks.push({ kind: 'action', ...g }));
        stratNotes.forEach(n => timelineBlocks.push({ kind: 'strat_note', ...n }));
        timelineBlocks.sort((a, b) => new Date(b.sortDate || b.date) - new Date(a.sortDate || a.date));

        if (timelineBlocks.length === 0) return null;

        return (
          <div className="strat-timeline">
            <div className="stl-title">📅 Timeline de la stratégie</div>
            <div className="stl-list">
              {timelineBlocks.map((block, i) => {
                if (block.kind === 'strat_note') {
                  return (
                    <div key={`sn-${i}`} className="stl-item stl-strat_note">
                      <div className="stl-dot">{block.auto ? '🤖' : '💬'}</div>
                      <div className="stl-body">
                        <span className={`stl-badge ${block.auto ? 'stl-badge-auto' : 'stl-badge-note'}`}>{block.auto ? '🤖 Appee' : 'Note stratégie'}</span>
                        <span className="stl-note-text">{block.text}</span>
                        <span className="stl-date">{format(new Date(block.date), "d MMM yyyy 'à' HH:mm", { locale: fr })}</span>
                      </div>
                    </div>
                  );
                }
                // Action group : done + notes imbriquées
                const ch = block.ch ? CH[block.ch] : null;
                const sortedNotes = [...block.notes].sort((a, b) => new Date(b.date) - new Date(a.date));
                return (
                  <div key={`ag-${i}`} className="stl-group">
                    {/* Ligne principale : étape */}
                    {block.done && (
                      <div className="stl-item stl-done">
                        <div className="stl-dot">{ch?.icon || '✅'}</div>
                        <div className="stl-body">
                          <span className="stl-badge stl-badge-done">Fait ✓</span>
                          <span className="stl-action">{block.actionTitle}</span>
                          <span className="stl-phase">{block.phase}</span>
                          <span className="stl-date">{format(new Date(block.doneDate), "d MMM yyyy 'à' HH:mm", { locale: fr })}</span>
                        </div>
                      </div>
                    )}
                    {/* Notes imbriquées sous l'étape */}
                    {sortedNotes.map((n, ni) => (
                      <div key={`an-${ni}`} className="stl-item stl-action_note stl-nested">
                        <div className="stl-nested-line" />
                        <div className="stl-dot stl-dot-sm">📝</div>
                        <div className="stl-body">
                          {!block.done && <span className="stl-action stl-action-sm">{block.actionTitle}</span>}
                          <span className="stl-badge stl-badge-note">Note</span>
                          <span className="stl-note-text">{n.text}</span>
                          <span className="stl-date">{format(new Date(n.date), "d MMM yyyy 'à' HH:mm", { locale: fr })}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {showReport && (
        <div className="report-overlay" onClick={e => { if (e.target === e.currentTarget) { setShowReport(false); setReport(null); } }}>
          <div className="report-modal">
            <div className="report-modal-header">
              <div>
                <div className="report-modal-title">📊 Générer un rapport</div>
                <div className="report-modal-sub">{strat.title} — {progress}% complété</div>
              </div>
              <button className="report-modal-close" onClick={() => { setShowReport(false); setReport(null); }}>✕</button>
            </div>
            <div className="report-modal-body">
              {!report ? (
                <>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {[{ key: 'weekly', label: 'Hebdomadaire' }, { key: 'monthly', label: 'Mensuel' }, { key: 'custom', label: 'Personnalisé' }].map(t => (
                      <button key={t.key} className={`report-type-btn${reportType === t.key ? ' active' : ''}`} onClick={() => setReportType(t.key)}>{t.label}</button>
                    ))}
                  </div>
                  <p style={{ fontSize: 13, color: 'var(--muted)', margin: 0 }}>
                    Je génère un rapport complet avec progression, notes et recommandations — exportable en Word.
                  </p>
                  <button className="report-generate-btn" onClick={generateReport} disabled={generatingReport}>
                    {generatingReport ? '⏳ Génération en cours…' : '✨ Générer le rapport'}
                  </button>
                </>
              ) : (
                <>
                  <pre className="report-content">{report}</pre>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button className="report-generate-btn" onClick={() => exportToWord(strat, tasks, notes, progress)}>
                      📄 Exporter en Word (.doc)
                    </button>
                    <button className="report-type-btn active" onClick={() => navigator.clipboard?.writeText(report)}>
                      📋 Copier
                    </button>
                    <button className="report-type-btn" onClick={() => setReport(null)}>← Nouveau</button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      {showMap && (
        <StrategyMindMap
          strat={strat}
          tasks={tasks}
          deliverables={deliverables}
          onToggle={toggleTask}
          onClose={() => setShowMap(false)}
        />
      )}

      {/* ── Landing Page Builder ── */}
      {landingBuilderAction && (
        <LandingBuilder
          strategyInstanceId={strat.instanceId}
          actionId={landingBuilderAction.id}
          existingPage={landingPages[landingBuilderAction.id] || null}
          onClose={() => setLandingBuilderAction(null)}
          onPublished={(page) => {
            setLandingPages(p => ({ ...p, [landingBuilderAction.id]: page }));
            setLandingBuilderAction(null);
          }}
        />
      )}
    </div>
  );
}

// ─── StrategicDashboard — compatibility export ────────────────────────────────
export function StrategicDashboard({ onClose }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 900, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 32, maxWidth: 480, width: '100%', textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,.25)' }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>🧠</div>
        <div style={{ fontSize: 20, fontWeight: 800, marginBottom: 8 }}>Stratégie Marketing 360°</div>
        <div style={{ fontSize: 14, color: '#6B7280', marginBottom: 24, lineHeight: 1.6 }}>
          Le module Stratégie 360° est accessible depuis l'onglet <strong>Stratégie</strong>. Lance et suis plusieurs campagnes simultanément.
        </div>
        <button onClick={onClose} style={{ background: 'linear-gradient(135deg,#5A35FF,#7C3AED)', color: '#fff', border: 'none', padding: '12px 24px', borderRadius: 999, fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>Fermer</button>
      </div>
    </div>
  );
}

// ── Strategy Wizard — Agence-level, AI-generated ──────────────────────────────

const WIZARD_STEPS = [
  {
    id: 'secteur',
    question: 'Dans quel secteur exerces-tu ?',
    hint: 'Cela permet à Appee de te créer une stratégie vraiment adaptée à ton marché.',
    type: 'grid',
    options: [
      { value: 'commerce',    icon: '🛍️',  label: 'Commerce & Retail',       sub: 'Boutique, magasin, e-shop' },
      { value: 'restaurant',  icon: '🍽️',  label: 'Restaurant & Café',        sub: 'Restauration, food, traiteur' },
      { value: 'beaute',      icon: '💆',  label: 'Beauté & Bien-être',       sub: 'Salon, spa, coiffure, esthétique' },
      { value: 'sante',       icon: '🏥',  label: 'Santé & Médical',          sub: 'Cabinet, pharmacie, para-médical' },
      { value: 'artisan',     icon: '🔧',  label: 'Artisan & Services',       sub: 'Plombier, électricien, menuisier...' },
      { value: 'conseil',     icon: '💼',  label: 'Conseil & Expertise',      sub: 'Consultant, coach, agence, RH...' },
      { value: 'immobilier',  icon: '🏠',  label: 'Immobilier',               sub: 'Agence, promotion, gestion' },
      { value: 'autre',       icon: '✨',  label: 'Autre activité',            sub: 'Mon secteur n\'est pas listé' },
    ],
  },
  {
    id: 'defi',
    question: 'Quel est ton plus grand défi en ce moment ?',
    hint: 'Sois honnête — c\'est là que la stratégie va faire la différence.',
    type: 'grid',
    options: [
      { value: 'retention',    icon: '🔄', label: 'Clients qui ne reviennent pas', sub: 'Tu vois des gens une fois puis plus rien' },
      { value: 'attrition',    icon: '👻', label: 'Tu perds des clients',          sub: 'La base diminue sans que tu saches pourquoi' },
      { value: 'acquisition',  icon: '🆕', label: 'Attirer de nouveaux clients',   sub: 'Le carnet de commandes est trop léger' },
      { value: 'ca',           icon: '📈', label: 'Augmenter le chiffre d\'affaires', sub: 'Les clients sont là mais le CA stagne' },
      { value: 'visibilite',   icon: '📢', label: 'Manque de visibilité',          sub: 'Les concurrents te passent devant' },
      { value: 'fidelisation', icon: '💎', label: 'Valoriser mes meilleurs clients', sub: 'Tu as de bons clients mais rien de spécial pour eux' },
      { value: 'lancement',    icon: '🚀', label: 'Je lance quelque chose',          sub: 'Une nouvelle activité, un produit ou un service à faire décoller' },
    ],
  },
  {
    id: 'relation',
    question: 'Comment tu gères ta relation client aujourd\'hui ?',
    hint: 'Pas de jugement — on part de là où tu en es.',
    type: 'list',
    options: [
      { value: 'rien',        icon: '😶', label: 'Je ne fais rien de structuré',      sub: 'Pas de système, juste le bouche-à-oreille' },
      { value: 'social',      icon: '📱', label: 'Réseaux sociaux seulement',         sub: 'Instagram, Facebook mais sans suivi individuel' },
      { value: 'email_occ',   icon: '📧', label: 'Quelques emails de temps en temps', sub: 'Pas régulier, souvent oublié' },
      { value: 'fidelite',    icon: '🎁', label: 'Carte de fidélité physique',        sub: 'Tampon ou carte, mais pas de données' },
      { value: 'crm_partiel', icon: '📊', label: 'J\'ai des outils mais je les utilise peu', sub: 'CRM, newsletter… mais pas assez exploités' },
    ],
  },
  {
    id: 'clients',
    question: 'Comment tu décrirais ta clientèle ?',
    hint: 'C\'est la base pour personnaliser chaque message.',
    type: 'list',
    options: [
      { value: 'locaux',    icon: '📍', label: 'Locaux et fidèles',            sub: 'Clients de proximité, confiance établie' },
      { value: 'varies',    icon: '🎲', label: 'Variée, difficile à cerner',   sub: 'Profils très différents, peu de régularité' },
      { value: 'peu_recurrents', icon: '🔁', label: 'Beaucoup de one-shots',   sub: 'Peu de fidélisation, fort turnover' },
      { value: 'pareto',    icon: '👑', label: '20% font 80% du CA',           sub: 'Quelques grands comptes ou VIP qui portent tout' },
      { value: 'nouveaux',  icon: '🌱', label: 'Surtout des nouveaux',         sub: 'Activité récente, base en construction' },
    ],
  },
  {
    id: 'objectif',
    question: 'Dans 90 jours, quel résultat te rendrait vraiment fier(e) ?',
    hint: 'Ce résultat devient l\'étoile polaire de ta stratégie.',
    type: 'grid',
    options: [
      { value: 'recurrence',  icon: '🔄', label: '+20% de clients réguliers',     sub: 'Ils reviennent plus souvent' },
      { value: 'ca_plus',     icon: '💰', label: '+15% de chiffre d\'affaires',    sub: 'Croissance mesurable du CA' },
      { value: 'vip',         icon: '🏆', label: 'Lancer mon programme VIP',       sub: 'Récompenser les meilleurs, créer l\'exclusivité' },
      { value: 'auto',        icon: '⚙️', label: 'Communication 100% automatique', sub: 'Anniversaires, relances, tout sans effort' },
      { value: 'dormants',    icon: '🔮', label: 'Réactiver mes clients perdus',    sub: 'Récupérer 20-25% des inactifs' },
      { value: 'reputation',  icon: '⭐', label: 'Plus d\'avis & de réputation',    sub: 'Google, recommandations, bouche-à-oreille digital' },
    ],
  },
];

function buildStrategyPrompt(answers, contacts) {
  const nb = contacts?.length || 0;
  const labels = {};
  WIZARD_STEPS.forEach(s => {
    const opt = s.options.find(o => o.value === answers[s.id]);
    if (opt) labels[s.id] = opt.label;
  });

  return `Tu es expert senior en marketing relationnel CRM pour les PME et TPE.

Un client vient de répondre à un audit marketing :
- Secteur : ${labels.secteur || answers.secteur}
- Défi principal : ${labels.defi || answers.defi}
- Relation client actuelle : ${labels.relation || answers.relation}
- Type de clientèle : ${labels.clients || answers.clients}
- Objectif 90 jours : ${labels.objectif || answers.objectif}
- Base contacts actuelle : ${nb} contacts dans Appee

Génère une stratégie marketing sur mesure en JSON STRICT (sans texte avant ou après, juste le JSON) :

{
  "title": "Nom court et percutant de la stratégie (max 5 mots)",
  "tagline": "Accroche courte qui donne envie (max 10 mots)",
  "adviceIntro": "Message personnel d'Appee qui explique pourquoi cette stratégie est LA bonne pour ce client — chaleureux, tutoiement, 2-3 phrases max, très concret",
  "duration": "X semaines",
  "roi": "Estimation bénéfice réaliste (ex: +25% rétention)",
  "icon": "Un seul emoji pertinent",
  "color": "#HEX couleur principale",
  "bg": "#HEX couleur de fond très claire",
  "phases": [
    {
      "id": "p1",
      "name": "Nom de la phase",
      "week": "Sem. 1-2",
      "actions": [
        {
          "id": "a1",
          "ch": "email",
          "title": "Titre court et actionnable de l'action",
          "desc": "Description détaillée et concrète — que faire exactement, pourquoi, avec quel message, quel timing. Très pratique, 2-3 phrases.",
          "appee": true,
          "optional": false
        }
      ]
    }
  ]
}

Règles STRICTES :
- 3 phases exactement, progression logique
- 3 à 5 actions par phase (mix de canaux : email, sms, phone, social, event, print, instore, landing)
- appee:true uniquement pour les actions 100% automatisables (envois, relances)
- optional:true pour les actions à fort impact mais non essentielles
- Chaque action doit être HYPER CONCRÈTE et adaptée au secteur "${labels.secteur}"
- Intègre le défi "${labels.defi}" dans au moins 2 actions spécifiques
- Le ROI doit être réaliste pour une PME avec ${nb} contacts
- color/bg doivent être cohérents avec l'émotion de la stratégie
- JSON valide UNIQUEMENT, zéro texte autour`;
}

function extractStrategyJSON(text) {
  if (!text) return null;
  const match = text.match(/\{[\s\S]*\}/);
  if (!match) return null;
  try {
    const data = JSON.parse(match[0]);
    if (!data.phases || !Array.isArray(data.phases)) return null;
    // Ensure all required fields
    return {
      ...data,
      id: `custom-${Date.now()}`,
      category: 'custom',
      kpis: [],
      contacts_fn: () => 0,
      phases: data.phases.map((p, pi) => ({
        ...p,
        id: p.id || `p${pi + 1}`,
        actions: (p.actions || []).map((a, ai) => ({
          ...a,
          id: a.id || `${p.id || 'p'}-a${ai + 1}`,
          appee: !!a.appee,
          optional: !!a.optional,
        })),
      })),
    };
  } catch {
    return null;
  }
}

// ── Smart template engine — generates a personalized strategy from the 5 answers ──
function buildStrategyFromAnswers(answers, contacts) {
  const nb   = contacts?.length || 0;
  const s    = answers.secteur   || 'services';
  const d    = answers.defi      || 'fidelisation';
  const rel  = answers.relation  || 'occasionnel';
  const cli  = answers.clients   || 'locaux';
  const obj  = answers.objectif  || 'retention';

  // ── Sector flavours ──
  const SECTOR = {
    commerce:     { icon:'🛍️', color:'#F59E0B', bg:'#FFFBEB', word:'boutique', email_hook:'offre exclusive', event_hook:'vente privée' },
    beaute:       { icon:'💆', color:'#EC4899', bg:'#FDF2F8', word:'salon',    email_hook:'soin offert',      event_hook:'journée portes ouvertes' },
    sante:        { icon:'🏥', color:'#10B981', bg:'#ECFDF5', word:'cabinet',  email_hook:'bilan gratuit',    event_hook:'atelier bien-être' },
    restauration: { icon:'🍽️', color:'#F97316', bg:'#FFF7ED', word:'restaurant', email_hook:'menu surprise',  event_hook:'soirée thématique' },
    immobilier:   { icon:'🏡', color:'#6366F1', bg:'#EEF2FF', word:'agence',   email_hook:'estimation gratuite', event_hook:'portes ouvertes' },
    coaching:     { icon:'🎯', color:'#8B5CF6', bg:'#F5F3FF', word:'coaching', email_hook:'session découverte', event_hook:'atelier collectif' },
    artisanat:    { icon:'🔨', color:'#92400E', bg:'#FEF3C7', word:'atelier',  email_hook:'création sur mesure', event_hook:'démonstration live' },
    services:     { icon:'⚡', color:'#5A35FF', bg:'#F0EEFF', word:'agence',   email_hook:'audit gratuit',    event_hook:'webinaire' },
  };
  const sv = SECTOR[s] || SECTOR.services;

  // ── Defi → phase 1 focus ──
  const DEFI_P1 = {
    fidelisation: { name:'Activation de la fidélité', intro:'Tes clients existants sont ton meilleur actif. On commence par réactiver leur attachement à ta marque avec des gestes concrets et personnalisés.' },
    acquisition:  { name:'Capture de nouveaux clients', intro:'Pour gagner de nouveaux clients, tu dois d\'abord être visible et convaincant. On part d\'une stratégie de recommandation + présence locale.' },
    reactivation: { name:'Réveil des clients dormants', intro:'Tu as des clients qui ont disparu — ils connaissent déjà ta valeur. Un bon message au bon moment les fait revenir.' },
    ambassadeurs: { name:'Programme ambassadeurs', intro:'Tes meilleurs clients peuvent devenir tes commerciaux. On structure un programme de parrainage qui les motive vraiment.' },
    panier:       { name:'Maximisation du panier moyen', intro:'Chaque client peut dépenser plus s\'il perçoit la valeur. On travaille les offres complémentaires et les upsells pertinents.' },
    digitalisation:{ name:'Digitalisation de la relation', intro:'Passer au digital ne veut pas dire perdre le côté humain. On structure tes outils pour gagner du temps tout en restant proche.' },
    lancement:    { name:'Pré-lancement & liste d\'attente', intro:'Un lancement réussi se prépare. On crée l\'attente autour de ta nouvelle offre (activité, produit ou service), on capte une liste d\'intéressés, puis on convertit fort le jour J.' },
  };
  const dp1 = DEFI_P1[d] || DEFI_P1.fidelisation;

  // ── Relation client → personalization tone ──
  const REL_TONE = {
    pro:         'Tes clients sont engagés — capitalise sur cette confiance pour aller plus loin.',
    occasionnel: 'Tes contacts te connaissent mais l\'habitude n\'est pas encore là. On va créer des routines de contact régulières.',
    reactif:     'Tu réagis aux demandes mais tu ne prends pas d\'initiative. On va inverser ça.',
    absent:      'Il n\'y a pas encore de vraie relation. On repart de zéro avec une base solide.',
    debut:       'Tu démarres — chaque action compte double. On pose des fondations solides dès maintenant.',
  };

  // ── ROI based on defi + contacts ──
  const ROI_MAP = {
    fidelisation: nb > 100 ? '+28% taux de retour client' : '+35% visites répétées',
    acquisition:  nb > 50  ? '+20 nouveaux clients/mois'  : '+15 nouveaux contacts/mois',
    reactivation: nb > 100 ? nb > 500 ? `+${Math.round(nb*0.12)} clients réactivés` : `+${Math.round(nb*0.18)} clients réactivés` : `+${Math.round(nb*0.25)} clients réactivés`,
    ambassadeurs: '+40% recommandations qualifiées',
    panier:       '+22% chiffre d\'affaires par client',
    digitalisation:'-3h/semaine sur les tâches admin',
    lancement:     nb > 50 ? '+30% de ventes dès le lancement' : 'Un lancement qui décolle dès le jour J',
  };

  // ── Action banks by channel and defi ──
  const A = {
    // Phase 1 actions
    welcome_email:   { ch:'email',  appee:true,  optional:false, title:'Email de bienvenue personnalisé', desc:`Envoie un email chaleureux à tous tes nouveaux contacts avec une ${sv.email_hook}. Mentionne leur prénom et leur ${sv.word}. Taux d'ouverture moyen : 45%.` },
    reactivation_sms:{ ch:'sms',    appee:true,  optional:false, title:'SMS de réactivation ciblé', desc:`Envoie un SMS court aux contacts sans interaction depuis 6 mois. Ex : "Ça fait un moment, [Prénom] ! On a une surprise pour toi → lien". Court, direct, efficace.` },
    birthday_email:  { ch:'email',  appee:true,  optional:false, title:'Campagne anniversaires automatisée', desc:'Configure des emails d\'anniversaire automatiques avec une offre spéciale. C\'est la campagne avec le meilleur ROI — taux de conversion 3x supérieur aux campagnes classiques.' },
    phone_vip:       { ch:'phone',  appee:false, optional:false, title:'Appels personnalisés aux VIP', desc:`Appelle tes 10-20 meilleurs clients pour les remercier et présenter une nouveauté. Un appel de 3 min crée plus de fidélité que 10 emails. Note les retours dans Appee.` },
    social_post:     { ch:'social', appee:false, optional:true,  title:'Post "coulisses" sur les réseaux', desc:`Partage une photo authentique de ton ${sv.word} ou d\'un moment fort. Les contenus "vraie vie" génèrent 3x plus d\'engagement. Tag tes clients avec leur accord.` },
    referral_email:  { ch:'email',  appee:true,  optional:false, title:'Programme de parrainage par email', desc:'Envoie une proposition de parrainage à tes clients satisfaits : "Recommande-nous et reçois X". Définis une récompense claire et attractive pour les deux parties.' },
    segment_campaign:{ ch:'email',  appee:true,  optional:false, title:'Campagne segmentée par profil', desc:`Crée 2-3 segments dans Appee (VIP, actifs, dormants) et envoie un message adapté à chacun. La personnalisation augmente le taux de clic de 60%.` },
    local_event:     { ch:'event',  appee:false, optional:true,  title:`Organisation d\'un ${sv.event_hook}`, desc:`Organise un événement local pour tes meilleurs clients. Invite-les par email personnalisé avec réservation. Crée un moment mémorable qui génère du bouche-à-oreille.` },
    feedback_email:  { ch:'email',  appee:true,  optional:false, title:'Demande d\'avis automatisée', desc:'Envoie un email simple (1 question) après chaque achat/RDV pour collecter des avis. Les retours t\'aident à t\'améliorer et rassurent les futurs clients. Automatise via Appee.' },
    upsell_email:    { ch:'email',  appee:true,  optional:false, title:'Email cross-sell ciblé', desc:'Identifie dans Appee les clients qui n\'ont acheté qu\'un seul service/produit et propose-leur une offre complémentaire logique. Personnalise avec l\'historique d\'achat.' },
    instore_display: { ch:'instore',appee:false, optional:true,  title:'Affichage "client du mois" en boutique', desc:`Crée un espace visuel dans ton ${sv.word} qui met en valeur tes clients ou leurs témoignages. Crée de la fierté d\'appartenance et génère du contenu social authentique.` },
    newsletter:      { ch:'email',  appee:true,  optional:false, title:'Newsletter mensuelle de valeur', desc:'Lance une newsletter mensuelle avec 3 contenus utiles (tips, actualités secteur, offre exclusive abonnés). Objectif : rester dans l\'esprit de tes clients entre deux achats.' },
    reactivation_email:{ ch:'email',appee:true,  optional:false, title:'Séquence email de réactivation', desc:'Crée une séquence de 3 emails espacés de 5 jours pour les contacts dormants : 1/ "Tu nous manques", 2/ offre spéciale, 3/ dernier appel. Taux de réactivation moyen : 12-18%.' },
    sms_promo:       { ch:'sms',    appee:true,  optional:true,  title:'SMS promotionnel flash', desc:'Envoie un SMS promotionnel limité dans le temps (48h) à ta base active. La contrainte de temps crée l\'urgence. Limite à 1-2 fois par mois pour ne pas lasser.' },
    landing_page:    { ch:'landing',appee:false, optional:true,  title:'Page de capture prospects', desc:'Crée une page simple avec une offre d\'entrée attractive (ebook, consultation, réduction) pour capturer des emails de prospects. Partage-la sur tes réseaux et Google.' },
    digital_crm:     { ch:'email',  appee:true,  optional:false, title:'Mise à jour base contacts Appee', desc:'Passe en revue tous tes contacts et complète les informations manquantes (email, téléphone, catégorie). Une base propre = des campagnes 2x plus efficaces.' },
  };

  // ── Build 3 phases based on defi ──
  const PHASE_MAPS = {
    fidelisation: [
      { name: dp1.name, week:'Sem. 1-2', actions:[A.birthday_email, A.segment_campaign, A.phone_vip, A.feedback_email] },
      { name:'Engagement & Valeur ajoutée', week:'Sem. 3-5', actions:[A.newsletter, A.local_event, A.social_post] },
      { name:'Programme fidélité durable', week:'Sem. 6-12', actions:[A.referral_email, A.upsell_email, A.instore_display] },
    ],
    acquisition: [
      { name: dp1.name, week:'Sem. 1-2', actions:[A.landing_page, A.social_post, A.referral_email] },
      { name:'Nurturing & Conversion', week:'Sem. 3-5', actions:[A.welcome_email, A.segment_campaign, A.phone_vip] },
      { name:'Fidélisation des nouveaux', week:'Sem. 6-12', actions:[A.birthday_email, A.feedback_email, A.newsletter] },
    ],
    reactivation: [
      { name: dp1.name, week:'Sem. 1-2', actions:[A.reactivation_email, A.reactivation_sms, A.phone_vip] },
      { name:'Reconquête & Offre', week:'Sem. 3-5', actions:[A.sms_promo, A.local_event, A.segment_campaign] },
      { name:'Stabilisation de la relation', week:'Sem. 6-12', actions:[A.birthday_email, A.newsletter, A.feedback_email] },
    ],
    ambassadeurs: [
      { name: dp1.name, week:'Sem. 1-2', actions:[A.referral_email, A.phone_vip, A.feedback_email] },
      { name:'Activation & Récompenses', week:'Sem. 3-5', actions:[A.social_post, A.local_event, A.segment_campaign] },
      { name:'Expansion & Fidélité VIP', week:'Sem. 6-12', actions:[A.newsletter, A.birthday_email, A.upsell_email] },
    ],
    panier: [
      { name: dp1.name, week:'Sem. 1-2', actions:[A.upsell_email, A.segment_campaign, A.phone_vip] },
      { name:'Offres ciblées & Cross-sell', week:'Sem. 3-5', actions:[A.sms_promo, A.feedback_email, A.social_post] },
      { name:'Fidélisation & Récurrence', week:'Sem. 6-12', actions:[A.birthday_email, A.newsletter, A.referral_email] },
    ],
    digitalisation: [
      { name: dp1.name, week:'Sem. 1-2', actions:[A.digital_crm, A.birthday_email, A.feedback_email] },
      { name:'Automatisation des campagnes', week:'Sem. 3-5', actions:[A.segment_campaign, A.reactivation_email, A.sms_promo] },
      { name:'Optimisation & Croissance', week:'Sem. 6-12', actions:[A.newsletter, A.referral_email, A.upsell_email] },
    ],
  };

  const phases = (PHASE_MAPS[d] || PHASE_MAPS.fidelisation).map((p, pi) => ({
    id: `p${pi+1}`,
    name: p.name,
    week: p.week,
    actions: p.actions.map((a, ai) => ({ ...a, id: `p${pi+1}-a${ai+1}` })),
  }));

  // ── Titles based on defi + secteur ──
  const TITLES = {
    fidelisation: ['Plan Fidélité Pro', 'Stratégie Rétention Client', 'Programme Loyauté'],
    acquisition:  ['Plan Croissance Client', 'Stratégie Acquisition Locale', 'Moteur de Prospection'],
    reactivation: ['Opération Retour', 'Plan Réactivation Base', 'Stratégie Reconquête'],
    ambassadeurs: ['Programme Ambassadeurs', 'Stratégie Bouche-à-oreille', 'Plan Recommandation'],
    panier:       ['Stratégie Upsell Pro', 'Plan Croissance Valeur Client', 'Maximisation Revenus'],
    digitalisation:['Plan Digital CRM', 'Stratégie Automatisation', 'Transition Numérique'],
  };
  const titleList = TITLES[d] || TITLES.fidelisation;
  const title = titleList[Math.floor(Math.random() * titleList.length)];

  const adviceIntro = `${dp1.intro} ${REL_TONE[rel] || ''} Avec tes ${nb} contacts dans Appee, on a tout ce qu'il faut pour démarrer fort.`;

  return {
    id:          `custom-${Date.now()}`,
    category:    'custom',
    kpis:        [],
    contacts_fn: () => 0,
    title,
    tagline:     `Stratégie sur mesure — ${sv.word} · ${nb} contacts · 90 jours`,
    adviceIntro,
    duration:    '10 semaines',
    roi:         ROI_MAP[d] || '+25% satisfaction client',
    icon:        sv.icon,
    color:       sv.color,
    bg:          sv.bg,
    phases,
  };
}

// Generating screen — shows phases appearing live
function WizardGenerating({ answers, contacts, onDone }) {
  const [status, setStatus]   = useState('thinking'); // thinking | building | done | error
  const [phases, setPhases]   = useState([]);
  const [strategy, setStrat]  = useState(null);
  const [dots, setDots]       = useState('');
  const [retryCount, setRetry] = useState(0);

  useEffect(() => {
    const iv = setInterval(() => setDots(d => d.length >= 3 ? '' : d + '.'), 400);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setStatus('thinking');
    setPhases([]);
    setStrat(null);
    const run = async () => {
      try {
        await new Promise(r => setTimeout(r, 900));
        if (cancelled) return;

        setStatus('building');

        // Try the dedicated AI endpoint first, fall back to smart templates
        let data = null;
        try {
          const prompt = buildStrategyPrompt(answers, contacts);
          const apiRes = await api.generateWizardStrategy(prompt);
          // Try to extract JSON from response
          if (apiRes?.data?.phases) data = extractStrategyJSON(JSON.stringify(apiRes.data));
          if (!data && apiRes?.reply)  data = extractStrategyJSON(apiRes.reply);
        } catch (e) {
          console.info('AI endpoint not available, using smart templates:', e?.message);
        }
        if (cancelled) return;

        // Always fall back to smart template engine — never fails
        if (!data) data = buildStrategyFromAnswers(answers, contacts);

        // Reveal phases one by one
        for (let i = 0; i < data.phases.length; i++) {
          if (cancelled) return;
          await new Promise(r => setTimeout(r, 500));
          setPhases(prev => [...prev, data.phases[i]]);
        }

        await new Promise(r => setTimeout(r, 400));
        if (cancelled) return;
        setStrat(data);
        setStatus('done');
        setTimeout(() => { if (!cancelled) onDone(data); }, 1200);
      } catch (err) {
        console.error('WizardGenerating error:', err?.message || err);
        if (!cancelled) {
          setStatus('error');
        }
      }
    };
    run();
    return () => { cancelled = true; };
  }, [retryCount]); // eslint-disable-line

  const CH_COLORS = { email:'#5A35FF', sms:'#0EA5E9', phone:'#F59E0B', social:'#10B981', print:'#6B7280', event:'#EC4899', landing:'#8B5CF6', instore:'#EA580C' };
  const CH_ICONS  = { email:'📧', sms:'📱', phone:'📞', social:'📲', print:'🖨️', event:'🎉', landing:'🌐', instore:'🏪' };

  return (
    <div className="swiz-generating">
      {status === 'thinking' && (
        <div className="swiz-gen-thinking">
          <div className="swiz-gen-brain">🧠</div>
          <div className="swiz-gen-title">Appee analyse tes réponses{dots}</div>
          <div className="swiz-gen-sub">Je conçois ta stratégie sur mesure…</div>
        </div>
      )}

      {(status === 'building' || status === 'done') && (
        <div className="swiz-gen-live">
          <div className="swiz-gen-live-header">
            {status === 'done' && strategy
              ? <><span className="swiz-gen-icon">{strategy.icon}</span><span className="swiz-gen-strat-name">{strategy.title}</span></>
              : <><span className="swiz-gen-icon">⚡</span><span className="swiz-gen-strat-name">Création en cours{dots}</span></>
            }
          </div>
          <div className="swiz-gen-phases">
            {phases.map((phase, i) => (
              <div key={phase.id} className="swiz-gen-phase" style={{ animationDelay: `${i * 0.1}s` }}>
                <div className="swiz-gen-phase-name">
                  <span className="swiz-gen-phase-num">{i + 1}</span> {phase.name}
                  <span className="swiz-gen-phase-week">{phase.week}</span>
                </div>
                <div className="swiz-gen-actions">
                  {phase.actions.map(a => (
                    <div key={a.id} className="swiz-gen-action">
                      <span className="swiz-gen-action-ch" style={{ color: CH_COLORS[a.ch] || '#5A35FF' }}>
                        {CH_ICONS[a.ch] || '📋'}
                      </span>
                      <span>{a.title}</span>
                      {a.appee && <span className="swiz-gen-appee">🤖</span>}
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {status === 'building' && phases.length < 3 && (
              <div className="swiz-gen-phase swiz-gen-phase--loading">
                <div className="swiz-gen-loading-bar" />
              </div>
            )}
          </div>
          {status === 'done' && (
            <div className="swiz-gen-done">✓ Stratégie prête !</div>
          )}
        </div>
      )}

      {status === 'error' && (
        <div className="swiz-gen-error">
          <div className="swiz-gen-error-icon">😔</div>
          <div className="swiz-gen-error-title">Oups, une erreur s'est produite</div>
          <div className="swiz-gen-error-sub">Appee n'a pas pu générer ta stratégie. Réessaie !</div>
          <button className="swiz-gen-retry-btn" onClick={() => setRetry(r => r + 1)}>
            🔄 Réessayer
          </button>
        </div>
      )}
    </div>
  );
}

// Recap screen
function WizardRecap({ strategy, answers, onLaunch, onRegenerate }) {
  const CH_ICONS = { email:'📧', sms:'📱', phone:'📞', social:'📲', print:'🖨️', event:'🎉', landing:'🌐', instore:'🏪' };
  const totalActions = strategy.phases.reduce((s, p) => s + p.actions.length, 0);
  const autoActions  = strategy.phases.reduce((s, p) => s + p.actions.filter(a => a.appee).length, 0);

  return (
    <div className="swiz-recap">
      <div className="swiz-recap-hero" style={{ borderColor: strategy.color, background: strategy.bg }}>
        <span className="swiz-recap-hero-icon">{strategy.icon}</span>
        <div className="swiz-recap-hero-text">
          <div className="swiz-recap-hero-title">{strategy.title}</div>
          <div className="swiz-recap-hero-tagline">{strategy.tagline}</div>
        </div>
        <div className="swiz-recap-hero-roi" style={{ color: strategy.color }}>{strategy.roi}</div>
      </div>

      <p className="swiz-recap-advice">{strategy.adviceIntro}</p>

      <div className="swiz-recap-stats">
        <div className="swiz-recap-stat">
          <span className="swiz-recap-stat-val">{strategy.phases.length}</span>
          <span className="swiz-recap-stat-lbl">phases</span>
        </div>
        <div className="swiz-recap-stat">
          <span className="swiz-recap-stat-val">{totalActions}</span>
          <span className="swiz-recap-stat-lbl">actions</span>
        </div>
        <div className="swiz-recap-stat">
          <span className="swiz-recap-stat-val">{autoActions}</span>
          <span className="swiz-recap-stat-lbl">automatiques 🤖</span>
        </div>
        <div className="swiz-recap-stat">
          <span className="swiz-recap-stat-val">{strategy.duration}</span>
          <span className="swiz-recap-stat-lbl">durée</span>
        </div>
      </div>

      <div className="swiz-recap-phases">
        {strategy.phases.map((ph, i) => (
          <div key={ph.id} className="swiz-recap-phase">
            <div className="swiz-recap-phase-head">
              <span className="swiz-recap-phase-num">{i + 1}</span>
              <span className="swiz-recap-phase-name">{ph.name}</span>
              <span className="swiz-recap-phase-week">{ph.week}</span>
            </div>
            <div className="swiz-recap-phase-actions">
              {ph.actions.map(a => (
                <div key={a.id} className="swiz-recap-action">
                  <span>{CH_ICONS[a.ch] || '📋'}</span>
                  <span className="swiz-recap-action-title">{a.title}</span>
                  {a.appee && <span title="Automatique">🤖</span>}
                  {a.optional && <span className="swiz-recap-opt">opt.</span>}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="swiz-recap-answers">
        {WIZARD_STEPS.map(s => {
          const opt = s.options.find(o => o.value === answers[s.id]);
          return opt ? <span key={s.id} className="swiz-answer-pill">{opt.icon} {opt.label}</span> : null;
        })}
      </div>

      <div className="swiz-recap-actions">
        <button className="swiz-btn-regen" onClick={onRegenerate}>🔄 Regénérer</button>
        <button className="swiz-btn-launch" onClick={onLaunch}>🚀 Lancer cette stratégie</button>
      </div>
    </div>
  );
}

// Appee chat panel (per step)
function WizardAppeePanel({ stepQuestion, selectedAnswer }) {
  const [open, setOpen]       = useState(false);
  const [input, setInput]     = useState('');
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([]);

  const send = async () => {
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setInput('');
    setMessages(m => [...m, { role: 'user', text: userMsg }]);
    setLoading(true);
    try {
      const history = messages.map(m => ({ role: m.role, content: m.text }));
      const ctx = selectedAnswer
        ? `Question : "${stepQuestion}" — j'ai choisi "${selectedAnswer}".`
        : `Question : "${stepQuestion}"`;
      const res = await api.chatMessage(
        `${ctx}\nQuestion : ${userMsg}\nRéponds en 2-3 phrases max, tutoiement, concret et encourageant.`,
        history
      );
      setMessages(m => [...m, { role: 'assistant', text: res?.reply || 'Je suis là 💜' }]);
    } catch {
      setMessages(m => [...m, { role: 'assistant', text: 'Désolé, erreur de connexion. Continue !' }]);
    } finally { setLoading(false); }
  };

  return (
    <div className={`swiz-appee-panel ${open ? 'swiz-appee-panel--open' : ''}`}>
      <button className="swiz-appee-toggle" onClick={() => setOpen(o => !o)}>
        <span>💜</span> Demander à Appee
        <span className="swiz-appee-toggle-arrow">{open ? '▾' : '▴'}</span>
      </button>
      {open && (
        <div className="swiz-appee-body">
          {messages.length === 0 && (
            <div className="swiz-appee-intro">Pose-moi n'importe quelle question — je t'aide à faire le bon choix 😊</div>
          )}
          <div className="swiz-appee-messages">
            {messages.map((m, i) => (
              <div key={i} className={`swiz-appee-msg swiz-appee-msg--${m.role}`}>
                {m.role === 'assistant' && <span className="swiz-appee-msg-icon">🤖</span>}
                <span>{m.text}</span>
              </div>
            ))}
            {loading && (
              <div className="swiz-appee-msg swiz-appee-msg--assistant">
                <span className="swiz-appee-msg-icon">🤖</span>
                <span className="swiz-appee-typing">···</span>
              </div>
            )}
          </div>
          <div className="swiz-appee-input-row">
            <input className="swiz-appee-input" value={input} onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && send()} placeholder="Pose ta question…" autoFocus />
            <button className="swiz-appee-send" onClick={send} disabled={!input.trim() || loading}>→</button>
          </div>
        </div>
      )}
    </div>
  );
}

function StrategyWizard({ onClose, onLaunch, contacts }) {
  const [step, setStep]         = useState(0);
  const [answers, setAnswers]   = useState({});
  const [phase, setPhase]       = useState('questions'); // questions | generating | recap
  const [strategy, setStrategy] = useState(null);

  const currentStep = WIZARD_STEPS[step];
  const totalSteps  = WIZARD_STEPS.length;
  const pct         = Math.round((step / totalSteps) * 100);

  const pick = (field, value) => {
    const next = { ...answers, [field]: value };
    setAnswers(next);
    if (step < totalSteps - 1) {
      setTimeout(() => setStep(s => s + 1), 160);
    } else {
      setTimeout(() => setPhase('generating'), 200);
    }
  };

  const back = () => {
    if (phase === 'recap') { setPhase('generating'); setStrategy(null); return; }
    if (phase === 'generating') { setPhase('questions'); return; }
    setStep(s => Math.max(0, s - 1));
  };

  const handleGenDone = (strat) => {
    setStrategy(strat);
    setPhase('recap');
  };

  const handleLaunch = () => {
    if (!strategy) return;
    const instance = {
      ...strategy,
      instanceId:   `custom-${Date.now()}`,
      launchedAt:   new Date().toISOString(),
      currentPhase: 0,
      wizardAnswers: answers,   // conserver les réponses (défi, objectif 90j…) pour la génération de contenu
      phases: strategy.phases.map(p => ({
        ...p,
        actions: p.actions.map(a => ({ ...a, done: false })),
      })),
    };
    onLaunch(instance);
    onClose();
  };

  return (
    <div className="swiz-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="swiz-modal">

        {/* Header */}
        <div className="swiz-header">
          <div className="swiz-header-left">
            <span className="swiz-logo">🤝</span>
            <div>
              <div className="swiz-title">Appee te guide</div>
              <div className="swiz-subtitle">
                {phase === 'questions'   && `Étape ${step + 1} sur ${totalSteps}`}
                {phase === 'generating' && 'Génération de ta stratégie…'}
                {phase === 'recap'      && 'Ta stratégie personnalisée est prête !'}
              </div>
            </div>
          </div>
          <button className="swiz-close" onClick={onClose}>✕</button>
        </div>

        {/* Progress */}
        <div className="swiz-progress-track">
          <div className="swiz-progress-fill" style={{ width: phase === 'recap' ? '100%' : phase === 'generating' ? '90%' : `${pct}%` }} />
        </div>

        {/* Body */}
        <div className="swiz-body">
          {phase === 'questions' && currentStep && (
            <>
              <div className="swiz-question">{currentStep.question}</div>
              <div className="swiz-hint">{currentStep.hint}</div>
              <div className={`swiz-options swiz-options--${currentStep.type === 'list' ? 'list' : currentStep.options.length}`}>
                {currentStep.options.map(opt => (
                  <button
                    key={opt.value}
                    className={`swiz-option ${answers[currentStep.id] === opt.value ? 'swiz-option--selected' : ''}`}
                    onClick={() => pick(currentStep.id, opt.value)}
                  >
                    <span className="swiz-option-icon">{opt.icon}</span>
                    <span className="swiz-option-label">{opt.label}</span>
                    <span className="swiz-option-sub">{opt.sub}</span>
                  </button>
                ))}
              </div>
            </>
          )}

          {phase === 'generating' && (
            <WizardGenerating answers={answers} contacts={contacts} onDone={handleGenDone} />
          )}

          {phase === 'recap' && strategy && (
            <WizardRecap
              strategy={strategy}
              answers={answers}
              onLaunch={handleLaunch}
              onRegenerate={() => { setStrategy(null); setPhase('generating'); }}
            />
          )}
        </div>

        {/* Footer */}
        {phase === 'questions' && (
          <div className="swiz-footer">
            <WizardAppeePanel
              stepQuestion={currentStep?.question}
              selectedAnswer={currentStep?.options.find(o => o.value === answers[currentStep?.id])?.label}
            />
            <div className="swiz-footer-nav">
              {step > 0 && <button className="swiz-btn-back" onClick={back}>← Retour</button>}
              <div className="swiz-step-dots">
                {WIZARD_STEPS.map((_, i) => (
                  <div key={i} className={`swiz-dot ${i === step ? 'swiz-dot--active' : i < step ? 'swiz-dot--done' : ''}`} />
                ))}
              </div>
            </div>
          </div>
        )}
        {phase === 'generating' && (
          <div className="swiz-footer"><div className="swiz-footer-nav" /></div>
        )}
      </div>
    </div>
  );
}


export default function Strategy() {
  const { contacts, addNotification, setStrategyInstances, companyProfile } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const focusHandledRef = useRef(false);
  const [view, setView]               = useState('list');
  const [selectedStrat, setSelected]  = useState(null);
  const [activeStrat, setActive]      = useState(null);
  const [strategies, setStrategies]   = useState(() => lsLoad()); // init depuis cache local
  const [stratsReady, setStratsReady] = useState(false);

  // Charger depuis la BDD au montage (remplace le cache local si disponible)
  useEffect(() => {
    api.getStrategyInstances()
      .then(data => {
        if (Array.isArray(data) && data.length > 0) {
          setStrategies(data);
          lsSave(data); // synchroniser le cache local
        } else if (!stratsReady) {
          // Pas de données en BDD → garder le cache local et le migrer
          const local = lsLoad();
          if (local.length > 0) {
            Promise.all(local.map(s => api.saveStrategyInstance(s).catch(() => null)));
          }
        }
      })
      .catch(() => setStrategies(lsLoad()))
      .finally(() => setStratsReady(true));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelect    = s  => { setSelected(s); setView('configure'); };
  const handleLaunch    = async s  => {
    const newList = [s, ...strategies];
    setStrategies(newList);
    setStrategyInstances(newList); // sync Dashboard
    lsSave(newList);
    try { await api.saveStrategyInstance(s); } catch(e) { console.warn('saveStrategyInstance failed', e); }
    addNotification(`🚀 Stratégie "${s.title}" lancée ! Je commence à travailler dessus.`);
    setActive(s);
    setView('active');
  };
  const handleOpenStrat = s  => { setActive(s); setView('active'); };
  const handleReco    = s  => { setSelected(s); setView('configure'); };
  const handleDelete    = async id => {
    const newList = strategies.filter(x => x.instanceId !== id);
    setStrategies(newList);
    setStrategyInstances(newList); // sync Dashboard
    lsSave(newList);
    try { await api.deleteStrategyInstance(id); } catch {}
    if (activeStrat?.instanceId === id) { setActive(null); setView('list'); }
    addNotification('Stratégie archivée.');
  };
  const handleUpdate    = async s  => {
    const newList = strategies.map(x => x.instanceId === s.instanceId ? s : x);
    setActive(s);
    setStrategies(newList);
    setStrategyInstances(newList); // sync Dashboard
    lsSave(newList);
    try { await api.updateStrategyInstance(s.instanceId, s); } catch {}
  };
  const handleClose     = () => { setActive(null); setView('list'); };
  const [showWizard, setShowWizard] = useState(false);

  // Deep-link « Éditer dans Stratégie » (depuis Studio → Pages & concours) :
  // ouvre directement la bonne stratégie une fois les données chargées.
  useEffect(() => {
    const fi = location.state?.focusInstance;
    if (!fi || focusHandledRef.current || !stratsReady || !strategies.length) return;
    const match = strategies.find(s => String(s.instanceId) === String(fi));
    if (match) {
      focusHandledRef.current = true;
      setActive(match);
      setView('active');
    } else {
      focusHandledRef.current = true;
      addNotification('Cette page n\'est plus reliée à une stratégie active.', 'error');
    }
  }, [location.state, stratsReady, strategies]); // eslint-disable-line react-hooks/exhaustive-deps

  // Compute stats for hero
  const totalActions = strategies.reduce((s, st) => s + st.phases.reduce((a, p) => a + p.actions.length, 0), 0);
  const doneActions  = strategies.reduce((s, st) => s + st.phases.reduce((a, p) => a + p.actions.filter(ac => ac.done).length, 0), 0);
  const avgPct = strategies.length > 0
    ? Math.round(strategies.reduce((s, st) => {
        const tot = st.phases.reduce((a, p) => a + p.actions.length, 0);
        const don = st.phases.reduce((a, p) => a + p.actions.filter(ac => ac.done).length, 0);
        return s + (tot > 0 ? don / tot * 100 : 0);
      }, 0) / strategies.length)
    : 0;

  return (
    <div className="strategy-page">

      {/* ── Hero header ── */}
      <div className="strat-hero strat-animate-in">
        <div className="strat-hero-nebula" />
        <div className="strat-hero-top">
          <div className="strat-hero-left">
            <div className="strat-hero-icon-wrap">
              <svg className="strat-hero-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M9.663 17h4.673M12 3v1m6.364 1.636-.707.707M21 12h-1M4 12H3m3.343-5.657-.707-.707m2.828 9.9a5 5 0 1 1 7.072 0l-.548.547A3.374 3.374 0 0 0 14 18.469V19a2 2 0 1 1-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/>
              </svg>
            </div>
            <div>
              <h1 className="strat-hero-title">Stratégie Marketing</h1>
              <p className="strat-hero-sub">Ton agence 360° — je conçois, lance et suis de A à Z</p>
            </div>
          </div>
          <div className="strat-hero-actions">
            {view !== 'list' ? (
              <button className="strat-hero-back-btn" onClick={() => setView('list')}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M15 18l-6-6 6-6"/></svg>
                Mes stratégies
              </button>
            ) : (
              <button className="strat-hero-new-btn" onClick={() => setView('select')}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                Nouvelle stratégie
              </button>
            )}
          </div>
        </div>

        {view === 'list' && (
          <SectionHint>
            Ton <strong>agence marketing perso</strong> : dis ton objectif (fidéliser, remplir l'agenda…) et l'IA te déroule un plan d'actions concret, semaine par semaine. Tu suis, tu coches — chaque action est déjà prête.
          </SectionHint>
        )}

        {view === 'list' && (
          <GenieTip
            id="strategy"
            title="🧭 Ton agence marketing, en pilote auto"
            foot={<>Chaque action est prête (e-mail, SMS, page) : tu valides, Appee exécute.</>}
          >
            Dis ton objectif — « lancer ma boîte », « fidéliser », « remplir l'agenda »… — et Appee construit un plan d'actions complet, semaine par semaine.
          </GenieTip>
        )}

        {view === 'list' && (
          <div className="strat-stats-row">
            <StratStat icon="🧠" label="En cours" value={strategies.length} color="rgba(90,53,255,.25)" delay={0} />
            <StratStat icon="✅" label="Actions faites" value={doneActions} color="rgba(16,185,129,.25)" delay={60} />
            <StratStat icon="📋" label="Total actions" value={totalActions} color="rgba(59,130,246,.2)" delay={120} />
            {strategies.length > 0 && <StratStat icon="📈" label="Avancement moyen" value={avgPct} suffix="%" color="rgba(245,158,11,.25)" delay={180} />}
          </div>
        )}
      </div>

      {showWizard && (
        <StrategyWizard
          onClose={() => setShowWizard(false)}
          onLaunch={handleLaunch}
          contacts={contacts}
        />
      )}

      {view === 'list' && (
        <StrategyList
          strategies={strategies}
          onOpen={handleOpenStrat}
          onNew={() => setView('select')}
          onDelete={handleDelete}
          onReco={handleReco}
          onUpdate={handleUpdate}
          contacts={contacts}
          companyProfile={companyProfile}
          onFixProfile={() => navigate('/settings/company')}
          onWizard={() => setShowWizard(true)}
        />
      )}
      {view === 'select' && (
        <StrategySelector
          contacts={contacts}
          onSelect={handleSelect}
          onBack={() => setView('list')}
          companyProfile={companyProfile}
          onFixProfile={() => navigate('/settings/company')}
          onWizard={() => setShowWizard(true)}
        />
      )}
      {view === 'configure' && selectedStrat && (
        <StrategyConfigurator
          strat={selectedStrat}
          contacts={contacts}
          onLaunch={handleLaunch}
          onBack={() => setView('select')}
        />
      )}
      {view === 'active' && activeStrat && (
        <ActiveStrategy
          strat={activeStrat}
          onClose={handleClose}
          onUpdate={handleUpdate}
        />
      )}
    </div>
  );
}
